/**
 * Hero ability hook builder for setup-time resolution.
 *
 * Resolves hero cards from the selected hero decks, extracts structured
 * ability metadata from markup patterns, and produces a list of
 * HeroAbilityHook entries stored in G.heroAbilityHooks.
 *
 * No boardgame.io imports. No .reduce(). No throws. Setup-time only.
 */
import { HERO_KEYWORDS } from '../rules/heroKeywords.js';
import { HERO_COUNT_SOURCES } from '../rules/heroCountSource.js';
import { revealRulesForLegacyKeyword, REVEAL_KEYWORDS } from '../rules/revealRule.js';
import { HERO_COMPOSITION_MARKERS, HERO_COMPOSITION_MARKER_NAMES, PARAMETERIZED_COMPOSITION_MARKER_NAMES, buildEmpoweredComposition, buildEmpoweredFreeChoiceComposition, buildEmpoweredChooseOneComposition, buildDynamicEmpoweredComposition, } from '../rules/heroCompositions.js';
import { normalizeTraitSlug } from '../state/traits.normalize.js';
import { CITY_SPACE_NAMES } from '../board/citySpaceNames.js';
// why: D-18705 / D-18706 — hero hooks must key by the canonical-face slash
// instance id (the id the played card carries in `G` zones), resolving
// ability text from the canonical face (sides[0]). The shared emitter is the
// single source of those instance ids (import-not-duplicate, D-13702 RS-4).
import { heroCardInstanceExtIds } from './buildHeroDeck.js';
import { MASTER_STRIKE_THIS_TURN_CONDITION_TYPE, MASTER_STRIKE_OR_AMBUSH_THIS_TURN_CONDITION_TYPE, OWNED_CARD_KOED_CONDITION_TYPE, OWNED_HERO_KOED_CONDITION_TYPE, DECK_CARD_KOED_CONDITION_TYPE, ATTACK_ICON_DECK_KO_CONDITION_TYPE, NON_GREY_HERO_DECK_KO_CONDITION_TYPE, } from '../hero/heroConditions.evaluate.js';
// ---------------------------------------------------------------------------
// Markup extraction — structured patterns only, no NL parsing
// ---------------------------------------------------------------------------
/** Regex for [hc:X] hero class condition markup. */
const HERO_CLASS_PATTERN = /\[hc:([^\]]+)\]/g;
// why: mirrors the [hc:X] pattern — same extraction semantics, same consumption
// behavior (markup tokens removed from downstream text after extraction).
/** Regex for [team:X] team condition markup. */
const TEAM_PATTERN = /\[team:([^\]]+)\]/g;
// why: D-24628 — the printed play-gate prefix: one or more [hc:X] / [team:X] tokens at the very
// start of a line, then a colon ("[hc:strength]:", "[team:shield][team:shield][team:shield]:").
// Keyword-led prefixes ("[keyword:X-Gene] [team:x-men]:") deliberately do not match.
/** Regex for a leading [hc:X] / [team:X] gate prefix ending in a colon. */
const LEADING_GATE_PREFIX_PATTERN = /^\s*(?:\[(?:hc|team):[^\]]+\]\s*)+:/;
// why: WP-803 / D-24692 — Man / Woman Out of Time (rules v23 L1667). KEYWORD_PATTERN cannot match
// the multi-word token, so a line that IS the token (behind an optional gate) is recognized here.
// The gate admits [keyword:Savior] as well as [hc:X] / [team:X] because LEADING_GATE_PREFIX_PATTERN
// is hc / team only (D-24628) and must not be widened (D-24628 and the fight-cost clause read it);
// the Savior condition itself is still built by the Step 2 `savior` arm. A line where the token sits
// inside other text (Winter Soldier's "A Hero in your hand gains [keyword:Man Out of Time] this turn")
// is a different capability (granting the keyword) and deliberately does not match; those lines
// resolve `grant-out-of-time` from their `[keyword:grant-out-of-time]` marker (WP-828 / D-24761).
/** Regex for a whole line that is the Man / Woman Out of Time token behind an optional gate. */
const OUT_OF_TIME_LINE_PATTERN = /^\s*(?:(?:(?:\[(?:hc|team):[^\]]+\]|\[keyword:Savior\])\s*)+:\s*)?\[keyword:(?:Man|Woman|Man\/Woman) Out of Time\]\s*$/;
// why: optional :N suffix carries magnitude for rescue/reveal effects (D-21503)
// why: hyphen allowed in keyword names to support reveal-ko and reveal-min tokens (D-21701, D-21702)
/** Regex for [keyword:X] or [keyword:X:N] keyword markup (N = non-negative integer). */
const KEYWORD_PATTERN = /\[keyword:([a-zA-Z][a-zA-Z-]*)(?::(\d+))?\]/g;
// why: D-24618 — a `[rule:X]` token names a rules concept (Shard, Sidekick, Divided Card).
// The parser models none of them, so a line whose ONLY markup is `[rule:X]` resolved to an
// empty hook that looked exactly like flavor text and never flagged hollow (Penumbra).
/** Regex for [rule:X] rules-concept markup. */
const RULE_TOKEN_PATTERN = /\[rule:([^\]]+)\]/g;
// why: D-24618 — a line wholly wrapped in parentheses is printed reminder text ("(Each
// [rule:Divided Card] has two different card names.)"), not an ability; it must not flag.
/** Regex for a whole-line parenthetical reminder. */
const REMINDER_TEXT_PATTERN = /^\s*\(.*\)\s*$/;
// why: D-24618 — plural rule tokens fold onto their singular concept so the hollow
// mechanic aggregates one row per concept ("Gain 2 [rule:Shards]" and "Gain a
// [rule:Shard]" are the same missing mechanic).
/** Plural `[rule:X]` slugs mapped to their singular concept slug. */
const RULE_TOKEN_SINGULAR = {
    shards: 'shard',
    sidekicks: 'sidekick',
};
// why: D-24623 — the unresolved-marker name for a gated line whose body parsed to nothing and
// names no rule concept or keyword. One shared name so the gap aggregates as one runtime row.
/** Unresolved marker recorded for a line that resolved only its play gate. */
const GATE_ONLY_MARKER = 'gate-only';
// why: D-24623 — KEYWORD_PATTERN admits only `[keyword:Name]` / `[keyword:Name:N]`, so a
// multi-word or space-magnitude token ("[keyword:Man Out of Time]", "[keyword:Danger Sense 2]")
// never matches and is dropped. This matches every token so those can be named.
/** Regex for any `[keyword:...]` token, including multi-word and space-magnitude forms. */
const ANY_KEYWORD_TOKEN_PATTERN = /\[keyword:([^\]]+)\]/g;
/** Regex for the token bodies KEYWORD_PATTERN does match (`Name` or `Name:N`). */
const MATCHED_KEYWORD_BODY_PATTERN = /^[a-zA-Z][a-zA-Z-]*(?::\d+)?$/;
// why: D-24624 — an ungated line that LEADS with an unmatched keyword token ("[keyword:Soaring
// Flight]", "[keyword:Excessive Kindness]: Draw a card.") is that keyword's printed ability, so
// its hollow takes the keyword's printed timing rather than the onPlay default.
/** Regex for a `[keyword:...]` token at the very start of a line. */
const LEADING_KEYWORD_TOKEN_PATTERN = /^\s*\[keyword:([^\]]+)\]/;
// why: D-24624 — printed timings from data/metadata/keywords-full.json for unmatched keywords
// that do NOT resolve at play. The hollow is still observed when the card is played (the only
// detection site), but it is labelled with this timing so the log and the runtime ledger never
// claim a play-time effect. Keys use the collectUnmatchedKeywordSlugs normalization. Soaring
// Flight / "When Recruited" / Excessive Kindness fire on recruit; Excessive Violence and Piercing
// Energy are spent when fighting; Switcheroo is used by revealing the card instead of playing it.
/** Printed non-play timing for unmatched keyword names that lead a line. */
const UNMATCHED_KEYWORD_TIMINGS = {
    'soaring-flight': 'onRecruit',
    '"when-recruited"-abilities': 'onRecruit',
    'excessive-kindness': 'onRecruit',
    'excessive-violence': 'onFight',
    'piercing-energy': 'onFight',
    switcheroo: 'onReveal',
};
// why: D-24044 — the ANCHORED Empowered parameter tail. The color must immediately follow
// the `[keyword:Empowered]` token as `by [hc:COLOR]` (the `^` anchors to the text right after
// the marker — a broad forward scan could wrongly bind a later, unrelated [hc:...] to
// Empowered). Non-global + stateless: a fresh `.exec` against the post-marker slice.
/** Regex for the anchored `by [hc:COLOR]` tail immediately after `[keyword:Empowered]`. */
const EMPOWERED_PARAM_TAIL_PATTERN = /^\s*by\s*\[hc:([a-z0-9-]+)\]/i;
// why: D-24047 — the ANCHORED leading `[hc:X]:` class-condition prefix gate of the
// conditional-prefix Empowered form. The `^` anchors to the line start so only a leading
// class condition (not a later, unrelated `[hc:...]`) is read as the gate; mirrors
// EMPOWERED_PARAM_TAIL_PATTERN's anchored, non-global, stateless `.exec` discipline.
/** Regex for the anchored leading `[hc:X]:` class-condition prefix gate. */
const EMPOWERED_PREFIX_GATE_PATTERN = /^\s*\[hc:([a-z0-9-]+)\]\s*:/i;
// why: D-24047 — gate #2 of the structural resolve gate counts `[keyword:Empowered]`
// markers (case-insensitive); the conditional-prefix form resolves ONLY a single marker.
// This is what rejects the two-marker choose-one (fight-or-flight); a condition-counting
// gate would mis-resolve it (the Honest-Partial Invariant). Global so String.match returns
// every occurrence (String.match ignores lastIndex, so the shared const stays stateless).
/** Regex for counting `[keyword:Empowered]` marker occurrences (case-insensitive). */
const EMPOWERED_MARKER_COUNT_PATTERN = /\[keyword:empowered\]/gi;
// why: D-24047 — gate #5 of the structural resolve gate rejects a multi-class continuation
// `... by [hc:Y] and [hc:Z]`. Applied ONLY to the slice after the consumed count tail
// (never a broad scan of the whole text); anchored, non-global, stateless `.exec`/`.test`.
/** Regex for an `and [hc:...]` multi-class continuation immediately after the count tail. */
const EMPOWERED_MULTICLASS_TAIL_PATTERN = /^\s*and\s*\[hc:/i;
// why: WP-310 / D-24098 — the anchored MULTI-class Empowered tail
// `by [hc:X] and [hc:Y] (and [hc:Z]…)`. The `(?:…)+` requires at least ONE
// `and [hc:…]` continuation, so this NEVER matches the single-class `by [hc:X]`
// form (owned by tryResolveEmpoweredCore) — preserving the single-class path.
// Anchored to the post-marker slice, non-global, stateless `.exec`.
/** Regex for the anchored multi-class `by [hc:X] and [hc:Y] (and …)` tail. */
const EMPOWERED_MULTICLASS_FULL_TAIL_PATTERN = /^\s*by\s*\[hc:[a-z0-9-]+\](?:\s*and\s*\[hc:[a-z0-9-]+\])+/i;
// why: D-24063 — anchored "Choose one:" prefix gate for the choose-one Empowered pre-pass.
// Non-global + anchored so it only fires when the line STARTS with this prefix; reuses the
// stateless `.test` discipline of EMPOWERED_PREFIX_GATE_PATTERN (no lastIndex concern).
/** Regex for the anchored "Choose one:" choose-one form prefix. */
const EMPOWERED_CHOOSE_ONE_PREFIX_PATTERN = /^\s*Choose one\s*:/i;
// why: D-24063 — extracts each `[keyword:Empowered] by [hc:X]` class tail from a choose-one
// line. Stored as non-global source; a fresh new RegExp(source, 'gi') is created inside the
// helper to avoid lastIndex state on the module-level const (same pattern as HERO_CLASS_PATTERN).
/** Regex source for `[keyword:Empowered] by [hc:X]` — instantiated global inside the choose-one helper. */
const EMPOWERED_CHOOSE_ONE_CLASS_TAIL_PATTERN = /\[keyword:Empowered\]\s*by\s*\[hc:([a-z0-9-]+)\]/i;
// why: D-24069 — the draw-or-empowered choose-one form pairs a printed "Draw a card" option with a
// single Empowered marker. This presence check distinguishes it from the WP-283 two-empowered
// choose-one (which offers two Empowered halves and no draw). Non-global so the stateless `.test`
// carries no lastIndex; the structural work is done by the prefix + single-marker + class-tail gates.
/** Regex for the "Draw a card" draw option in a draw-or-empowered choose-one line. */
const DRAW_A_CARD_PATTERN = /Draw a card/i;
// why: D-24065 — anchored pattern for the dynamic Empowered form whose class is the runtime
// class of a revealed card, not a static `[hc:X]` literal. Locked to this exact phrasing
// (cross-the-multiverse); wildcard extension beyond this phrasing family is forbidden.
/** Regex for the "by the Hero Classes of the card you revealed this way" dynamic tail. */
const EMPOWERED_REVEALED_CLASSES_PATTERN = /by the Hero Classes of the card you revealed this way/i;
// why: D-24016 — the count-scaled attack token has three segments
// ([keyword:attack-per-count:<source>:<perUnit>]); KEYWORD_PATTERN only captures
// keyword(:N)?, so the count source and per-unit rate need a dedicated pattern.
// why: WP-740 / D-24562 — an OPTIONAL 4th `:<perEach>` segment carries the "for each N" divisor
// (D-24493 perEach) on a STANDALONE line (Play to the Crowd's "+1 for each two Bystanders").
// Before this only the count-scaled-choose pre-pass read it. Absent ≡ 1 and is omitted from the
// effect, so every existing 3-segment marker parses byte-identically.
/** Regex for [keyword:attack-per-count:<source>:<perUnit>(:<perEach>)?] count-scaled markup. */
const COUNT_SCALED_PATTERN = /\[keyword:attack-per-count:([a-z][a-z-]*):(\d+)(?::(\d+))?\]/g;
// why: WP-674 / D-24489 — the recruit sibling of COUNT_SCALED_PATTERN. Same
// three-segment shape, but the marker drives a `recruit-per-count` effect that
// grants recruit rather than attack (noir's Follow Big Leads).
/** Regex for [keyword:recruit-per-count:<source>:<perUnit>] count-scaled markup. */
const RECRUIT_COUNT_SCALED_PATTERN = /\[keyword:recruit-per-count:([a-z][a-z-]*):(\d+)\]/g;
// why: WP-714 / D-24537 — the bystander-capture sibling of COUNT_SCALED_PATTERN. Same
// three-segment shape, but the marker drives a `kidnap-per-count` effect that captures
// N Bystanders rather than granting a resource (Ultron's Genetic Experimentation). No
// optional perEach segment — this card's rate is 1.
/** Regex for [keyword:kidnap-per-count:<source>:<perUnit>] count-scaled markup. */
const KIDNAP_COUNT_SCALED_PATTERN = /\[keyword:kidnap-per-count:([a-z][a-z-]*):(\d+)\]/g;
// why: WP-673 / D-24488 — Divine Lightning's line carries BOTH `[keyword:Worthy]`
// (in the phrase "each other card … that makes you Worthy") AND the count-scaled
// marker `[keyword:attack-per-count:worthy-cards-played-this-turn:N]`. On such a
// line the Worthy token is the COUNT CRITERION, not a play gate, so its
// heroCostAtLeastInHandOrPlay condition must be suppressed (mirrors the
// size-changing / investigate descriptive-token suppression). Non-global,
// stateless `.test`; matches the appended marker regardless of the per-unit rate.
const WORTHY_COUNT_SCALED_MARKER_PATTERN = /\[keyword:attack-per-count:worthy-cards-played-this-turn:\d+\]/;
// why: D-24019 — the optional-KO-reward token has three segments
// ([keyword:optional-ko-reward:<reward>:<n>]); KEYWORD_PATTERN cannot match it
// (it stops at the second colon), so the reward and magnitude need a dedicated
// pattern. The capture group is (\d+) — matching the COUNT_SCALED_PATTERN
// precedent, NOT [1-9]\d*. The strict [1-9]\d* gate is the apply-script's job
// (build time); here the parser captures the integer and the n ≥ 1 check is
// enforced downstream (isValidMagnitude at the reward executor).
/** Regex for [keyword:optional-ko-reward:<reward>:<n>] optional-KO-reward markup. */
const OPTIONAL_KO_REWARD_PATTERN = /\[keyword:optional-ko-reward:([a-z][a-z-]*):(\d+)\]/g;
// why: WP-382 / D-24183 — the ko-wound-reward token mirrors optional-ko-reward's
// three-segment shape ([keyword:ko-wound-reward:<reward>:<n>]); like it, the
// third `:<reward>` segment means KEYWORD_PATTERN cannot match it, so it never
// reaches the unresolved-marker scan.
/** Regex for [keyword:ko-wound-reward:<reward>:<n>] KO-a-Wound-then-reward markup. */
const KO_WOUND_REWARD_PATTERN = /\[keyword:ko-wound-reward:([a-z][a-z-]*):(\d+)\]/g;
// why: [keyword:optional-put-bottom-hq:<n>] token for "You may put a card from the
// HQ on the bottom of the Hero Deck". Simple 2-segment token carrying just the
// magnitude (always 1 for this MVP form). Follows the COUNT_SCALED_PATTERN precedent.
/** Regex for [keyword:optional-put-bottom-hq:<n>] optional-put-bottom-HQ markup. */
const OPTIONAL_PUT_BOTTOM_HQ_PATTERN = /\[keyword:optional-put-bottom-hq:(\d+)\]/g;
// why: D-24132 — [keyword:put-any-number-bottom-hq:<n>] token for "Choose any number of
// cards/Heroes from the HQ. Put them on the bottom of the Hero Deck" (the MULTI-select
// sibling of optional-put-bottom-hq). Simple 2-segment token carrying just the magnitude
// (always 1 for this MVP form). Follows the OPTIONAL_PUT_BOTTOM_HQ_PATTERN precedent.
/** Regex for [keyword:put-any-number-bottom-hq:<n>] put-any-number-bottom-HQ markup. */
const PUT_ANY_NUMBER_BOTTOM_HQ_PATTERN = /\[keyword:put-any-number-bottom-hq:(\d+)\]/g;
// why: D-24133 — the MANDATORY single-card "Put a card from the HQ on the bottom of the
// Hero Deck. If that card had a recruit/attack icon, you get +N" form (Wonder Man's Absorb
// Ambient Power) is marked `[keyword:put-bottom-hq-icon-reward:<n>]`. It needs NO dedicated
// extraction step: the generic KEYWORD_PATTERN loop (Step 2) already matches the 2-segment
// token, records the keyword, and captures the magnitude N (the icon-reward amount); the
// effect builder's fallback emits `{ type, magnitude }`, and the park handler reads the
// magnitude. (The sibling optional-/put-any-number- dedicated steps are belt-and-suspenders;
// deduplicateKeywords collapses the double-push.)
// why: D-24132 — a put-any-number-bottom-hq line MAY carry a trailing "Then you get
// [keyword:Empowered] by [hc:X] (and [hc:Y]…)" grant on the SAME line (Wonder Man's 8th
// Wonder of the World). This detection pattern finds the `[keyword:Empowered]` token so the
// pre-pass can slice the anchored `by [hc:…]` tail after it and route the classes onto the
// put-any-number effect (applied AFTER the moves at resolve time) instead of a play-time
// standalone Empowered primitive. Non-global source; a fresh RegExp is created per use.
/** Regex for the `[keyword:Empowered]` token (case-insensitive), for the put-any-number tail. */
const EMPOWERED_MARKER_TOKEN_PATTERN = /\[keyword:empowered\]/i;
// why: D-24024 — the forward-compat parameterized reveal token has 3+
// colon-separated segments ([keyword:reveal:<predicate>:<actions>(:continue)?]).
// KEYWORD_PATTERN stops at the second colon and cannot match it; the legacy
// [keyword:reveal:<n>] form is disambiguated because the predicate segment must
// START WITH A LETTER (so a bare digit magnitude routes to KEYWORD_PATTERN, not
// here). One token = one RevealRule (mirrors COUNT_SCALED_PATTERN's
// one-token-one-effect shape). predicate ∈ {always, cost-zero, cost-odd,
// cost-lte-<n>, cost-gte-<n>}; actions are '+'-joined ∈ {draw, ko, discard, attack-by-cost,
// attack-fixed-<n>, choose-discard-or-return}; an optional trailing ':continue'.
// No card uses this grammar this WP — it makes a new reveal variant data-only.
/** Regex for [keyword:reveal:<predicate>:<actions>(:continue)?] parameterized reveal markup. */
const REVEAL_RULE_PATTERN = /\[keyword:reveal:([a-z][a-z0-9-]*):([a-z][a-z0-9+-]*)(?::(continue))?\]/g;
// why: D-24027 — how many deck-top cards a reveal descriptor peeks is DESCRIPTOR-level
// (the reveal handler's loop bound), NOT rule-level (the per-card predicate), so it rides
// a dedicated 2-segment token mirroring COUNT_SCALED_PATTERN's dedicated-token shape — not
// the legacy `[keyword:reveal:N]` magnitude (that is a draw threshold) nor a RevealRule
// segment. Disambiguation: `[keyword:reveal-count:N]` is NOT matched by REVEAL_RULE_PATTERN
// (which needs the literal `reveal:<predicate>`); KEYWORD_PATTERN matches it as keyword
// `reveal-count`, but isValidHeroKeyword('reveal-count') is false (it is a modifier marker,
// never a HeroKeyword — absent from HERO_KEYWORDS), so only REVEAL_COUNT_PATTERN consumes it.
/** Regex for [keyword:reveal-count:<n>] reveal-count modifier markup. */
const REVEAL_COUNT_PATTERN = /\[keyword:reveal-count:(\d+)\]/g;
// why: WP-479 / D-24286 — a bare `[keyword:reveal-reorder]` modifier marker (no
// value segment) sets the reveal descriptor's `reorderRemainder` flag. Like
// `reveal-count` it is a MODIFIER marker, never a HeroKeyword (absent from
// HERO_KEYWORDS); KEYWORD_PATTERN matches it as keyword `reveal-reorder` but
// isValidHeroKeyword is false, so it must be listed in RECOGNIZED_NON_KEYWORD_MARKERS
// below to avoid a false parse-unrecognized flag.
/** Regex for the [keyword:reveal-reorder] reveal-reorder modifier marker. */
const REVEAL_REORDER_PATTERN = /\[keyword:reveal-reorder\]/;
// why: D-24024 — the 8 legacy reveal keywords whose markers translate to the
// collapsed `reveal` descriptor. Built from the canonical REVEAL_KEYWORDS array so
// the parser and the translation function share one source of truth.
const REVEAL_KEYWORD_SET = new Set(REVEAL_KEYWORDS);
// why: WP-257 / D-24034 — a `[keyword:X]` token whose X is NOT a HeroKeyword and
// NOT a composition marker is normally an unresolved marker (→ parse-unrecognized).
// But a few `[keyword:...]` tokens are recognized MODIFIER markers consumed by a
// dedicated pattern, not by KEYWORD_PATTERN's keyword arm. `reveal-count` is the
// one whose first segment (a bare hyphenated word + `:<n>`) ALSO matches
// KEYWORD_PATTERN, so the unresolved-marker scan must exclude it to avoid a false
// flag. (`attack-per-count` / `optional-ko-reward` / the parameterized `reveal:...`
// tokens carry extra colon segments that KEYWORD_PATTERN cannot match, so they
// never reach the keyword arm; only `reveal-count` needs listing here.)
const RECOGNIZED_NON_KEYWORD_MARKERS = new Set([
    'reveal-count',
    // why: WP-479 / D-24286 — reveal-reorder is a bare modifier marker (like
    // reveal-count), not a HeroKeyword; exclude it from the unresolved-marker scan.
    'reveal-reorder',
]);
// why: the coverage probe (scripts/hero-effect-coverage.mjs) labels any `[keyword:X]`
// token outside HERO_KEYWORDS + HERO_COMPOSITION_MARKER_NAMES as an "unsupported
// mechanic". These names are NOT HeroKeywords yet ARE consumed by a dedicated arm of
// parseAbilityText (condition markers, modifier markers), so without this list the probe
// mislabels them. The probe still counts one as unsupported for a hero whose hooks leave
// it in unresolvedMarkers — which is how the gated arms (sunlight / moonlight on an
// unmodeled day-night line, teleport / x-gene off their card allowlists) stay honest, and
// why a name left here after its arm is removed fails safe (the fallback flags it).
/** Non-HeroKeyword `[keyword:X]` names the hero ability parser recognizes. */
export const HERO_PARSER_RECOGNIZED_MARKER_NAMES = [
    ...RECOGNIZED_NON_KEYWORD_MARKERS,
    'spectrum',
    'sunlight',
    'moonlight',
    'recruit-threshold',
    'draw-threshold',
    'discard-threshold',
    'outwit',
    'worthy',
    'savior',
    'antics',
    'defeated-villain-or-mastermind',
    // why: WP-853 / D-24789 — Absorb Energies' owned-card KO trigger marker; its
    // parseAbilityText arm pushes the ownedCardKoedThisTurn wait-and-see condition.
    'owned-card-koed',
    // why: WP-856 / D-24792 — Possessed by Brother's Spirit's Hero-KO trigger marker; its
    // parseAbilityText arm pushes the one-shot ownedHeroKoedThisTurn wait-and-see condition.
    'owned-hero-koed',
    // why: WP-855 / D-24791 — Endless Appetite's deck-KO trigger marker; its parseAbilityText
    // arm pushes the per-KO deckCardKoedThisTurn wait-and-see condition.
    'deck-card-koed',
    // why: WP-859 / D-24795 — Metamorphosis / Bursting with Life's deck-KO turn-record markers; their
    // parseAbilityText arms push the one-shot attackIconCardKoedFromDeckThisTurn /
    // nonGreyHeroKoedFromDeckThisTurn wait-and-see conditions.
    'deck-koed-attack-icon',
    'deck-koed-non-grey-hero',
    'first-hero-condition',
    // why: WP-743 / D-24566 — Grief / Spring the Trap condition markers; each has a
    // parseAbilityText arm that pushes its whole-turn wait-and-see condition.
    'master-strike-this-turn',
    'master-strike-or-ambush-this-turn',
    'teleport',
    'x-gene',
    // why: D-24696 — the Spy Network choose-one option marker; consumed by the count-scaled-choose
    // pre-pass (tryResolveCountScaledChooseOneLine) and left unresolved off a choose-one line.
    'gain-officer',
    // why: WP-803 / D-24692 — the three Out of Time spellings (normalized like the coverage probe:
    // "Man Out of Time" → man-out-of-time). OUT_OF_TIME_LINE_PATTERN resolves a whole-line token to
    // the `out-of-time` keyword; an unmarked line it does not match keeps the slug in
    // unresolvedMarkers, so the probe flags it. (Winter Soldier's 2>4 grant lines resolve
    // `grant-out-of-time` from their marker since WP-828 / D-24761.)
    'man-out-of-time',
    'woman-out-of-time',
    'man/woman-out-of-time',
];
// why: D-24019 — the reward of an optional-ko-reward effect is dispatched to an
// ALREADY-BUILT reward executor; only these four are seeded. An unseeded reward
// (e.g. a not-yet-built gain-shard) emits no descriptor — such a marker can
// never reach the pending queue. Mirrored defensively in heroEffects.execute.ts.
const OPTIONAL_KO_REWARD_SEEDED_REWARDS = new Set([
    'rescue',
    'draw',
    'attack',
    'recruit',
]);
// why: WP-382 / D-24183 — the seeded reward vocabulary for ko-wound-reward
// (draw / attack / recruit — the Healing Factor family's core vocabulary). An
// unseeded reward emits no descriptor. Mirrors the same constant in
// hero/heroEffects.execute.ts (two copies, per duplicate-first).
const KO_WOUND_REWARD_SEEDED_REWARDS = new Set([
    'draw',
    'attack',
    'recruit',
]);
// why: D-24148 — the shuffle-discard-empty-reward token has three segments
// ([keyword:shuffle-discard-empty-reward:<reward>:<n>]); KEYWORD_PATTERN cannot
// match it (it stops at the second colon), so it needs a dedicated pattern like
// the D-24019 precedent above. The capture group is (\d+) — the strict [1-9]\d*
// gate is the apply-script's job (build time); the n ≥ 1 check is enforced in
// the extraction step below.
/** Regex for [keyword:shuffle-discard-empty-reward:<reward>:<n>] markup. */
const SHUFFLE_DISCARD_EMPTY_REWARD_PATTERN = /\[keyword:shuffle-discard-empty-reward:([a-z][a-z-]*):(\d+)\]/g;
// why: D-24148 — the empty-discard grant dispatches to G.turnEconomy via
// addResources; only the two printed variants are seeded (Reprocess = recruit,
// Electromagnetic Eyebeams = attack). An unseeded reward segment emits no
// descriptor, so the line stays a hollow-detectable no-op instead of silently
// half-executing. Deliberately narrower than the D-24019 set.
const SHUFFLE_DISCARD_EMPTY_REWARD_SEEDED_REWARDS = new Set([
    'attack',
    'recruit',
]);
// why: extract magnitude from icon-adjacent integers — avoids per-card manual markup (D-21505)
/** Regex for attack/recruit icon-adjacent magnitude, e.g. "+2[icon:attack]". */
const ICON_MAGNITUDE_PATTERN = /\+?(\d+)\s*\[icon:(attack|recruit)\]/g;
// why: WP-790 / D-24652 — the printed "usable only against …" clause that follows a
// `+N[icon:attack]` grant (rules v23, the Liberate entry: a bonus spendable only on the named
// targets). Matched on the text AFTER the attack icon-magnitude. "usable only to fight …"
// (mdns Wong Seal the Rift) deliberately does not match.
/** Regex for the restriction clause, e.g. ", usable only against Villains on the Rooftops." */
const ATTACK_RESTRICTION_CLAUSE_PATTERN = /,?\s*usable only against (.+?)\.?$/i;
/** Regex for a Mastermind target word inside the clause ("Commander" is fear/vill wording). */
const ATTACK_RESTRICTION_MASTERMIND_PATTERN = /\b(mastermind|commander)\b/i;
/** Regex for an exclusion clause ("other Villains", nmut Karma) — left unmodeled (fails closed). */
const ATTACK_RESTRICTION_OTHER_PATTERN = /\bother\b/i;
// why: WP-790 / D-24652 — co2e Storm's Ranged follow-up line widens the PRECEDING restricted
// grant to the Mastermind; it is not a second grant.
/** Regex for widen (a): "[hc:X]: You may use this bonus [icon:attack] against the Mastermind instead." */
const ATTACK_RESTRICTION_WIDEN_BONUS_PATTERN = /^\[hc:[a-z]+\]:\s*You may use this bonus \[icon:attack\] against the Mastermind instead\.?$/i;
// why: WP-790 / D-24652 — vill Electro's Ranged "Instead you may get +N … against the Commander"
// line is the same grant re-aimed, not a second +N; it fuses only when N matches the grant.
/** Regex for widen (b): "[hc:X]: Instead you may get +N[icon:attack] usable only against the Commander." */
const ATTACK_RESTRICTION_WIDEN_INSTEAD_PATTERN = /^\[hc:[a-z]+\]:\s*Instead you may get \+(\d+)\[icon:attack\] usable only against the (Mastermind|Commander)\.?$/i;
// why: WP-794 / D-24663 — the printed fight-cost reduction clauses (core Storm Lightning Bolt /
// Tidal Wave, cvwr Storm & Black Panther Lightning Strike, dkcy Forge Dirty Work). Matched on the
// text AFTER the leading gate prefix, trimmed (data carries a double space after the gate). These
// two forms are the whole vocabulary: "Each Villain gets -N", "the next time you fight the
// Mastermind" and Royal Decree deliberately do not match (fail closed).
/** Regex for the City form, e.g. "Any Villain you fight on the Rooftops this turn gets -2[icon:attack]." */
const FIGHT_COST_REDUCTION_CITY_PATTERN = /^Any Villain you fight (?:on|in) the (Sewers|Bank|Rooftops|Streets|Bridge) this turn gets -(\d+)\[icon:attack\]\.?$/i;
/** Regex for the Mastermind form, e.g. "The Mastermind gets -2[icon:attack] this turn." */
const FIGHT_COST_REDUCTION_MASTERMIND_PATTERN = /^The Mastermind gets -(\d+)\[icon:attack\] this turn\.?$/i;
// why: WP-660 / D-24471 — an [icon:recruit|attack] token inside a THRESHOLD / RATE
// CONDITION clause ("made at least N[icon:recruit]", "for every N[icon:recruit]",
// "N or more [icon:recruit]") refers to a resource the player MADE this turn — it is the
// condition's threshold, NOT a resource the card grants. The icon-magnitude (Step 2b) and
// icon→keyword (Step 3) extractors would otherwise read it as a phantom +N grant (the live
// bug: She-Hulk's Radioactive Riot granting a free +6 recruit every play; Hurl Legal
// Objections granting +6 recruit alongside its Transform). This pattern locates those
// condition icons so their character positions are EXCLUDED from both extractors. Global;
// the icon token is the last "[icon:…]" in each match. A GRANT icon elsewhere on the line
// ("you get +3[icon:attack]") is never preceded by these words, so it is never suppressed.
const CONDITION_ICON_PATTERN = /(?:at least|for every)\s*\d+\s*\[icon:(?:attack|recruit)\]|\d+\s+or more\s+\[icon:(?:attack|recruit)\]/gi;
// why: D-24746 — "If you played at least N other cards this turn, you get +2[icon:attack]"
// (ca75 KGB Training N=7, co2e Repulsor Rays N=8, xmen Human Cannon N=6) carries no marker, so
// the line parsed to an UNGATED attack:2 granted on every play. This text gate pushes a
// playedThisTurn condition instead. Captures N; non-global so `.exec` is stateless.
const PLAYED_OTHER_CARDS_THRESHOLD_PATTERN = /\bIf you played at least (\d+) other cards this turn\b/i;
// why: WP-825 / D-24758 — "if you played another [team:X] Hero that costs N or more this turn"
// (co2e Nick Fury's Stealth Assault Squad, N=1). Step 1b already pushes the requiresTeam gate for
// the inline [team:X]; this text arm sets its printed cost floor, so the cost-0 S.H.I.E.L.D.
// Agent / Trooper starters no longer satisfy it. Captures the team slug and N; non-global so
// `.exec` is stateless.
const ANOTHER_TEAM_HERO_COST_FLOOR_PATTERN = /\bif you played another\s*\[team:([^\]]+)\]\s*Hero that costs (\d+) or more this turn\b/i;
// why: D-24486 — a NEGATIVE-magnitude attack/recruit icon ("gets -2[icon:attack]",
// "cost -1[icon:recruit]") is a debuff applied to an Adversary or a cost reduction — NEVER a
// resource the player gains. Its leading "-" is the discriminator: Legendary's vocabulary has
// no "you get -N attack" self-grant. The icon-magnitude (Step 2b) and icon→keyword (Step 3)
// extractors drop the sign and read it as a phantom +N SELF-grant (the live bug: Royal Decree's
// "Each Villain that isn't worth at least 5VP gets -1[icon:attack]" granting Player 0 a real +1
// attack every team-ability activation). This pattern locates those negative icons so both
// extractors EXCLUDE their character positions (mirrors CONDITION_ICON_PATTERN). A GRANT icon
// elsewhere on the line ("you get +3[icon:attack]") carries no "-", so it is never suppressed.
const NEGATIVE_MAGNITUDE_ICON_PATTERN = /-\s*\d+\s*\[icon:(?:attack|recruit)\]/gi;
// why: D-24605 — an unsigned attack icon that states an ADVERSARY's printed attack is a
// description of the enemy, never a resource the player gains: "fight the top card of the
// Bystander Deck as if it were a 4[icon:attack] Darkhold Demon Villain" (mdns Wong, Face Your
// Demons), "Defeat a Villain that has 3[icon:attack] or less" (cvwr Stature), "Defeat a Villain
// of 5 [icon:attack] or 6 [icon:attack]" (ff04 Silver Surfer). The sign-dropping Step 2b/3
// extractors read each as a phantom +N self-grant (the live bug: Face Your Demons granting a
// free +4 attack on every play). This pattern locates those stat icons so both extractors
// EXCLUDE them (mirrors CONDITION_ICON_PATTERN / NEGATIVE_MAGNITUDE_ICON_PATTERN). A GRANT
// icon ("you get +3[icon:attack]") never follows "as if it were a", never precedes "or less",
// and never follows "Villain of", so it is never suppressed.
// why: D-24660 — "whose [icon:attack] is less than …" (core/co2e Pure Fury and five
// siblings: "Defeat any Villain or Mastermind whose [icon:attack] is less than the number
// of …") names the TARGET's attack, never a grant. Unsuppressed, Step 3 read the bare icon
// as a magnitude-less `attack` keyword that logged a phantom `attack` no-handler hollow on
// every play (live solo match f4JyVOX_Tq3). Every "whose [icon:attack]" line in data/cards
// is this target-stat form.
// why: D-24662 — "use / spend (any amount of your) [icon:recruit] as [icon:attack]" (core Thor
// God of Thunder and its reprints) names the two resources of a conversion, never a grant of
// either. Unsuppressed, Step 3 read both bare icons as magnitude-less `recruit` and `attack`
// keywords that logged phantom no-handler hollows beside the real recruit-as-attack effect
// (live match s1jtBcEAOfw). Every icon in a match is suppressed, so both are covered.
const ADVERSARY_STAT_ICON_PATTERN = /as if it were an?\s+\d+\s*\[icon:attack\]|\d+\s*\[icon:attack\]\s+or less|Villain of\s+\d+\s*\[icon:attack\](?:\s+or\s+\d+\s*\[icon:attack\])?|whose\s+\[icon:attack\]|(?:use|spend)\s+(?:any amount of your\s+)?\[icon:recruit\]\s+as\s+\[icon:attack\]/gi;
// why: D-24606 — the Annihilation-era Focus cost prefix ("[keyword:Focus] 2[icon:recruit]
// [icon:5] <effect>", the ff04 form "[keyword:Focus 6][icon:recruit] [icon:5] <effect>") is a
// pay-to-activate ability: "you may spend N [icon:recruit] to do <effect>". The cost icon is a
// resource the player SPENDS, and the effect after it only happens when the cost is paid. No
// Focus handler exists, so the Step 2b/3 extractors read the cost as a phantom +N grant AND
// fired the gated effect unconditionally (the live bug: The Power Cosmic granting +9 recruit
// and +9 attack on every play). Every icon from the Focus token to the end of the line is
// suppressed, so the line emits no grant and keeps its honest `focus` unresolved marker.
// Non-global so `.exec` is stateless. The optional " N" covers the ff04 space form.
const FOCUS_COST_PATTERN = /\[keyword:Focus(?:\s+\d+)?\]/i;
// why: D-24622 — "[keyword:Lightshow]: <effect>" is a gated ability (rules v23 ~L1616): once per
// turn, only when at least two Lightshow cards were played this turn, the player uses a SINGLE
// Lightshow ability from any of those cards. No Lightshow handler exists, so the Step 2b/3
// extractors fired every Lightshow line's icon as an unconditional grant on every play (the
// live bug, match 19720cb4: a lone Blazing Flare granted +2 recruit; two Blazing Flares plus
// Twin Blast granted all three abilities). Every icon from the Lightshow token to the end of
// the line is suppressed, so the line emits no grant and keeps its honest `lightshow`
// unresolved marker until the Lightshow executor (count + once-per-turn single choice) exists.
// Non-global so `.exec` is stateless.
const LIGHTSHOW_GATE_PATTERN = /\[keyword:Lightshow\]/i;
// why: extract magnitude from icon-adjacent integers — avoids per-card manual markup (D-21505)
/** Regex for VP-cost-threshold in reveal lines: "2[icon:vp] or less". Non-global; first match only. */
const VP_COST_THRESHOLD_PATTERN = /(\d+)\s*\[icon:vp\]\s*or less/;
/** Regex for [icon:X] icon markup. */
const ICON_PATTERN = /\[icon:([^\]]+)\]/g;
/** Regex for [timing:X] explicit timing markup. */
const TIMING_PATTERN = /\[timing:([^\]]+)\]/;
/**
 * Maps icon markup values to HeroKeyword values.
 *
 * Only icons that directly correspond to a canonical keyword are mapped.
 * Unrecognized icon values are ignored.
 */
const ICON_TO_KEYWORD = {
    attack: 'attack',
    recruit: 'recruit',
    ko: 'ko',
};
/**
 * Maps timing markup values to HeroAbilityTiming values.
 */
const TIMING_MARKUP_MAP = {
    onPlay: 'onPlay',
    onFight: 'onFight',
    onRecruit: 'onRecruit',
    onKO: 'onKO',
    onReveal: 'onReveal',
};
// why: D-24049 — a few keywords fire at a non-onPlay timing by default. Wall-Crawl
// ("when you recruit this Hero, you may put it on top of your deck") fires at recruit,
// so [keyword:Wall-Crawl] must land on an onRecruit hook. This keyword→default-timing
// map is consulted in Step 5: a keyword listed here sets the hook's default timing; an
// explicit [timing:X] marker still overrides; a keyword absent from the map keeps the
// onPlay default.
const KEYWORD_TIMING_DEFAULTS = {
    'wall-crawl': 'onRecruit',
    // why: WP-498 / D-24301 — return-on-discard is reactive: it fires when a card
    // effect discards the marked card from hand, so its hook lands on onDiscard (the
    // parser default is onPlay). The timing is declarative-only — the discardFromHand
    // chokepoint keys on the keyword, not this timing — but the hook must carry it so
    // the per-hook timing-membership drift test passes.
    'return-on-discard': 'onDiscard',
    // why: WP-705 / D-24526 — teleport-on-discard is reactive (Guerrilla Warfare's
    // "when a card effect causes you to discard this card…"), so its hook lands on
    // onDiscard (parser default is onPlay). Declarative-only — the discardFromHand
    // chokepoint keys on the keyword — but the hook must carry it for the per-hook
    // timing-membership drift test.
    'teleport-on-discard': 'onDiscard',
    // why: WP-736 / D-24556 — Excessive Violence resolves at FIGHT time ("Once per turn,
    // you can spend 1 extra attack to fight … using Excessive Violence."), so its fused hook
    // lands on onFight (the parser default is onPlay). The hook still fires at PLAY time to
    // ENROLL the card (executeHeroEffects does not filter by timing); the actual EV abilities
    // fire from the fight move's fireExcessiveViolencePlays. buildExcessiveViolenceFusion sets
    // this timing directly on the fused hook, so this entry documents the default and keeps the
    // keyword→timing map complete.
    'excessive-violence': 'onFight',
};
// why: D-24055 — the rulebook value for Spectrum: ≥3 Hero classes.
const SPECTRUM_CLASS_THRESHOLD = 3;
// why: WP-658 / D-24469 — the allowlist of transform BASE cards whose full printed
// behaviour (its transform condition AND the swap placement) is faithfully modeled, so
// their [keyword:Transform] resolves to an EXECUTABLE transform. Keyed by the base card's
// canonical key `{setAbbr}/{heroSlug}/{cardSlug}`. She-Hulk's `hurl-legal-objections` is
// the first and only member: its "made ≥6 Recruit this turn" gate is the shipped
// recruit-threshold condition (D-24354), authored on the card as `[keyword:recruit-threshold:6]`,
// and its destination is a plain in-play swap. Every OTHER wwhk transform card carries a
// condition the engine does NOT yet model (drew-2, discarded-≥2, KO-pile counts, combat
// outcomes, reveal-cost, feast+KO, gain-a-Wound, …); resolving those would fire an
// UNCONDITIONAL — and therefore unfaithful — swap, so they stay unresolved markers (an
// honest parse-unrecognized hollow, unchanged from today). Each future WP that models a
// card's transform condition adds that card here + its condition marker. Honest-Partial
// Invariant — mirrors the investigate resolver's static-criterion subset.
const SUPPORTED_TRANSFORM_BASES = new Set([
    'wwhk/she-hulk/hurl-legal-objections',
    // why: WP-665 / D-24476 — Amadeus Cho's Gamma-Draining Nanites. Its transform gates on
    // "drew two cards this turn", now modeled by the cardsDrawnThisTurnAtLeast wait-and-see
    // condition (a [keyword:draw-threshold:2] marker on its transform ability line). The
    // card's ability is two hooks (draw / transform), so the draw stays unconditional.
    'wwhk/amadeus-cho/gamma-draining-nanites',
    // why: WP-859 / D-24795 — Miek's Metamorphosis. Its transform gates on "a card with an
    // [icon:attack] icon was KO'd from your deck this turn", modeled by the
    // attackIconCardKoedFromDeckThisTurn wait-and-see condition ([keyword:deck-koed-attack-icon]).
    // FEAST_THEN_TRANSFORM_CARDS splits its one line so the Feast stays unconditional.
    'wwhk/miek-the-unhived/metamorphosis',
    // why: WP-859 / D-24795 — No-Name's Bursting with Life. Its transform gates on "a non-grey Hero
    // was KO'd from your deck this turn", modeled by the nonGreyHeroKoedFromDeckThisTurn
    // wait-and-see condition ([keyword:deck-koed-non-grey-hero]); split like Metamorphosis.
    'wwhk/no-name-brood-queen/bursting-with-life',
]);
// why: WP-857 / D-24793 — cards whose printed "You may [keyword:Feast]" (KO the top card of your
// deck) resolves to the executable `feast` keyword. Keyed by canonical `{setAbbr}/{heroSlug}/
// {cardSlug}`. Each listed line is fully covered by the shipped KO-or-keep reveal (RS-1: the card
// is shown before the choice); Hive King Miek is Metamorphosis's Transform face, reachable once
// that Transform is modeled. World-Spanning Hunger ("Feast up to three times", +2 per non-grey Hero)
// and The Void Unchained (cost-0 check, Transform branch) are deliberately NOT here: their other
// text is unmodeled, so resolving Feast on them would be unfaithful and silence their hollow signal.
// Honest-Partial Invariant — the SUPPORTED_TRANSFORM_BASES / X_GENE_CARDS precedent.
const FEAST_CARDS = new Set([
    'wwhk/miek-the-unhived/devouring-frenzy',
    'wwhk/miek-the-unhived/metamorphosis',
    'wwhk/miek-the-unhived/hive-king-miek',
    'wwhk/no-name-brood-queen/appetite-for-destruction',
    'wwhk/no-name-brood-queen/bursting-with-life',
    // why: WP-859 / D-24795 — World-Spanning Hunger's "Feast up to three times" (FEAST_REPEAT_COUNTS)
    // and its "+2 for each non-grey Hero that was KO'd from your deck this turn" (the
    // non-grey-heroes-koed-from-deck-this-turn count source plus the per-Feast reward) are now
    // modeled, so its Feast resolves.
    'wwhk/no-name-brood-queen/world-spanning-hunger',
]);
// why: WP-859 / D-24795 — "Feast up to three times" is three KO-or-keep prompts (keeping a card is
// choosing not to Feast that time). The parser is text-only and de-duplicates keywords, so the
// count cannot come from the text: the caller reads it here and threads it in as feastRepeatCount.
// Every other FEAST_CARDS card Feasts once.
const FEAST_REPEAT_COUNTS = new Map([
    ['wwhk/no-name-brood-queen/world-spanning-hunger', 3],
]);
// why: WP-859 / D-24795 — a condition gates its whole line's hook, and these two cards print
// "You may [keyword:Feast]. Then, if …, [keyword:Transform] this …" on ONE line, so a condition
// marker would also gate the Feast (the very KO that satisfies it). A parse-only pre-pass
// (splitFeastThenTransformLine, the coalesceCountScaledChooseOne precedent; display text untouched)
// splits the line at its " Then, " into a Feast hook and a Transform hook. The condition marker is
// appended to the end of the printed line, so it lands on the Transform half. Allowlisted, so no
// other card's hooks change (Gamma-Draining Nanites' data already prints two lines).
const FEAST_THEN_TRANSFORM_CARDS = new Set([
    'wwhk/miek-the-unhived/metamorphosis',
    'wwhk/no-name-brood-queen/bursting-with-life',
]);
// why: WP-705 / D-24526 — cards whose bare [keyword:Teleport] display token resolves to
// the reactive `teleport-on-discard` keyword (the mandatory set-aside-and-return-at-end-
// of-turn form). Keyed by canonical key `{setAbbr}/{heroSlug}/{cardSlug}`. Guerrilla
// Warfare is the first and only member — its full printed behaviour is modeled by the
// teleport-on-discard reaction. Every OTHER [keyword:Teleport] card carries the UNMODELED
// general onPlay-Teleport mechanic, so its token stays an honest unresolved marker
// (parse-unrecognized hollow) — the Honest-Partial Invariant, mirroring SUPPORTED_TRANSFORM_BASES.
const TELEPORT_ON_DISCARD_CARDS = new Set([
    'ssw2/ruby-summers/guerrilla-warfare',
]);
// why: WP-723 / D-24544 — cards whose printed [keyword:X-Gene] is RECOGNIZED (per-card
// allowlisted, keyed by canonical `{setAbbr}/{heroSlug}/{cardSlug}`). On an allowlisted
// card the marker's co-located [hc:X] becomes the heroClassInDiscardPile condition (Step
// 1a) and the trailing effect is separately marked. Only the two cards whose trailing
// effect is expressible today (draw / optional-ko-hand-discard) are listed; heir-to-
// wolverine's count-scaled "Berserk that many times" is UNMODELED, so it is deliberately
// NOT here — its [keyword:X-Gene] stays an honest parse-unrecognized hollow. Recognizing
// X-Gene globally would silence that signal; the per-card allowlist is the transform /
// teleport-on-discard precedent (Honest-Partial Invariant).
const X_GENE_CARDS = new Set([
    'xmen/x-23/adamantium-foot-claws',
    'xmen/x-23/bioengineered-assassin',
]);
// why: WP-735 / D-24555 — cards whose printed "Digest N / Indigestion" lines are FUSED into
// one executable digest-indigestion hook (per-card allowlisted, keyed by canonical
// `{setAbbr}/{heroSlug}/{cardSlug}`). Scope = the Core-4 whose branches reduce to shipped
// attack / recruit / draw / rescue executors. Every OTHER Digest card (insatiable-hunger) has a
// branch the engine does not yet model, so its [keyword:Digest N] / [keyword:Indigestion] stays
// unresolved — the Honest-Partial Invariant, mirroring SUPPORTED_TRANSFORM_BASES / X_GENE_CARDS.
// why: WP-754 / D-24581 — hungry-for-action un-deferred: its single Digest 3 branch ("You may
// discard a card. If you do, draw a card.") is the optional-discard-draw executor; it has no
// Indigestion line (the Cauldron of the Cosmos single-branch shape).
// why: WP-740 / D-24562 — play-to-the-crowd un-deferred: its Digest branch is attack-per-count
// over victory-bystanders with perEach 2 (the widened COUNT_SCALED_PATTERN), its Indigestion
// branch is rescue 2, and its doubled [team:venomverse] "both" line parses to one condition
// carrying requiredMatches 2 (D-24563).
const DIGEST_INDIGESTION_CARDS = new Set([
    'vnom/venompool/digest-that-chimichanga',
    'vnom/carnage/carnivore',
    'vnom/venom/devouring-drool',
    'vnom/venomized-dr-strange/cauldron-of-the-cosmos',
    'vnom/venompool/play-to-the-crowd',
    'vnom/venom-rocket/hungry-for-action',
]);
// why: WP-735 / D-24555 — reads the printed "[keyword:Digest N]" threshold. The space-form
// token (`Digest<space>N`) is deliberately NOT matched by KEYWORD_PATTERN (which requires `:N`
// or `]` right after the name), so it needs a dedicated pattern; capture group 1 is the
// threshold N. The printed display token is left in the card text unchanged (AbilityText.vue
// renders it). Non-global so `.exec`/`.test` are stateless.
const DIGEST_MARKER_PATTERN = /\[keyword:Digest (\d+)\]/;
// why: WP-735 / D-24555 — detects the Indigestion branch line (the bare [keyword:Indigestion]
// display token, which KEYWORD_PATTERN sees as an unrecognized keyword → unresolved marker).
const INDIGESTION_MARKER_PATTERN = /\[keyword:Indigestion\]/;
// why: WP-735 / D-24555 — detects the "[hc:X]: Instead, you get both." upgrade line. Its
// [hc:X] / [team:X] condition is parsed separately (Step 1a/1b) into the bothCondition; this
// pattern only identifies which line is the upgrade so the fusion consumes it. Case-insensitive.
const DIGEST_BOTH_LINE_PATTERN = /\bInstead\b[^.]*\bboth\b/i;
// why: WP-736 / D-24556 — the printed "[keyword:Excessive Violence]" space-form display token.
// KEYWORD_PATTERN (`\[keyword:([a-zA-Z][a-zA-Z-]*)(?::(\d+))?\]`) requires `:N` or `]` right
// after the keyword name, so a SPACE inside "Excessive Violence" means it can never match — the
// token falls through as an unresolved marker today (the hollow being fixed). This dedicated
// pattern identifies which ability line carries the EV ability so the fusion consumes it; the
// printed display token is left in the card text unchanged (AbilityText.vue renders it). Non-global
// so `.test` is stateless.
const EXCESSIVE_VIOLENCE_PATTERN = /\[keyword:Excessive Violence\]/;
// why: WP-736 / D-24556 — the allowlist of Venomverse cards whose Excessive Violence ability
// reduces to a shipped inner executor (draw / recruit / rescue / optional-ko-hand-discard), so
// their [keyword:Excessive Violence] resolves to an EXECUTABLE fused hook. Keyed by the canonical
// `{setAbbr}/{heroSlug}/{cardSlug}` key (the DIGEST_INDIGESTION_CARDS / SUPPORTED_TRANSFORM_BASES
// per-card-allowlist precedent). Every OTHER EV card (carnage/feast-or-famine needs a reveal-top
// cost-0 KO LOOP executor that does not exist; the out-of-set dead/slapstick; can-i-get line 0's
// passive rescue-doubler; any future member) keeps its honest parse-unrecognized hollow — the
// Honest-Partial Invariant. Resolve ONLY the listed cards; every other EV card keeps an honest
// unresolved marker.
// why: WP-754 / D-24581 — carnage/gruesome-feast and mgtg drax/remove-his-spine un-deferred: their
// "Reveal the top card of your deck. You may KO it." is the reveal-top-may-ko executor, fired at
// fight time like the other inner effects.
const EXCESSIVE_VIOLENCE_CARDS = new Set([
    'vnom/carnage/rending-claws',
    'vnom/venom/razor-teeth',
    'vnom/venom-rocket/serious-overkill',
    'vnom/venompool/can-i-get-a-little-gratitude',
    'vnom/carnage/gruesome-feast',
    'mgtg/drax/remove-his-spine',
]);
// why: WP-668 / D-24481, D-24670 — keywords whose handler computes the line's own
// "+[icon:attack] equal to …" amount. The printed icon on such a line is that grant, not
// a separate flat attack, so the icon-suppression in the keyword pass drops the plain
// 'attack' keyword. Unmarked "+[icon:attack] equal to" lines are NOT listed — they stay
// honest hollows (D-24649).
const SELF_COMPUTED_ATTACK_KEYWORDS = new Set([
    'reveal-herodeck-attack',
    'reveal-cost-attack',
    'reveal-attack-choose',
    'victory-villain-attack',
    // why: WP-850 / D-24786 — "You get +[icon:attack] equal to those cards' printed [icon:attack]"
    // (Firepower from the Future): the handler sums the discarded cards' printed Attack, so both
    // icons on the line are that grant, never a separate magnitude-less flat attack.
    'discard-top-printed-attack',
    // why: WP-858 / D-24794 — the two Ritual Artifact lines (Staff of Legba "+[icon:attack] equal to
    // that Hero's cost", Medallion of Many Loas "+1[icon:attack] for each Hero Class") print the
    // reward the useRitualArtifact move computes; neither icon is a flat grant at play.
    'ritual-recruited-hero',
    'ritual-hero-classes',
]);
// why: WP-765 / D-24598 — the cards whose "[keyword:Sunlight]: … / [keyword:Moonlight]: … /
// [X]: Instead, you get both." lines are FUSED into one executable `day-night-both` hook (the
// WP-735 digest-indigestion composite precedent). HeroCondition has no OR, so emitting the three
// lines as separate hooks would double-fire a branch when the upgrade holds AND that branch's
// day/night state is also in effect. Keyed by the canonical `{setAbbr}/{heroSlug}/{cardSlug}`
// (the DIGEST_INDIGESTION_CARDS allowlist posture). Nanite's upgrade line is four [team:x-men]
// tokens, which the parser collapses into one condition carrying requiredMatches 4 (D-24563).
const DAY_NIGHT_BOTH_CARDS = new Set([
    'mdns/werewolf-by-night/release-the-beast',
    'nmut/warlock/analyze-planetary-rotation',
    'nmut/warlock/nanite-shapeshifter',
]);
// why: WP-765 / D-24598 — the printed day/night display tokens. KEYWORD_PATTERN already matches
// them (Step 2 arms below); these non-global, case-insensitive patterns only locate WHICH line of
// a card is its Sunlight / Moonlight line for the fusion and the unmodeled-line lookup.
const SUNLIGHT_MARKER_PATTERN = /\[keyword:Sunlight\]/i;
const MOONLIGHT_MARKER_PATTERN = /\[keyword:Moonlight\]/i;
// why: WP-765 / D-24598 — day/night lines whose printed effect the engine does not yet model.
// Keyed `{setAbbr}/{heroSlug}/{cardSlug}:{sunlight|moonlight}`. Each still gets its day/night
// condition, but ALSO records its `sunlight`/`moonlight` token as an unresolved marker and drops
// the parsed attack/recruit grant. Without this suppression these gated lines would silently
// grant bonuses the engine does not model — the icon on "If you do, you get +2[icon:attack]" or
// "+1[icon:attack] for each …" would fire as a flat grant (the D-24570 parsed-grant suppression
// sibling). They stay visibly hollow (parse-unrecognized) until a named follow-up models them.
const DAY_NIGHT_UNMODELED_LINES = new Set([
    // why: WP-767 / D-24606 — Snarling Fangs' Moonlight line is now modelled (the per-defeat
    // trigger + optional-ko-your-hero markers), so it left this list; eight entries remain.
    'mdns/werewolf-by-night/track-the-captives:moonlight',
    'mdns/morbius/scalded-by-sunlight:sunlight',
    'nmut/sunspot/solar-powered:sunlight',
    'nmut/sunspot/thermokinetic-fury:sunlight',
    'nmut/sunspot/empyreal-force:sunlight',
    'nmut/wolfsbane/night-vision:moonlight',
    'nmut/wolfsbane/nocturnal-savagery:moonlight',
    'nmut/mirage/haunted-by-the-demon-bear:moonlight',
]);
// why: D-24628 — lines whose resource grant depends on an unmodeled cost or event clause, keyed
// `{setAbbr}/{heroSlug}/{cardSlug}`. They keep their printed play gate, drop the parsed grant, and
// record CONDITIONAL_GRANT_UNMODELED_MARKER, until a follow-up models the clause.
const CONDITIONAL_GRANT_UNMODELED_LINES = new Set([
    'bkpt/general-okoye/direct-the-agents-of-wakanda',
    'vill/magneto/mutants-will-rule',
]);
// why: D-24628 — one shared mechanic name so these lines aggregate as one runtime hollow row.
/** Unresolved marker recorded for an unmodeled conditional resource grant. */
const CONDITIONAL_GRANT_UNMODELED_MARKER = 'unmodeled-conditional-grant';
/** Regex for a printed attack or recruit icon. */
const RESOURCE_ICON_PATTERN = /\[icon:(?:attack|recruit)\]/;
// why: WP-765 / D-24598 — the two day/night condition types. Kept as a named set so the
// unmodeled-line suppression can keep ONLY the day/night gate on such a line.
const DAY_NIGHT_CONDITION_TYPES = new Set([
    'sunlightInEffect',
    'moonlightInEffect',
]);
// why: WP-723 / D-24544 — detects an X-Gene line. On such a line (for an allowlisted card)
// the leading [hc:X] is the discard-condition class ("a [class] card in your discard pile"),
// NOT a heroClassMatch play-this-turn gate, so Step 1a reroutes it (mirrors the reveal-from-
// hand / D-24470 co-located-token suppression). Non-global, stateless `.test`; case-insensitive
// to match the [keyword:X] lowercasing.
const X_GENE_MARKER_PATTERN = /\[keyword:x-gene\]/i;
// why: D-24801 (supersedes D-24074's class grant) — detects whether an ability line carries
// the Size-Changing keyword. On such a line the same-line `[hc:...]` tokens are the classes
// that discount the card's recruit cost (rules v23 p.33: "recruit this card for 2 less if you
// played any [class] cards this turn"), not `heroClassMatch` play-conditions — so Step 1a
// routes them to the Size-Changing class list instead of conditions. Non-global, stateless
// `.test` (no lastIndex concern); case-insensitive to match the `[keyword:X]` lowercasing.
const SIZE_CHANGING_MARKER_PATTERN = /\[keyword:size-changing\]/i;
// why: D-24801 — the Microscopic Size-Changing keyword (rules v23 p.30): each same-line
// `[hc:X]` icon is one "2 less" step, earned by one matching-class card played this turn.
// SIZE_CHANGING_MARKER_PATTERN never matches it (the token body starts "Microscopic").
const MICROSCOPIC_SIZE_CHANGING_MARKER_PATTERN = /\[keyword:microscopic size-changing\]/i;
// why: WP-535 fix-forward — Rogue's Copy Powers text carries a MID-SENTENCE [hc:covert]
// ("This card is both [hc:covert] and the color you copy") that describes the class the
// card BECOMES, NOT a play gate. Like size-changing, a copy-powers line's [hc:X] must be
// suppressed at Step 1a so no spurious heroClassMatch condition gates the copy behind
// covert synergy. Non-global, stateless `.test`; case-insensitive.
const COPY_POWERS_MARKER_PATTERN = /\[keyword:copy-powers\]/i;
// why: WP-659 / D-24470 — detects a reveal-from-hand line (Psychic Link: "Each player may
// reveal another [team:x-men] Hero. Each player who does draws a card."). On such a line the
// co-located [team:X]/[hc:X] is the reveal CRITERION, not a requiresTeam/heroClassMatch play
// gate — Step 1a/1b suppress it exactly as they do for size-changing / copy-powers / resolved
// investigate. Non-global, stateless `.test`; case-insensitive to match the [keyword:X] casing.
const REVEAL_FROM_HAND_MARKER_PATTERN = /\[keyword:reveal-from-hand\]/i;
// why: WP-843 / D-24777 — detects a reveal-or-gain-wound line (co2e Crazed Rampage: "Each
// player reveals another [hc:strength] Hero or gains a Wound."). The reveal-from-hand resolver
// also reads this marker's criterion, so the co-located [hc:X] / [team:X] is a reveal
// CRITERION, not a heroClassMatch / requiresTeam play-gate — the existing Step 1a / 1b
// suppression drops it with no new flag (the D-24470 / D-24530 family). Non-global, stateless
// `.test`; case-insensitive to match the [keyword:X] casing.
const REVEAL_OR_GAIN_WOUND_EACH_MARKER_PATTERN = /\[keyword:reveal-or-gain-wound-each\]/i;
// why: WP-659 / D-24470 — the first [hc:X] / [team:X] token on a reveal-from-hand line is the
// reveal criterion. Two dedicated stateless patterns (mirroring the investigate clause
// patterns) so the resolver reads exactly one token into an InvestigateCriterion.
const REVEAL_FROM_HAND_HC_PATTERN = /\[hc:([a-z0-9-]+)\]/i;
const REVEAL_FROM_HAND_TEAM_PATTERN = /\[team:([a-z0-9-]+)\]/i;
// why: WP-564 / D-24373 — the printed default look count for Investigate ("look at the
// top two cards of your deck"). Carried as a descriptor field so the deferred
// "look at three cards instead of two" modifier can set it later without reshaping the
// effect (mirrors the reveal family's revealCount).
const INVESTIGATE_DEFAULT_LOOK_COUNT = 2;
// why: WP-564 / D-24373 — locates the `[keyword:Investigate]` marker so the criterion
// parser reads the text AFTER it. Case-insensitive to match the `[keyword:X]` casing.
// Non-global, stateless `.exec`.
const INVESTIGATE_MARKER_PATTERN = /\[keyword:investigate\]/i;
// why: WP-564 / D-24373 — the criterion-form whitelist, each anchored `^…$` on the
// TRIMMED criterion text so a trailing disposition / modifier clause (Discover the
// Bodies' "…costs 0. KO that card.", Crack the Case's "…icon. You may draw…") fails to
// match and stays deferred (its marker records parse-unrecognized). Case-insensitive.
/** "for a card with an [icon:attack] icon." / "…with a [icon:recruit] icon." */
const INVESTIGATE_ICON_PATTERN = /^for a card with an? \[icon:(attack|recruit)\] icon\.$/i;
/** "for a card that costs N." (exact) */
const INVESTIGATE_COST_EXACT_PATTERN = /^for a card that costs (\d+)\.$/i;
/** "for a card that costs N or less." */
const INVESTIGATE_COST_LTE_PATTERN = /^for a card that costs (\d+) or less\.$/i;
/** "for a card that costs N or more." */
const INVESTIGATE_COST_GTE_PATTERN = /^for a card that costs (\d+) or more\.$/i;
/** "for a [hc:tech] card." (leading class form) */
const INVESTIGATE_HC_LEADING_PATTERN = /^for an? \[hc:([a-z0-9-]+)\] card\.$/i;
/** "for a card that's <clause>." — the clause is one or more hc/team tokens joined by " and/or ". */
const INVESTIGATE_THATS_PATTERN = /^for a card that's (.+)\.$/i;
/** A single `[hc:X]` token — the whole trimmed clause part. */
const INVESTIGATE_CLAUSE_HC_PATTERN = /^\[hc:([a-z0-9-]+)\]$/i;
/** A single `[team:X]` token — the whole trimmed clause part. */
const INVESTIGATE_CLAUSE_TEAM_PATTERN = /^\[team:([a-z0-9-]+)\]$/i;
/**
 * Computes the character ranges of `[icon:recruit|attack]` tokens that sit inside a
 * threshold/rate CONDITION clause (WP-660 / D-24471), so the icon-magnitude (Step 2b)
 * and icon→keyword (Step 3) extractors can exclude them and not emit a phantom grant.
 *
 * Each `CONDITION_ICON_PATTERN` match spans the whole phrase; the icon token is its last
 * `[icon:…]`, so the returned range is `[iconStart, matchEnd)`.
 *
 * @param abilityText - The raw ability line.
 * @returns Character ranges of the condition icons (empty when the line has none).
 */
function computeConditionIconRanges(abilityText) {
    const ranges = [];
    const regex = new RegExp(CONDITION_ICON_PATTERN.source, 'gi');
    let match = regex.exec(abilityText);
    while (match !== null) {
        // why: the icon is the LAST "[icon:" in the matched phrase; suppress from there to the
        // phrase end so both extractors (icon-magnitude starts at the digit, icon→keyword starts
        // at the "[") overlap the range and are skipped.
        const iconOffset = match[0].lastIndexOf('[icon:');
        ranges.push({ start: match.index + iconOffset, end: match.index + match[0].length });
        match = regex.exec(abilityText);
    }
    return ranges;
}
/**
 * Computes the character ranges of NEGATIVE-magnitude `[icon:attack|recruit]` tokens
 * ("gets -2[icon:attack]", "cost -1[icon:recruit]") so the icon-magnitude (Step 2b) and
 * icon→keyword (Step 3) extractors can exclude them and not emit a phantom player grant
 * (D-24486). A negative attack/recruit icon is always an Adversary debuff or a cost
 * reduction — never a resource the player gains — so the leading "-" alone identifies it.
 *
 * Each match runs from the "-" through the icon's closing "]"; the returned range starts at
 * the icon token (`[icon:`) and ends at the match end, matching the CONDITION_ICON_PATTERN
 * range convention: the icon→keyword match (starts at "[") overlaps directly, and the
 * icon-magnitude match (starts at the digit, ends at "]") overlaps by its tail.
 *
 * @param abilityText - The raw ability line.
 * @returns Character ranges of the negative-magnitude icons (empty when the line has none).
 */
function computeNegativeMagnitudeIconRanges(abilityText) {
    const ranges = [];
    const regex = new RegExp(NEGATIVE_MAGNITUDE_ICON_PATTERN.source, 'gi');
    let match = regex.exec(abilityText);
    while (match !== null) {
        const iconOffset = match[0].lastIndexOf('[icon:');
        ranges.push({ start: match.index + iconOffset, end: match.index + match[0].length });
        match = regex.exec(abilityText);
    }
    return ranges;
}
/**
 * Computes the character ranges of unsigned `[icon:attack]` tokens that state an
 * ADVERSARY's printed attack ("as if it were a 4[icon:attack] … Villain", "a Villain that
 * has 3[icon:attack] or less", "a Villain of 5 [icon:attack] or 6 [icon:attack]") so the
 * icon-magnitude (Step 2b) and icon→keyword (Step 3) extractors can exclude them and not
 * emit a phantom player grant (D-24605).
 *
 * A match may hold two icons (the "Villain of N or M" form), so EVERY `[icon:…]` token in
 * the match is returned as its own `[iconStart, iconEnd)` range. The icon→keyword match
 * (starts at "[") overlaps directly; the icon-magnitude match (digit through "]") overlaps
 * by its tail.
 *
 * @param abilityText - The raw ability line.
 * @returns Character ranges of the adversary-stat icons (empty when the line has none).
 */
function computeAdversaryStatIconRanges(abilityText) {
    const ranges = [];
    const regex = new RegExp(ADVERSARY_STAT_ICON_PATTERN.source, 'gi');
    let match = regex.exec(abilityText);
    while (match !== null) {
        let iconOffset = match[0].indexOf('[icon:');
        while (iconOffset !== -1) {
            const iconEnd = match[0].indexOf(']', iconOffset) + 1;
            ranges.push({ start: match.index + iconOffset, end: match.index + iconEnd });
            iconOffset = match[0].indexOf('[icon:', iconEnd);
        }
        match = regex.exec(abilityText);
    }
    return ranges;
}
/**
 * Computes the Focus-gated character range of an ability line (D-24606): from the
 * `[keyword:Focus]` / `[keyword:Focus N]` token to the end of the line. The cost icon and
 * the effect it pays for both sit in this range, so the icon-magnitude (Step 2b) and
 * icon→keyword (Step 3) extractors skip every icon in it until a Focus handler exists.
 *
 * @param abilityText - The raw ability line.
 * @returns The single Focus-gated range, or an empty array when the line has no Focus cost.
 */
function computeFocusGatedRanges(abilityText) {
    const focusMatch = FOCUS_COST_PATTERN.exec(abilityText);
    if (focusMatch === null) {
        return [];
    }
    return [{ start: focusMatch.index, end: abilityText.length }];
}
/**
 * Computes the Lightshow-gated character range of an ability line (D-24622): from the first
 * `[keyword:Lightshow]` token to the end of the line. The gated effect sits in this range, so
 * the icon-magnitude (Step 2b) and icon→keyword (Step 3) extractors skip every icon in it
 * until a Lightshow executor exists.
 *
 * @param abilityText - The raw ability line.
 * @returns The single Lightshow-gated range, or an empty array when the line has no Lightshow gate.
 */
function computeLightshowGatedRanges(abilityText) {
    const lightshowMatch = LIGHTSHOW_GATE_PATTERN.exec(abilityText);
    if (lightshowMatch === null) {
        return [];
    }
    return [{ start: lightshowMatch.index, end: abilityText.length }];
}
/**
 * Returns whether a match span `[matchStart, matchEnd)` overlaps any suppressed condition
 * icon range (WP-660 / D-24471). Used to skip an icon the extractors would otherwise read
 * as a resource grant.
 *
 * @param matchStart - Start index of the icon (or icon-magnitude) match.
 * @param matchEnd - End index of the match.
 * @param ranges - Suppressed condition-icon ranges from computeConditionIconRanges.
 * @returns True when the match overlaps a suppressed range.
 */
function overlapsSuppressedRange(matchStart, matchEnd, ranges) {
    for (const range of ranges) {
        if (matchStart < range.end && matchEnd > range.start) {
            return true;
        }
    }
    return false;
}
/**
 * Extracts structured hero ability metadata from a single ability text.
 *
 * Follows the authoritative parsing order:
 * 1. Extract [hc:X] -> HeroCondition entries
 * 2. Extract [keyword:X] -> HeroKeyword entries
 * 3. Extract [icon:X] -> HeroKeyword entries
 * 4. Normalize keywords (dedup, validate against union)
 * 5. Assign timing (explicit markup or default 'onPlay')
 *
 * No step depends on results of a later step.
 */
function parseAbilityText(abilityText, 
// why: WP-658 / D-24469 — the caller (buildHeroAbilityHooks) knows the card's
// canonical key and passes whether this card's transform is in
// SUPPORTED_TRANSFORM_BASES. parseAbilityText itself is text-only (it never sees
// the card id), so the support decision is threaded in. Defaults false so every
// other caller/test keeps the pre-WP-658 behaviour (a [keyword:Transform] on a
// non-supported card stays an unresolved marker).
// why: WP-765 / D-24598 — dayNightUnmodeled is resolved by the caller from
// DAY_NIGHT_UNMODELED_LINES (parseAbilityText never sees the card id). Defaults false.
options = {}) {
    const transformSupported = options.transformSupported === true;
    // why: WP-705 / D-24526 — the caller resolves whether this card is in
    // TELEPORT_ON_DISCARD_CARDS and threads it in (parseAbilityText is text-only and
    // never sees the card id). Defaults false so a [keyword:Teleport] on any non-listed
    // card stays an honest unresolved marker.
    const teleportOnDiscardSupported = options.teleportOnDiscardSupported === true;
    // why: WP-723 / D-24544 — the caller resolves whether this card is in X_GENE_CARDS and
    // threads it in (parseAbilityText is text-only and never sees the card id). Defaults false
    // so a [keyword:X-Gene] on any non-allowlisted card (heir-to-wolverine) stays an honest
    // unresolved marker and its co-located [hc:X] stays a heroClassMatch gate — both unchanged.
    const xGeneSupported = options.xGeneSupported === true;
    // why: WP-765 / D-24598 — true only for a DAY_NIGHT_UNMODELED_LINES line (see the Step 2 arm
    // and the unmodeled-line suppression before the 'conditional' keyword below).
    const dayNightUnmodeled = options.dayNightUnmodeled === true;
    // why: D-24628 — true only for a CONDITIONAL_GRANT_UNMODELED_LINES card, resolved by the caller
    // (see the suppression before the 'conditional' keyword below).
    const conditionalGrantUnmodeled = options.conditionalGrantUnmodeled === true;
    // why: WP-857 / D-24793 — the caller resolves whether this card is in FEAST_CARDS and threads
    // it in (parseAbilityText is text-only and never sees the card id). Defaults false so a
    // [keyword:Feast] on any non-allowlisted card stays an honest unresolved marker.
    const feastSupported = options.feastSupported === true;
    // why: WP-859 / D-24795 — resolved by the caller from FEAST_REPEAT_COUNTS ("Feast up to three
    // times"); 1 for every other card, so a one-Feast line builds one `feast` effect as before.
    const feastRepeatCount = options.feastRepeatCount ?? 1;
    // why: WP-859 / D-24795 — set by the deck-koed-attack-icon marker arm: on that line the printed
    // [icon:attack] is the condition's criterion ("a card with an [icon:attack] icon"), not a grant.
    let lineReadsAttackIconDeckKo = false;
    const keywords = [];
    const heroClassConditions = [];
    const teamConditions = [];
    // why: D-24801 — the discount classes parsed from a Size-Changing line's same-line
    // `[hc:...]` tokens (Step 1a). Empty on every non-Size-Changing line and on a
    // Size-Changing line with no `[hc:X]` (recognized, no discount — graceful empty, no throw).
    const sizeChangingClasses = [];
    // why: D-24801 — when the line carries [keyword:Size-Changing], its [hc:...] tokens are
    // the discount classes, not heroClassMatch conditions. Computed once up front so Step 1a
    // routes every same-line [hc:...] to the Size-Changing list (guardrail #4: extract ALL).
    const lineHasSizeChanging = SIZE_CHANGING_MARKER_PATTERN.test(abilityText);
    // why: D-24801 — a Microscopic Size-Changing line's [hc:...] icons, one entry per icon
    // (repeats kept: three [hc:tech] icons are three 2-less steps). Not play-conditions.
    const microscopicSizeChangingClasses = [];
    const lineHasMicroscopicSizeChanging = MICROSCOPIC_SIZE_CHANGING_MARKER_PATTERN.test(abilityText);
    // why: D-24801 — the multi-word token never matches KEYWORD_PATTERN (Step 2), so the
    // recognized keyword is recorded here; without it the line would park the unresolved
    // 'microscopic-size-changing' marker and read as a parse-unrecognized hollow.
    if (lineHasMicroscopicSizeChanging) {
        keywords.push('microscopic-size-changing');
    }
    // why: WP-535 fix-forward — a copy-powers line's [hc:X] is the DESCRIPTIVE class the card
    // gains, not a play gate; suppress its heroClassMatch condition (see Step 1a).
    const lineHasCopyPowers = COPY_POWERS_MARKER_PATTERN.test(abilityText);
    // why: WP-564 / D-24373 — resolve the static Investigate criterion up front (undefined
    // for a deferred / non-Investigate line). On a RESOLVED investigate line the same-line
    // `[hc:…]` / `[team:…]` / `[icon:attack|recruit]` tokens are the CRITERION, not
    // conditions or resource grants, so Steps 1a / 1b / 3 suppress them (mirrors the
    // size-changing / copy-powers Step 1a routing). No in-scope resolved card carries a
    // separate gate of those token types; a gated card (Discover the Bodies' `[hc:covert]:`)
    // does NOT resolve, so its gate is preserved (the resolution returns undefined).
    const investigateCriteria = tryResolveInvestigateFromLine(abilityText);
    const lineHasResolvedInvestigate = investigateCriteria !== undefined;
    // why: WP-659 / D-24470 — resolve the reveal-from-hand criterion up front (undefined for a
    // non-reveal-from-hand line). On such a line the co-located `[team:X]` / `[hc:X]` is the
    // reveal CRITERION, not a requiresTeam / heroClassMatch play-gate, so Steps 1a / 1b suppress
    // it (mirrors the size-changing / copy-powers / investigate routing). This is the fix for
    // Psychic Link's mid-sentence `[team:x-men]` being mis-read as a spurious "played another
    // X-Men Hero this turn" gate.
    const revealFromHandCriterion = tryResolveRevealFromHandCriterion(abilityText);
    const lineHasRevealFromHand = revealFromHandCriterion !== undefined;
    // why: WP-729 / D-24550 — on a reveal-rule line carrying a TRAIT predicate
    // (`[keyword:reveal:team-X:draw]` / `[keyword:reveal:hc-X:draw]` — Card Shark's
    // "Reveal the top card. If it's an [team:x-men] Hero, draw it."), the co-located
    // `[team:X]` / `[hc:X]` is the reveal CRITERION, not a requiresTeam / heroClassMatch
    // play-gate. Suppress it from Steps 1a / 1b (mirrors the reveal-from-hand / D-24470
    // precedent). Scoped to the trait-predicate reveal marker only; a cost-only reveal
    // line (`[keyword:reveal:2]`) does not set the flag and is byte-unaffected.
    const lineHasRevealTraitCriterion = /\[keyword:reveal:(?:team|hc)-[a-z][a-z0-9-]*:/.test(abilityText);
    // why: WP-723 / D-24544 — an X-Gene line fires the Step 1a reroute + Step 2 consume ONLY
    // for an allowlisted card (xGeneSupported AND the marker present). On such a line the leading
    // [hc:X] is the discard-condition class, not a heroClassMatch play-gate; on any non-allowlisted
    // card the flag is false, so [hc:X] stays a heroClassMatch and [keyword:X-Gene] stays an
    // unresolved marker (heir-to-wolverine's honest hollow) — recognition is per-card.
    const lineHasXGene = xGeneSupported && X_GENE_MARKER_PATTERN.test(abilityText);
    // why: WP-681 / D-24498 — on a Battlefield Promotion line ([keyword:optional-ko-shield-officer])
    // the co-located `[team:shield]` describes the KO TARGET ("KO a [team:shield] Hero"), NOT a
    // requiresTeam play-gate. Suppress it from Step 1b so the card fires unconditionally (mirrors
    // the reveal-from-hand / investigate criterion suppression). The engine's koTeamFilter enforces
    // the S.H.I.E.L.D.-only KO target at resolve time; the setup gate must not also block the play.
    const lineHasOptionalKoShieldOfficer = abilityText.includes('[keyword:optional-ko-shield-officer]');
    // why: WP-826 / D-24759 — likewise on a Mobilize for War line ([keyword:ko-up-to-shield-hand-discard])
    // the `[team:shield]` names the KO TARGET ("KO up to two [team:shield] Heroes"), not a play-gate; the
    // entry's koTeamFilter enforces it at resolve time, so Step 1b emits no requiresTeam gate for it.
    const lineHasKoUpToShield = abilityText.includes('[keyword:ko-up-to-shield-hand-discard:');
    // why: D-24530 — on a Pure Fury line ([keyword:pure-fury]) the co-located `[team:shield]`
    // describes WHICH Heroes to count in the KO pile ("less than the number of [team:shield]
    // Heroes in the KO pile"), NOT a requiresTeam play-gate. Suppress it from Step 1b so the
    // card fires unconditionally (mirrors the optional-ko-shield-officer / reveal-from-hand
    // suppression). heroEffectPureFury reads the KO pile at resolve time; the setup gate must
    // not block the play on "another shield Hero played this turn" (live bug: Pure Fury blocked
    // whenever no shield Hero preceded it — 2p Red Skull / Midtown match yqj7YblJCt4).
    const lineHasPureFury = abilityText.includes('[keyword:pure-fury]');
    // why: D-24628 — when a line opens with an explicit gate prefix of [hc:X] / [team:X] tokens
    // and a colon ("[hc:strength]: Return a [team:shield] Hero…"), that prefix IS the play gate;
    // a [team:Y] after the colon names a target or criterion, not "another Y Hero played this
    // turn" (live: Cannonball Natural Leader logged "needs another shield Hero", match 039e3dce).
    // Lines with no such prefix keep the old behavior, because their inline [team:X] can be a
    // plain-English condition ("if you played another [team:shield] Hero") or a count gate.
    const leadingGatePrefixMatch = LEADING_GATE_PREFIX_PATTERN.exec(abilityText);
    const leadingGatePrefixLength = leadingGatePrefixMatch === null ? 0 : leadingGatePrefixMatch[0].length;
    // why: WP-673 / D-24488 — when the line carries the worthy count-scaled marker,
    // its `[keyword:Worthy]` token is the COUNT CRITERION ("each other card … that
    // makes you Worthy"), not a heroCostAtLeastInHandOrPlay play-gate — so Step 2
    // suppresses the Worthy condition it would otherwise push (mirrors the
    // size-changing / investigate descriptive-token suppression above).
    const lineHasWorthyCountScaledAttack = WORTHY_COUNT_SCALED_MARKER_PATTERN.test(abilityText);
    // why: WP-660 / D-24471 — character ranges of `[icon:recruit|attack]` tokens that are
    // the THRESHOLD/RATE of a "made at least N", "for every N", or "N or more" CONDITION
    // clause. Steps 2b (icon-magnitude) and 3 (icon→keyword) skip any icon overlapping these
    // ranges so a condition reference is never read as a resource grant (the spurious-recruit
    // bug). A grant icon elsewhere on the line is unaffected (positional, not line-level).
    const suppressedIconRanges = computeConditionIconRanges(abilityText);
    // why: D-24486 — also suppress NEGATIVE-magnitude attack/recruit icons (Adversary
    // debuffs / cost reductions, "gets -2[icon:attack]"), so the sign-dropping Step 2b/3
    // extractors never read one as a phantom player grant (the Royal Decree +1 bug). Same
    // positional range machinery as the condition-icon suppression above.
    for (const negativeIconRange of computeNegativeMagnitudeIconRanges(abilityText)) {
        suppressedIconRanges.push(negativeIconRange);
    }
    // why: D-24605 — also suppress unsigned attack icons that state an Adversary's printed
    // attack ("as if it were a 4[icon:attack] … Villain", "3[icon:attack] or less"), so the
    // Step 2b/3 extractors never read one as a phantom player grant (the Face Your Demons
    // +4 bug). Same positional range machinery as the two suppressions above.
    for (const adversaryStatIconRange of computeAdversaryStatIconRanges(abilityText)) {
        suppressedIconRanges.push(adversaryStatIconRange);
    }
    // why: D-24606 — also suppress every icon in a Focus-gated segment (the pay-to-activate
    // cost and the effect it buys), so neither is read as a free grant (The Power Cosmic +9 bug).
    for (const focusGatedRange of computeFocusGatedRanges(abilityText)) {
        suppressedIconRanges.push(focusGatedRange);
    }
    // why: D-24622 — also suppress every icon in a Lightshow-gated segment, so an ungated
    // Lightshow ability is never a free grant (the lone-Blazing-Flare +2 recruit bug).
    for (const lightshowGatedRange of computeLightshowGatedRanges(abilityText)) {
        suppressedIconRanges.push(lightshowGatedRange);
    }
    const effects = [];
    // why: D-24031 — composition markers (Berserk) accumulate here as deep copies of their
    // registry AST, kept separate from `keywords`/`effects` (the open mechanic space).
    const primitiveEffects = [];
    // why: WP-257 / D-24034 — raw `[keyword:X]` tokens that resolve to no keyword,
    // composition, or recognized modifier. The hollow detector reads this so an
    // unresolved marker flags `parse-unrecognized` while flavor text (no marker) does not.
    const unresolvedMarkers = [];
    // why: D-24045 — the positive symmetric record of `unresolvedMarkers`: composition
    // markers that RESOLVED on this line (a primitive attached). Pushed in the SAME two
    // branches that push `primitiveEffects`; the mechanic ledger reads it to classify
    // composition-marker status by-hook (per-card), not by-name (resolves the D-24044
    // over-claim). Parse-time provenance only — never affects execution.
    const resolvedMarkers = [];
    // Step 1a: Extract [hc:X] condition markup
    // why: defense-in-depth normalization on already-validated hc values — pipeline
    // produces lowercase, but a single authoring slip like [hc:Tech] should not
    // silently break superpowers.
    let heroClassMatch = null;
    const heroClassRegex = new RegExp(HERO_CLASS_PATTERN.source, 'g');
    // why: WP-723 / D-24544 — on an X-Gene line only the LEADING [hc:X] is the discard-
    // condition class; this guard ensures exactly one heroClassInDiscardPile condition is
    // emitted even if a line ever carried a second [hc:X] (allowlisted cards carry one).
    let xGeneConditionPushed = false;
    heroClassMatch = heroClassRegex.exec(abilityText);
    while (heroClassMatch !== null) {
        const normalizedClass = normalizeTraitSlug(heroClassMatch[1]);
        if (lineHasSizeChanging) {
            // why: D-24801 — on a Size-Changing line the [hc:...] tokens are the classes that
            // discount the recruit cost, so route them to the Size-Changing list and emit NO
            // heroClassMatch condition for them (extract ALL on this line — guardrail #4). The
            // [keyword:Size-Changing] token itself is a recognized HeroKeyword (Step 2), so no
            // unresolved marker is recorded either.
            sizeChangingClasses.push(normalizedClass);
        }
        else if (lineHasMicroscopicSizeChanging) {
            // why: D-24801 — on a Microscopic Size-Changing line each [hc:...] icon is one 2-less
            // step (repeats kept), never a play gate; the keyword is recorded up front.
            microscopicSizeChangingClasses.push(normalizedClass);
        }
        else if (lineHasCopyPowers) {
            // why: WP-535 fix-forward — on a copy-powers line the [hc:X] is the DESCRIPTIVE class
            // ("This card is both [hc:covert] and the color you copy"), NOT a play gate. Covert is
            // already the card's printed heroClass (cardTraits), and the copied class is granted at
            // runtime by heroEffectCopyPowers → cardCopiedClasses; emit NO heroClassMatch
            // condition so the copy always fires (mirrors the size-changing exclusion above). Not
            // pushed to the grant list either — covert is already printed, so a grant is redundant.
        }
        else if (lineHasResolvedInvestigate) {
            // why: WP-564 / D-24373 — on a RESOLVED investigate line the [hc:X] is the CRITERION
            // ("Investigate for a [hc:tech] card" / "…that's [hc:ranged] and/or [hc:instinct]"),
            // NOT a play gate. The criterion is already captured in investigateCriteria; emit NO
            // heroClassMatch condition (mirrors the size-changing / copy-powers exclusions above).
        }
        else if (lineHasRevealFromHand) {
            // why: WP-659 / D-24470 — on a reveal-from-hand line the [hc:X] is the reveal CRITERION
            // ("reveal another [hc:X] Hero"), NOT a play gate. Already captured in
            // revealFromHandCriterion; emit NO heroClassMatch condition (mirrors the investigate
            // exclusion above — this is the mid-sentence-token-is-not-a-gate fix for Psychic Link).
        }
        else if (lineHasRevealTraitCriterion) {
            // why: WP-729 / D-24550 — on a reveal-rule trait-predicate line the [hc:X] is the reveal
            // CRITERION ("reveal the top card; if it's an [hc:X] Hero, draw it"), captured by the
            // [keyword:reveal:hc-X:draw] marker — NOT a heroClassMatch play-gate. Emit NO
            // heroClassMatch condition (Crescent Moon Darts / Balanced Attack; mirrors the
            // reveal-from-hand exclusion above).
        }
        else if (lineHasXGene) {
            // why: WP-723 / D-24544 — on an X-Gene line the co-located [hc:X] is the discard-
            // condition CLASS ("a [class] card in your discard pile"), NOT a heroClassMatch
            // play-this-turn gate. Drop the heroClassMatch it would otherwise emit and push a
            // heroClassInDiscardPile condition carrying the class read from the token (never a
            // hardcoded 'instinct'), so the trailing effect gates on discard-pile presence. Only
            // the leading token drives the condition (xGeneConditionPushed) — mirrors the reveal-
            // from-hand / D-24470 co-located-token reinterpretation.
            if (!xGeneConditionPushed) {
                heroClassConditions.push({
                    type: 'heroClassInDiscardPile',
                    value: normalizedClass,
                });
                xGeneConditionPushed = true;
            }
        }
        else {
            heroClassConditions.push({
                type: 'heroClassMatch',
                value: normalizedClass,
            });
        }
        heroClassMatch = heroClassRegex.exec(abilityText);
    }
    // Step 1b: Extract [team:X] condition markup
    const teamRegex = new RegExp(TEAM_PATTERN.source, 'g');
    let teamMatch = teamRegex.exec(abilityText);
    while (teamMatch !== null) {
        // why: WP-564 / D-24373 — on a RESOLVED investigate line the [team:X] is the CRITERION
        // ("…that's [hc:strength] and/or [team:x-factor-investigations]"), not a requiresTeam
        // gate; it is already captured in investigateCriteria, so emit no condition for it.
        // why: WP-659 / D-24470 — likewise on a reveal-from-hand line the [team:X] is the reveal
        // CRITERION ("reveal another [team:x-men] Hero"), already captured in
        // revealFromHandCriterion — so emit NO requiresTeam gate. This is the Psychic Link fix:
        // the mid-sentence [team:x-men] was wrongly gating the card on "another X-Men played".
        // why: D-24530 — likewise on a Pure Fury line the [team:shield] describes the KO-pile
        // Heroes to count, not a play-gate — so emit NO requiresTeam gate (see lineHasPureFury).
        // why: WP-729 / D-24550 — likewise on a reveal-rule trait-predicate line the [team:X] is
        // the reveal CRITERION ("if it's an [team:x-men] Hero, draw it"), captured by the
        // [keyword:reveal:team-X:draw] marker — so emit NO requiresTeam gate (Card Shark / HYDRA
        // Half-Wit; the live "needs another x-men Hero played this turn" mis-gate this removes).
        // why: D-24628 — likewise a [team:X] in the body after an explicit leading gate prefix is
        // descriptive (see leadingGatePrefixLength), so emit NO requiresTeam gate for it.
        const isBodyTokenAfterGatePrefix = leadingGatePrefixLength > 0 && teamMatch.index >= leadingGatePrefixLength;
        if (!lineHasResolvedInvestigate && !lineHasRevealFromHand && !lineHasOptionalKoShieldOfficer && !lineHasKoUpToShield && !lineHasPureFury && !lineHasRevealTraitCriterion && !isBodyTokenAfterGatePrefix) {
            teamConditions.push({
                type: 'requiresTeam',
                value: normalizeTraitSlug(teamMatch[1]),
            });
        }
        teamMatch = teamRegex.exec(abilityText);
    }
    // Condition emission order: all heroClassMatch first, then requiresTeam
    // (deterministic, independent of markup position in text).
    // why: D-24563 — a repeated icon in the leading gate prefix ("[hc:ranged][hc:ranged]:") becomes
    // ONE condition carrying requiredMatches. The collapse runs here, before the Empowered param
    // splices below, so a splice can never remove a collapsed prefix gate.
    const conditions = collapseLeadingRepeatedIconConditions(leadingGatePrefixMatch?.[0] ?? '', [...heroClassConditions, ...teamConditions]);
    applyAnotherTeamHeroCostFloor(abilityText, conditions);
    // Pre-pass: resolve the choose-one Empowered form before the KEYWORD_PATTERN loop.
    // why: D-24063 — the choose-one line must produce ONE composition for the whole line;
    // running a pre-pass before the per-token loop prevents double-composition (once per
    // [keyword:Empowered] token). processedAsChooseOne suppresses the per-token empowered
    // dispatch for this line when the pre-pass already resolved it.
    let processedAsChooseOne = false;
    const chooseOneResult = tryResolveEmpoweredChooseOneLine(abilityText);
    if (chooseOneResult !== undefined) {
        primitiveEffects.push(chooseOneResult.composition);
        // why: D-24045 — record the resolved composition marker by-hook (same gate as all
        // other primitiveEffects pushes); 'empowered' is the normalized marker name.
        resolvedMarkers.push('empowered');
        // why: D-24063 — suppresses per-token empowered handling in the KEYWORD_PATTERN loop
        // below so the choose-one composition is built exactly once for the whole line.
        processedAsChooseOne = true;
        // The [hc:X] tokens in the choose-one form are count parameters, not gate conditions
        // (same suppression as the sole-condition core path). Clear so 'conditional' is not added.
        conditions.splice(0, conditions.length);
    }
    // Pre-pass: resolve the draw-or-empowered choose-one form before the KEYWORD_PATTERN loop.
    // why: D-24069 — One-Hit Wonder's "Choose one: Draw a card, or you get [keyword:Empowered] by
    // [hc:X]" must emit ONE draw-or-empowered effect carrying the empowered class and suppress the
    // per-token empowered dispatch, so the line no longer falls through to tryResolveEmpoweredCore
    // (the silent-empowered bug). Runs AFTER the two-empowered choose-one pre-pass (which already
    // returned undefined for this single-marker shape) and BEFORE the per-token loop.
    let processedAsDrawOrEmpowered = false;
    let drawOrEmpoweredClass;
    if (!processedAsChooseOne) {
        const drawOrEmpoweredResult = tryResolveDrawOrEmpoweredLine(abilityText);
        if (drawOrEmpoweredResult !== undefined) {
            keywords.push('draw-or-empowered');
            drawOrEmpoweredClass = drawOrEmpoweredResult.empoweredClass;
            // why: D-24069 — suppresses the per-token empowered dispatch in the KEYWORD_PATTERN loop
            // below so the line emits exactly one draw-or-empowered effect (mirrors processedAsChooseOne).
            processedAsDrawOrEmpowered = true;
            // why: the [hc:X] is the Empowered count PARAMETER, not a gate condition; clear it so the
            // hook adds no 'conditional' keyword (same suppression as the choose-one path above).
            conditions.splice(0, conditions.length);
        }
    }
    // Pre-pass: resolve the count-scaled choose-one form before the KEYWORD_PATTERN loop and
    // before Steps 2d / 2d' (the per-count marker extractors).
    // why: WP-675 / D-24490 — vnom's "Choose one: +N recruit per other recruit-icon card / Or
    // +N attack per other attack-icon card" is coalesced (buildHeroAbilityHooks) into one line
    // carrying BOTH per-count markers. This pre-pass folds the two markers into ONE
    // count-scaled-choose effect (two options) so the two grants become a CHOICE, not two
    // independent grants, and sets a flag that suppresses the per-count keyword extraction and
    // the printed grant/criterion icons on the line.
    let processedAsCountScaledChoose = false;
    let countScaledChoiceOptions;
    if (!processedAsChooseOne && !processedAsDrawOrEmpowered) {
        const countScaledChoiceResult = tryResolveCountScaledChooseOneLine(abilityText);
        if (countScaledChoiceResult !== undefined) {
            keywords.push('count-scaled-choose');
            countScaledChoiceOptions = countScaledChoiceResult.options;
            processedAsCountScaledChoose = true;
            // why: the per-count "with a [icon] icon" criteria are count PARAMETERS, not play-gates;
            // clear conditions so the hook adds no 'conditional' keyword (mirrors the pre-passes above).
            conditions.splice(0, conditions.length);
        }
    }
    // Pre-pass: capture a put-any-number-bottom-hq line's trailing Empowered grant before the
    // KEYWORD_PATTERN loop (D-24132). When the line carries the put-any-number marker AND a
    // same-line "[keyword:Empowered] by [hc:…]" tail (Wonder Man's 8th Wonder of the World), the
    // Empowered classes are the count PARAMETERS of a grant that must apply AFTER the moves
    // resolve — so capture them onto the put-any-number effect and suppress the per-token
    // Empowered dispatch (which would otherwise emit a standalone play-time primitive that fires
    // BEFORE the choice resolves, counting the pre-move HQ). Runs AFTER the choose-one / draw-or-
    // empowered pre-passes (which returned undefined for this shape) and BEFORE the per-token loop.
    let processedAsPutAnyNumberEmpowered = false;
    let putAnyNumberEmpoweredClasses = [];
    if (!processedAsChooseOne
        && !processedAsDrawOrEmpowered
        && PUT_ANY_NUMBER_BOTTOM_HQ_PATTERN.test(abilityText)) {
        // why: PUT_ANY_NUMBER_BOTTOM_HQ_PATTERN is global; .test advances lastIndex on the shared
        // const, so reset it after the presence check to keep the module-level const stateless.
        PUT_ANY_NUMBER_BOTTOM_HQ_PATTERN.lastIndex = 0;
        const tailClasses = extractPutAnyNumberEmpoweredTailClasses(abilityText);
        if (tailClasses.length > 0) {
            putAnyNumberEmpoweredClasses = tailClasses;
            processedAsPutAnyNumberEmpowered = true;
            // why: the Empowered tail's [hc:…] tokens parsed as heroClassMatch conditions in Step 1a
            // are the grant's count PARAMETERS, not gate conditions — remove the matching ones so they
            // neither gate the put-any-number effect nor add a 'conditional' keyword. Removes ONLY the
            // consumed params (not a blanket clear) so any unrelated gate on the line survives.
            for (const empoweredClass of putAnyNumberEmpoweredClasses) {
                const paramIndex = findFirstHeroClassMatchIndex(conditions, empoweredClass);
                if (paramIndex !== -1) {
                    conditions.splice(paramIndex, 1);
                }
            }
        }
    }
    // Step 2: Extract [keyword:X] or [keyword:X:N] markup
    // Collect magnitudes keyed by keyword — explicit markup wins over icon-derived.
    const magnitudes = new Map();
    const keywordRegex = new RegExp(KEYWORD_PATTERN.source, 'g');
    let keywordMatch = keywordRegex.exec(abilityText);
    while (keywordMatch !== null) {
        const normalizedKeyword = keywordMatch[1].toLowerCase();
        if (normalizedKeyword === 'investigate') {
            // why: WP-564 / D-24373 — investigate IS a HeroKeyword, but its keyword + descriptor
            // are recorded ONLY when a static criterion resolves. A deferred form (choose-first,
            // other-zone, name-match, disposition, modifier, compose-with-Teleport) records
            // `investigate` as an unresolved marker so the line stays hollow (parse-unrecognized),
            // exactly as before this WP — the Honest-Partial Invariant (mirrors the Empowered
            // resolver). Checked BEFORE isValidHeroKeyword so the generic push does not fire for it.
            if (investigateCriteria !== undefined) {
                keywords.push('investigate');
            }
            else {
                unresolvedMarkers.push('investigate');
            }
        }
        else if (normalizedKeyword === 'reveal-from-hand') {
            // why: WP-659 / D-24470 — reveal-from-hand IS a HeroKeyword, but its keyword +
            // effect are recorded ONLY when the co-located [team:X]/[hc:X] criterion resolves.
            // A marker with no class/team token records `reveal-from-hand` as an unresolved
            // marker so the line stays an honest hollow (parse-unrecognized) — the Honest-Partial
            // Invariant (mirrors the investigate / transform resolvers above). Checked BEFORE
            // isValidHeroKeyword so the generic push does not fire for it.
            if (revealFromHandCriterion !== undefined) {
                keywords.push('reveal-from-hand');
            }
            else {
                unresolvedMarkers.push('reveal-from-hand');
            }
        }
        else if (normalizedKeyword === 'reveal-or-gain-wound-each') {
            // why: WP-843 / D-24777 — reveal-or-gain-wound-each IS a HeroKeyword, but its keyword +
            // effect are recorded ONLY when the co-located [hc:X]/[team:X] criterion resolves (the
            // shared reveal-from-hand resolver). A marker with no class/team token records the
            // keyword as an unresolved marker so the line stays an honest hollow — the Honest-Partial
            // Invariant (mirrors the reveal-from-hand branch above). Checked BEFORE isValidHeroKeyword
            // so the generic push does not fire for it.
            if (revealFromHandCriterion !== undefined) {
                keywords.push('reveal-or-gain-wound-each');
            }
            else {
                unresolvedMarkers.push('reveal-or-gain-wound-each');
            }
        }
        else if (normalizedKeyword === 'transform') {
            // why: WP-658 / D-24469 — transform IS a HeroKeyword, but its keyword +
            // effect are recorded ONLY for a card whose transform is fully modeled (the
            // SUPPORTED_TRANSFORM_BASES allowlist the caller resolved into
            // transformSupported). Every other wwhk transform card carries an UNMODELED
            // printed condition, so resolving its [keyword:Transform] would fire an
            // UNCONDITIONAL (unfaithful) swap; those record `transform` as an unresolved
            // marker so the line stays an honest hollow (parse-unrecognized), exactly as
            // before this WP — the Honest-Partial Invariant (mirrors the investigate
            // resolver above). Checked BEFORE isValidHeroKeyword so the generic push
            // does not fire for a held-back card.
            if (transformSupported) {
                keywords.push('transform');
            }
            else {
                unresolvedMarkers.push('transform');
            }
        }
        else if (normalizedKeyword === 'feast') {
            // why: WP-857 / D-24793 — feast IS a HeroKeyword, but its keyword + effect are recorded
            // ONLY for a FEAST_CARDS card (the allowlist the caller resolved into feastSupported).
            // World-Spanning Hunger ("Feast up to three times", +2 per non-grey Hero) and The Void
            // Unchained (cost-0 check, Transform branch) carry other text the engine does not model,
            // so their Feast stays an unresolved marker — the Honest-Partial Invariant (mirrors the
            // transform arm above). Checked BEFORE isValidHeroKeyword so the generic push does not
            // fire for a held-back card. Like transform, the keyword falls through to its plain effect.
            if (feastSupported) {
                keywords.push('feast');
            }
            else {
                unresolvedMarkers.push('feast');
            }
        }
        else if ((normalizedKeyword === 'undercover-hand-shield-hero' ||
            normalizedKeyword === 'undercover-officer-stack') &&
            processedAsCountScaledChoose) {
            // why: WP-679 / D-24495 — this Undercover source-shape marker is being consumed as a
            // choose-one option (the parked count-scaled-choose dispatches it at resolve time). Do
            // NOT also push it as a standalone onPlay keyword, or the send would fire twice — once
            // unconditionally at play time and once via the resolved choice. (Intentional no push.)
        }
        else if (normalizedKeyword === 'gain-officer') {
            // why: D-24696 — [keyword:gain-officer] is a choose-one OPTION marker, never a HeroKeyword
            // (ca75 Spy Network "Choose one: Gain a S.H.I.E.L.D. Officer, or you get +2[icon:attack]."):
            // the parked count-scaled-choose dispatches it at resolve time, so on a choose-one line it
            // is consumed (no push). Off a choose-one line it has nothing to run, so it stays an
            // unresolved marker (honest parse-unrecognized hollow) — the Honest-Partial Invariant.
            if (!processedAsCountScaledChoose) {
                unresolvedMarkers.push(normalizedKeyword);
            }
        }
        else if (isValidHeroKeyword(normalizedKeyword)) {
            keywords.push(normalizedKeyword);
            // Capture optional :N magnitude suffix when present and valid integer
            const magnitudeString = keywordMatch[2];
            if (magnitudeString !== undefined && /^\d+$/.test(magnitudeString)) {
                magnitudes.set(normalizedKeyword, parseInt(magnitudeString, 10));
            }
        }
        else if (isParameterizedCompositionMarker(normalizedKeyword)) {
            // why: D-24044 — a PARAMETERIZED composition marker (empowered) whose AST is BUILT per
            // a parameter parsed from the text immediately after the marker, not a static
            // HERO_COMPOSITION_MARKERS row. It resolves to a built composition ONLY for the
            // unconditional core form (an anchored `by [hc:COLOR]` tail whose color is the line's
            // sole condition); any deferred variant (no anchored tail, a prefix gate, multi-class,
            // a team gate) instead records an unresolved marker so the WP-257 hollow detector still
            // flags it — the Honest-Partial Invariant. Checked BEFORE isHeroCompositionMarker since
            // `empowered` is in HERO_COMPOSITION_MARKER_NAMES (the deduped union) too.
            //
            // why: D-24063 / D-24069 / D-24132 — processedAsChooseOne (the two-empowered choose-one),
            // processedAsDrawOrEmpowered (draw + single empowered), and processedAsPutAnyNumberEmpowered
            // (put-any-number + trailing empowered) each suppress per-token empowered handling when their
            // whole-line pre-pass already claimed the line. Skipping here prevents double-composition / a
            // stray standalone play-time empowered composition on a line a pre-pass owns (for put-any-
            // number, the grant is applied AFTER the moves at resolve time instead).
            if (!processedAsChooseOne && !processedAsDrawOrEmpowered && !processedAsPutAnyNumberEmpowered) {
                const textAfterMarker = abilityText.slice(keywordMatch.index + keywordMatch[0].length);
                // why: D-24047 — resolve-order: try the unchanged sole-condition core FIRST, then the
                // conditional-prefix class-gated form, then the free-choice fallback, then the
                // unresolved fallback. This keeps the core path and its two baseline-verified cases
                // untouched: `one-hit-wonder` (single marker + single condition, no prefix) still
                // resolves via the core path.
                const empoweredComposition = tryResolveEmpoweredCore(textAfterMarker, conditions);
                if (empoweredComposition !== undefined) {
                    primitiveEffects.push(empoweredComposition);
                    // why: D-24045 — record the resolved composition marker (same gate as the
                    // primitiveEffects push). A deferred variant takes the else branch and records an
                    // unresolved marker instead — the Honest-Partial symmetry the ledger reads by-hook.
                    resolvedMarkers.push(normalizedKeyword);
                    // why: D-24044 — suppress the consumed [hc:COLOR] param so it does not ALSO gate the
                    // hook (it is the count parameter, not a condition). The resolve gate guarantees it is
                    // the line's sole condition, so clearing `conditions` removes exactly it — which also
                    // prevents the 'conditional' keyword being added downstream.
                    conditions.splice(0, conditions.length);
                }
                else {
                    const conditionalPrefixMatch = tryResolveEmpoweredConditionalPrefix(abilityText, textAfterMarker, conditions);
                    if (conditionalPrefixMatch !== undefined) {
                        primitiveEffects.push(conditionalPrefixMatch.composition);
                        // why: D-24045 — same by-hook provenance gate as the core path.
                        resolvedMarkers.push(normalizedKeyword);
                        // why: D-24047 — suppress ONLY the consumed count param heroClassMatch(Y) and
                        // RETAIN the leading prefix gate heroClassMatch(X). The retained gate IS the
                        // conditional behavior the WP-256 executor honors (it runs primitiveEffects only
                        // when the hook's conditions pass), so this lifts D-24044's conditional-prefix
                        // deferral for the class-gated case WITHOUT an executor edit. NEVER clear all
                        // conditions on this path (that is the sole-condition core-path shortcut). The
                        // helper confirmed a matching count param exists, so the index is always found.
                        const consumedParamIndex = findFirstHeroClassMatchIndex(conditions, conditionalPrefixMatch.countColor);
                        if (consumedParamIndex !== -1) {
                            conditions.splice(consumedParamIndex, 1);
                        }
                    }
                    else {
                        // why: WP-310 / D-24098 — the unconditional MULTI-class form
                        // `by [hc:X] and [hc:Y] (and …)`. Tried AFTER the single-class core +
                        // conditional-prefix paths (which both defer a multi-class tail) and BEFORE the
                        // free-choice/dynamic fallbacks, so no existing form's routing changes.
                        const multiClassCompositions = tryResolveEmpoweredMultiClass(textAfterMarker, conditions);
                        if (multiClassCompositions !== undefined) {
                            // why: WP-310 — push one composition per parsed class in printed order; the
                            // executor runs each (every entry grants +Attack by that class's HQ count), so
                            // the applied bonus is their sum.
                            for (const composition of multiClassCompositions) {
                                primitiveEffects.push(composition);
                            }
                            // why: D-24045 — same by-hook provenance gate as all other resolution paths.
                            resolvedMarkers.push(normalizedKeyword);
                            // why: WP-310 — the parsed classes are the line's SOLE conditions (the resolve
                            // gate guaranteed conditions === the N count params), so clear them all —
                            // mirroring the core path's sole-condition shortcut. This prevents the consumed
                            // count params gating the hook or adding a stray 'conditional' keyword downstream.
                            conditions.splice(0, conditions.length);
                        }
                        else {
                            const freeChoiceComposition = tryResolveEmpoweredFreeChoice(textAfterMarker, conditions);
                            if (freeChoiceComposition !== undefined) {
                                primitiveEffects.push(freeChoiceComposition);
                                // why: D-24045 — same by-hook provenance gate as all other resolution paths.
                                resolvedMarkers.push(normalizedKeyword);
                            }
                            else {
                                // why: D-24065 — dynamic fallback: recognizes "by the Hero Classes of the card
                                // you revealed this way" (cross-the-multiverse); last fallback before the
                                // unresolved-marker path.
                                const dynamicComposition = tryResolveEmpoweredDynamic(textAfterMarker);
                                if (dynamicComposition !== undefined) {
                                    primitiveEffects.push(dynamicComposition);
                                    // why: D-24045 — same by-hook provenance gate as all other resolution paths.
                                    resolvedMarkers.push(normalizedKeyword);
                                }
                                else {
                                    unresolvedMarkers.push(normalizedKeyword);
                                }
                            }
                        }
                    }
                }
            }
        }
        else if (isHeroCompositionMarker(normalizedKeyword)) {
            // why: D-24031 — a composition marker (berserk) attaches a DEEP COPY of its AST to
            // primitiveEffects, NEVER to hook.keywords (berserk is not a HeroKeyword;
            // isValidHeroKeyword stays false). structuredClone (a Node v22 global, deterministic
            // over the plain-JSON AST) forecloses both aliasing the shared registry const
            // (D-13502) and hand-literal drift. A cousin is a registry row, not an engine edit.
            const composition = HERO_COMPOSITION_MARKERS[normalizedKeyword];
            if (composition !== undefined) {
                primitiveEffects.push(structuredClone(composition));
                // why: D-24045 — record the resolved composition marker (same gate as the
                // primitiveEffects push). A static composition (berserk) always resolves here, so it
                // stays executable in the ledger; only deferred parameterized variants go unsupported.
                resolvedMarkers.push(normalizedKeyword);
            }
        }
        else if (normalizedKeyword === 'spectrum') {
            // why: D-24055 — [keyword:Spectrum] is the rulebook gate, modeled as a
            // condition (not a keyword), so the line's printed effects gate on ≥3 classes.
            // Placed before the unresolved-marker fallback so it never flags.
            conditions.push({
                type: 'distinctHeroClassesAtLeast',
                value: String(SPECTRUM_CLASS_THRESHOLD),
            });
        }
        else if (normalizedKeyword === 'sunlight' || normalizedKeyword === 'moonlight') {
            // why: WP-765 / D-24598 — [keyword:Sunlight] / [keyword:Moonlight] is the rulebook gate
            // (rules v23 ~L1696-1731), modeled as a game-state CONDITION on the D-24055 Spectrum
            // marker→condition precedent, so the line's printed effects fire only while that state is
            // in effect (computeDayNight, read as the hook resolves). Placed before the
            // recruit-threshold arm and the unresolved-marker fallback so it never flags hollow.
            if (normalizedKeyword === 'sunlight') {
                conditions.push({ type: 'sunlightInEffect', value: '' });
            }
            else {
                conditions.push({ type: 'moonlightInEffect', value: '' });
            }
            // why: WP-765 / D-24598 — an unmodeled day/night line keeps its gate AND records the token
            // as an unresolved marker, so it stays a visible hollow when its state is in effect (its
            // parsed grant is dropped below — see DAY_NIGHT_UNMODELED_LINES).
            if (dayNightUnmodeled) {
                unresolvedMarkers.push(normalizedKeyword);
            }
        }
        else if (normalizedKeyword === 'recruit-threshold') {
            // why: WP-545 / D-24354 — mirrors the D-24055 Spectrum marker→condition pattern:
            // a [keyword:…] marker that pushes a game-state CONDITION (not a keyword/effect)
            // onto the same hook that carries the line's printed effects, so Surge of Power's
            // +3[icon:attack] gates on "you made N or more Recruit this turn". The parameterized
            // threshold is KEYWORD_PATTERN's optional :N capture (keywordMatch[2]). Placed before
            // the unresolved-marker fallback so it never records a parse-unrecognized hollow.
            const recruitThreshold = keywordMatch[2];
            if (recruitThreshold !== undefined) {
                conditions.push({
                    type: 'recruitMadeThisTurnAtLeast',
                    value: recruitThreshold,
                });
            }
        }
        else if (normalizedKeyword === 'draw-threshold') {
            // why: WP-665 / D-24476 — mirrors the recruit-threshold marker→condition arm:
            // a [keyword:draw-threshold:N] marker pushes a cardsDrawnThisTurnAtLeast:N
            // game-state condition onto this line's hook, so Gamma-Draining Nanites'
            // Transform (abilities[1]) gates on "you drew N or more cards this turn". The draw
            // itself lives on abilities[0] (a separate hook) and stays unconditional. The
            // threshold is KEYWORD_PATTERN's optional :N capture. Placed before the
            // unresolved-marker fallback so it never records a parse-unrecognized hollow.
            const drawThreshold = keywordMatch[2];
            if (drawThreshold !== undefined) {
                conditions.push({
                    type: 'cardsDrawnThisTurnAtLeast',
                    value: drawThreshold,
                });
            }
        }
        else if (normalizedKeyword === 'discard-threshold') {
            // why: WP-777 / D-24616 — mirrors the draw-threshold marker→condition arm: a
            // [keyword:discard-threshold:N] marker pushes a cardsDiscardedThisTurnAtLeast:N
            // condition onto this line's hook, so New Wings' +4 / Pumpkin Bombs' +2 attack gates on
            // "If you discarded any cards this turn" instead of firing on every play (the phantom
            // grant in match 19720cb4). The literal matches CARDS_DISCARDED_THIS_TURN_CONDITION_TYPE
            // (heroConditions.evaluate.ts). Placed before the unresolved-marker fallback so it never
            // records a parse-unrecognized hollow.
            const discardThreshold = keywordMatch[2];
            if (discardThreshold !== undefined) {
                conditions.push({
                    type: 'cardsDiscardedThisTurnAtLeast',
                    value: discardThreshold,
                });
            }
        }
        else if (normalizedKeyword === 'outwit') {
            // why: WP-653 / D-24464 — Outwit is a game-state CONDITION (the D-24055
            // Spectrum marker→condition pattern), gating the line's printed effects on
            // >= 3 distinct non-zero Hero costs. Placed before the unresolved-marker
            // fallback so it never records a parse-unrecognized hollow.
            conditions.push({ type: 'distinctHeroCostsAtLeast', value: '3' });
        }
        else if (normalizedKeyword === 'worthy') {
            // why: WP-653 / D-24464 — Worthy gates on a Hero costing >= 5 in hand or
            // play (the Spectrum/recruit-threshold marker→condition precedent).
            // why: WP-673 / D-24488 — but on a worthy count-scaled line the Worthy token
            // is the COUNT CRITERION ("each other card … that makes you Worthy"), not a
            // play gate, so emit NO condition for it (the count source does the counting).
            if (!lineHasWorthyCountScaledAttack) {
                conditions.push({ type: 'heroCostAtLeastInHandOrPlay', value: '5' });
            }
        }
        else if (normalizedKeyword === 'savior') {
            // why: WP-653 / D-24464 — Savior gates on >= 3 Bystanders in the Victory
            // Pile (the Spectrum/recruit-threshold marker→condition precedent).
            conditions.push({ type: 'bystandersInVictoryAtLeast', value: '3' });
        }
        else if (normalizedKeyword === 'antics') {
            // why: WP-653 / D-24464 — Antics gates on >= 3 cards costing 1-2 and/or
            // Size-Changing (the Spectrum/recruit-threshold marker→condition precedent).
            conditions.push({ type: 'cheapOrSizeChangingAtLeast', value: '3' });
        }
        else if (normalizedKeyword === 'defeated-villain-or-mastermind') {
            // why: WP-656 / D-24467 — Diamond Form's "Whenever you defeat a Villain or
            // Mastermind this turn, you get +3 Recruit." The marker→condition precedent
            // (Spectrum / recruit-threshold / Outwit): push the wait-and-see gate
            // `defeatedVillainOrMastermindThisTurn` onto the same hook as the line's printed
            // +3[icon:recruit], so the recruit fires once per qualifying defeat this turn (not
            // on play). A boolean gate — `value` is unused by the evaluator; '1' is a stable
            // placeholder. Placed before the unresolved-marker fallback so it never flags hollow.
            conditions.push({ type: 'defeatedVillainOrMastermindThisTurn', value: '1' });
        }
        else if (normalizedKeyword === 'owned-card-koed') {
            // why: WP-853 / D-24789 — Absorb Energies' "Whenever a card you own is KO'd this
            // turn, you get +2 Recruit." The Diamond Form marker→condition precedent (D-24467):
            // push the wait-and-see gate onto the same hook as the line's printed +2[icon:recruit],
            // so the recruit fires once per KO of the player's own card (not on play). A per-KO
            // gate — `value` is unused; '1' is a stable placeholder. Placed before the
            // unresolved-marker fallback so it never flags hollow.
            conditions.push({ type: OWNED_CARD_KOED_CONDITION_TYPE, value: '1' });
        }
        else if (normalizedKeyword === 'owned-hero-koed') {
            // why: WP-856 / D-24792 — Possessed by Brother's Spirit's "The first time that one of
            // your Heroes or a Hero from your deck or discard pile is KO'd this turn, you get …".
            // Push the one-shot wait-and-see gate onto the same hook as the line's choose-one
            // reward. This arm runs in the per-token loop, AFTER the count-scaled choose-one
            // pre-pass (whose conditions.splice would otherwise drop it). `value` is unused; '1'
            // is a stable placeholder. Placed before the unresolved-marker fallback.
            conditions.push({ type: OWNED_HERO_KOED_CONDITION_TYPE, value: '1' });
        }
        else if (normalizedKeyword === 'deck-card-koed') {
            // why: WP-855 / D-24791 — Endless Appetite's "Whenever a card is KO'd from your deck this
            // turn, you may draw a card." The owned-card-koed precedent (D-24789): push the per-KO
            // wait-and-see gate onto the same hook as the line's [keyword:draw:1], so the draw fires
            // once per KO from the player's deck (not on play; "you may" is auto-applied, pure
            // upside). `value` is unused; '1' is a stable placeholder. Placed before the
            // unresolved-marker fallback so it never flags hollow.
            conditions.push({ type: DECK_CARD_KOED_CONDITION_TYPE, value: '1' });
        }
        else if (normalizedKeyword === 'deck-koed-attack-icon') {
            // why: WP-859 / D-24795 — Metamorphosis' "Then, if a card with an [icon:attack] icon was KO'd
            // from your deck this turn, [keyword:Transform] …". Push the one-shot wait-and-see gate onto
            // the Transform half (FEAST_THEN_TRANSFORM_CARDS split the line), and flag the line so its
            // descriptive attack icon is dropped below. `value` is unused; '1' is a stable placeholder.
            conditions.push({ type: ATTACK_ICON_DECK_KO_CONDITION_TYPE, value: '1' });
            lineReadsAttackIconDeckKo = true;
        }
        else if (normalizedKeyword === 'deck-koed-non-grey-hero') {
            // why: WP-859 / D-24795 — Bursting with Life's "Then, if a non-grey Hero was KO'd from your
            // deck this turn, [keyword:Transform] …". The same shape as the arm above; no icon to drop.
            conditions.push({ type: NON_GREY_HERO_DECK_KO_CONDITION_TYPE, value: '1' });
        }
        else if (normalizedKeyword === 'master-strike-this-turn') {
            // why: WP-743 / D-24566 — Grief's "If a Master Strike was completed this turn, you
            // get +2 Recruit." The marker→condition precedent (D-24467): push the whole-turn
            // wait-and-see gate onto the same hook as the line's printed +2[icon:recruit], so the
            // recruit fires only once a Master Strike has been played this turn. A boolean gate;
            // '1' is a stable placeholder. Placed before the unresolved-marker fallback.
            conditions.push({ type: MASTER_STRIKE_THIS_TURN_CONDITION_TYPE, value: '1' });
        }
        else if (normalizedKeyword === 'master-strike-or-ambush-this-turn') {
            // why: WP-743 / D-24566 — Spring the Trap's "If a Master Strike or Villain that has an
            // Ambush ability was played this turn, you get +1 Attack." Same marker→condition
            // precedent (D-24467); the +1[icon:attack] stays on this hook via Step 2b. A boolean
            // gate; '1' is a stable placeholder. Placed before the unresolved-marker fallback.
            conditions.push({ type: MASTER_STRIKE_OR_AMBUSH_THIS_TURN_CONDITION_TYPE, value: '1' });
        }
        else if (normalizedKeyword === 'first-hero-condition') {
            // why: WP-681 / D-24498 — Deadpool's "Hey, Can I Get a Do-Over?" gates its
            // optional discard-and-redraw on "if this is the first Hero you played this turn".
            // The marker→condition precedent (Spectrum / recruit-threshold / Outwit): push the
            // `firstHeroPlayedThisTurn` gate onto the same hook as the line's [keyword:do-over],
            // so the Do-Over choice parks only when no other Hero has been played this turn. A
            // boolean gate — `value` is unused by the evaluator; '1' is a stable placeholder.
            // Placed before the unresolved-marker fallback so it never records a hollow.
            conditions.push({ type: 'firstHeroPlayedThisTurn', value: '1' });
        }
        else if (normalizedKeyword === 'teleport') {
            // why: WP-705 / D-24526 — the bare [keyword:Teleport] display token resolves to
            // the reactive `teleport-on-discard` keyword ONLY for a card whose reactive-on-discard
            // form is modeled (TELEPORT_ON_DISCARD_CARDS → teleportOnDiscardSupported). Every
            // other [keyword:Teleport] card carries the UNMODELED general onPlay-Teleport mechanic,
            // so it records `teleport` as an unresolved marker (honest parse-unrecognized hollow),
            // exactly as before this WP — the Honest-Partial Invariant (mirrors the transform /
            // investigate resolvers). Checked before the generic unresolved-marker fallback.
            if (teleportOnDiscardSupported) {
                keywords.push('teleport-on-discard');
                // why: D-24045 — record the `teleport` token as RESOLVED (its by-hook positive
                // provenance) so the hero-mechanic-ledger classifies guerrilla-warfare's `teleport`
                // mechanic as executable, not an unsupported/parse-unrecognized hollow — symmetric
                // with unresolvedMarkers.push('teleport') on the non-allowlisted branch below.
                resolvedMarkers.push('teleport');
            }
            else {
                unresolvedMarkers.push('teleport');
            }
        }
        else if (normalizedKeyword === 'x-gene') {
            // why: WP-723 / D-24544 — X-Gene is a parser directive, NOT a HeroKeyword (it carries no
            // effect of its own — it only conditions a separately-marked trailing effect). For an
            // allowlisted card (xGeneSupported) the marker is CONSUMED: Step 1a already turned its
            // co-located [hc:X] into the heroClassInDiscardPile condition, so record NOTHING here (no
            // keyword, no unresolved marker) — nothing is left hollow. For any other card it stays an
            // unresolved marker (honest parse-unrecognized hollow) so heir-to-wolverine's unmodeled
            // count-scaled Berserk keeps its truthful signal. Per-card allowlisted (transform /
            // teleport precedent); recognizing it globally via RECOGNIZED_NON_KEYWORD_MARKERS would
            // silence that signal. Checked before the generic unresolved-marker fallback.
            if (!xGeneSupported) {
                unresolvedMarkers.push('x-gene');
            }
        }
        else if (!RECOGNIZED_NON_KEYWORD_MARKERS.has(normalizedKeyword)) {
            // why: WP-257 / D-24034 — a `[keyword:X]` token that is NOT a valid keyword,
            // NOT a composition marker, and NOT a recognized modifier (reveal-count) is a
            // SAW-a-marker-resolved-to-nothing case. Record the raw token so the hollow
            // detector can flag `parse-unrecognized` at runtime — distinct from flavor
            // text, which contains no marker token and so records nothing here.
            unresolvedMarkers.push(normalizedKeyword);
        }
        keywordMatch = keywordRegex.exec(abilityText);
    }
    // why: D-24606 — the ff04 space form "[keyword:Focus 6]" never matches KEYWORD_PATTERN
    // (which needs `:N` or `]` right after the name), so it was silently dropped. Record it as
    // the same `focus` unresolved marker the anni "[keyword:Focus]" form already produces, so the
    // now-inert line stays a detectable parse-unrecognized hollow rather than looking like flavor.
    if (FOCUS_COST_PATTERN.test(abilityText) && !unresolvedMarkers.includes('focus')) {
        unresolvedMarkers.push('focus');
    }
    // why: WP-803 / D-24692 — Man / Woman Out of Time. Runs AFTER the Step 2 loop (not as an early
    // return), so the line's gate conditions are exactly what Step 1 / 1b and the Savior arm built.
    // Pushes the keyword only: the generic Step 4 builder emits the single { type: 'out-of-time' }
    // effect (pushing the effect here too would run the handler twice). Resolving a keyword also
    // skips the D-24623 unmatched-slug fallback, so no unresolved slug is recorded.
    if (OUT_OF_TIME_LINE_PATTERN.test(abilityText) && !keywords.includes('out-of-time')) {
        keywords.push('out-of-time');
    }
    // why: D-24746 — "If you played at least N other cards this turn" gates the line's grant on a
    // playedThisTurn snapshot condition read as the card resolves. The threshold is N + 1: the
    // played card is already in inPlay when executeHeroEffects evaluates its gate, and
    // playedThisTurn counts every played card (itself included), so N OTHER cards is N + 1 played.
    const playedOtherCardsMatch = PLAYED_OTHER_CARDS_THRESHOLD_PATTERN.exec(abilityText);
    if (playedOtherCardsMatch !== null) {
        const otherCardsRequired = parseInt(playedOtherCardsMatch[1], 10);
        conditions.push({ type: 'playedThisTurn', value: String(otherCardsRequired + 1) });
    }
    // Step 2b: Extract icon-adjacent magnitudes for attack/recruit keywords.
    // Only sets magnitude if no explicit [keyword:X:N] markup already provided it.
    // why: WP-790 / D-24652 — remember where the attack icon-magnitude that set the attack
    // magnitude ENDS, so the restriction clause is read only from the text after it.
    let attackIconMagnitudeEnd;
    const iconMagnitudeRegex = new RegExp(ICON_MAGNITUDE_PATTERN.source, 'g');
    let iconMagnitudeMatch = iconMagnitudeRegex.exec(abilityText);
    while (iconMagnitudeMatch !== null) {
        // why: WP-660 / D-24471 — skip an icon-magnitude that is a CONDITION threshold (e.g.
        // the "6" in "made at least 6[icon:recruit]"), so it is not read as a +6 grant.
        if (!overlapsSuppressedRange(iconMagnitudeMatch.index, iconMagnitudeMatch.index + iconMagnitudeMatch[0].length, suppressedIconRanges)) {
            const iconKeyword = iconMagnitudeMatch[2].toLowerCase();
            const iconMagnitudeValue = parseInt(iconMagnitudeMatch[1], 10);
            if (!magnitudes.has(iconKeyword)) {
                magnitudes.set(iconKeyword, iconMagnitudeValue);
                if (iconKeyword === 'attack') {
                    attackIconMagnitudeEnd = iconMagnitudeMatch.index + iconMagnitudeMatch[0].length;
                }
            }
        }
        iconMagnitudeMatch = iconMagnitudeRegex.exec(abilityText);
    }
    // Step 2c: Extract VP-cost threshold for reveal lines.
    // Pattern: "N[icon:vp] or less" — non-global, first match only.
    // Only sets magnitude if no explicit [keyword:reveal:N] markup provided it.
    const vpThresholdMatch = VP_COST_THRESHOLD_PATTERN.exec(abilityText);
    if (vpThresholdMatch !== null && !magnitudes.has('reveal')) {
        const vpThresholdValue = parseInt(vpThresholdMatch[1], 10);
        magnitudes.set('reveal', vpThresholdValue);
    }
    // Step 2d: Extract [keyword:attack-per-count:<source>:<perUnit>] count-scaled
    // markup. The per-unit rate is stored in magnitudes; the count source is
    // stored in countSources so the effect builder can attach it. Only sources in
    // HERO_COUNT_SOURCES are accepted — an unrecognized source is ignored (no
    // 'attack-per-count' effect is emitted, so the icon-suppression below does not
    // fire and the line keeps its printed attack icon).
    // why: D-24016 — count-scaled attack tokens have three segments not matched by
    // KEYWORD_PATTERN; the per-unit rate is the magnitude, the source resolves the count.
    const countSources = new Map();
    // why: WP-740 / D-24562 — the optional "for each N" divisor from the marker's 4th segment.
    // Stays undefined for a 3-segment marker so the effect build omits perEach (absent ≡ 1).
    let attackPerEach;
    const countScaledRegex = new RegExp(COUNT_SCALED_PATTERN.source, 'g');
    let countScaledMatch = countScaledRegex.exec(abilityText);
    while (countScaledMatch !== null) {
        const countSourceCandidate = countScaledMatch[1];
        const perUnitString = countScaledMatch[2];
        const perEachString = countScaledMatch[3];
        // why: WP-675 / D-24490 — on a count-scaled-choose line the per-count markers are the
        // two CHOICE options (folded by the pre-pass), NOT two independent grants; skip the
        // standalone per-count keyword so only the count-scaled-choose effect is emitted.
        if (isValidHeroCountSource(countSourceCandidate) && !processedAsCountScaledChoose) {
            keywords.push('attack-per-count');
            magnitudes.set('attack-per-count', parseInt(perUnitString, 10));
            countSources.set('attack-per-count', countSourceCandidate);
            if (perEachString !== undefined) {
                attackPerEach = parseInt(perEachString, 10);
            }
        }
        countScaledMatch = countScaledRegex.exec(abilityText);
    }
    // Step 2d': Extract [keyword:recruit-per-count:<source>:<perUnit>] count-scaled
    // markup — the recruit sibling of Step 2d. Same contract: the per-unit rate is
    // the magnitude, the source resolves the count, and only sources in
    // HERO_COUNT_SOURCES are accepted. Emits a 'recruit-per-count' effect that
    // grants recruit; the recruit-icon-suppression below then drops the co-located
    // printed [icon:recruit] so it does not double-count.
    // why: WP-674 / D-24489 — a "+N recruit for each X" grant (noir Follow Big Leads).
    const recruitCountScaledRegex = new RegExp(RECRUIT_COUNT_SCALED_PATTERN.source, 'g');
    let recruitCountScaledMatch = recruitCountScaledRegex.exec(abilityText);
    while (recruitCountScaledMatch !== null) {
        const recruitCountSourceCandidate = recruitCountScaledMatch[1];
        const recruitPerUnitString = recruitCountScaledMatch[2];
        // why: WP-675 / D-24490 — same choice-fold suppression as the attack Step 2d above.
        if (isValidHeroCountSource(recruitCountSourceCandidate) && !processedAsCountScaledChoose) {
            keywords.push('recruit-per-count');
            magnitudes.set('recruit-per-count', parseInt(recruitPerUnitString, 10));
            countSources.set('recruit-per-count', recruitCountSourceCandidate);
        }
        recruitCountScaledMatch = recruitCountScaledRegex.exec(abilityText);
    }
    // Step 2d'': Extract [keyword:kidnap-per-count:<source>:<perUnit>] count-scaled
    // markup — the bystander-capture sibling of Step 2d. Same contract: the per-unit
    // rate is the magnitude, the source resolves the count, and only sources in
    // HERO_COUNT_SOURCES are accepted. Emits a 'kidnap-per-count' effect that captures
    // N Bystanders; there is NO printed resource icon on this line, so (unlike the
    // attack/recruit siblings) no co-located icon-suppression is needed.
    // why: WP-714 / D-24537 — Ultron's Genetic Experimentation "Kidnap a Bystander for
    // each other [hc:tech] Ally you played this turn".
    const kidnapCountScaledRegex = new RegExp(KIDNAP_COUNT_SCALED_PATTERN.source, 'g');
    let kidnapCountScaledMatch = kidnapCountScaledRegex.exec(abilityText);
    while (kidnapCountScaledMatch !== null) {
        const kidnapCountSourceCandidate = kidnapCountScaledMatch[1];
        const kidnapPerUnitString = kidnapCountScaledMatch[2];
        // why: WP-675 / D-24490 — same choice-fold suppression as the attack Step 2d above.
        if (isValidHeroCountSource(kidnapCountSourceCandidate) && !processedAsCountScaledChoose) {
            keywords.push('kidnap-per-count');
            magnitudes.set('kidnap-per-count', parseInt(kidnapPerUnitString, 10));
            countSources.set('kidnap-per-count', kidnapCountSourceCandidate);
        }
        kidnapCountScaledMatch = kidnapCountScaledRegex.exec(abilityText);
    }
    // Step 2e: Extract [keyword:optional-ko-reward:<reward>:<n>] markup. The
    // reward is stored in rewardTypes; the reward magnitude is stored in
    // magnitudes so the effect builder can attach both. A descriptor is emitted
    // ONLY when the reward is in the seeded set AND n ≥ 1 — an unseeded reward
    // (e.g. a not-yet-built gain-shard) or a zero magnitude emits no effect, so
    // such a marker can never reach the pending queue.
    // why: D-24019 — the optional-KO-reward token has three segments not matched
    // by KEYWORD_PATTERN; the reward dispatches to the existing executor and the
    // magnitude is the reward magnitude.
    const rewardTypes = new Map();
    const optionalKoRewardRegex = new RegExp(OPTIONAL_KO_REWARD_PATTERN.source, 'g');
    let optionalKoRewardMatch = optionalKoRewardRegex.exec(abilityText);
    while (optionalKoRewardMatch !== null) {
        const rewardCandidate = optionalKoRewardMatch[1];
        const rewardMagnitude = parseInt(optionalKoRewardMatch[2], 10);
        if (isValidHeroKeyword(rewardCandidate)
            && OPTIONAL_KO_REWARD_SEEDED_REWARDS.has(rewardCandidate)
            && rewardMagnitude >= 1) {
            keywords.push('optional-ko-reward');
            magnitudes.set('optional-ko-reward', rewardMagnitude);
            rewardTypes.set('optional-ko-reward', rewardCandidate);
        }
        optionalKoRewardMatch = optionalKoRewardRegex.exec(abilityText);
    }
    // Step 2f: Extract [keyword:ko-wound-reward:<reward>:<n>] markup (WP-382 / D-24183).
    // why: mirrors Step 2e — the ko-wound-reward token has three segments; the reward
    // dispatches to the same reward executor after the Wound is KO'd, and the
    // magnitude is the reward magnitude. A descriptor is emitted ONLY when the reward
    // is seeded AND n >= 1.
    const koWoundRewardRegex = new RegExp(KO_WOUND_REWARD_PATTERN.source, 'g');
    let koWoundRewardMatch = koWoundRewardRegex.exec(abilityText);
    while (koWoundRewardMatch !== null) {
        const rewardCandidate = koWoundRewardMatch[1];
        const rewardMagnitude = parseInt(koWoundRewardMatch[2], 10);
        if (isValidHeroKeyword(rewardCandidate)
            && KO_WOUND_REWARD_SEEDED_REWARDS.has(rewardCandidate)
            && rewardMagnitude >= 1) {
            keywords.push('ko-wound-reward');
            magnitudes.set('ko-wound-reward', rewardMagnitude);
            rewardTypes.set('ko-wound-reward', rewardCandidate);
        }
        koWoundRewardMatch = koWoundRewardRegex.exec(abilityText);
    }
    // Step 2e-bis: Extract [keyword:shuffle-discard-empty-reward:<reward>:<n>]
    // markup (D-24148). Mirrors Step 2e: the reward is stored in rewardTypes and
    // the grant magnitude in magnitudes so the effect builder can attach both. A
    // descriptor is emitted ONLY when the reward is in the seeded set AND n ≥ 1 —
    // an unseeded reward or a zero magnitude emits no effect.
    const shuffleDiscardRewardRegex = new RegExp(SHUFFLE_DISCARD_EMPTY_REWARD_PATTERN.source, 'g');
    let shuffleDiscardRewardMatch = shuffleDiscardRewardRegex.exec(abilityText);
    while (shuffleDiscardRewardMatch !== null) {
        const shuffleRewardCandidate = shuffleDiscardRewardMatch[1];
        const shuffleRewardMagnitude = parseInt(shuffleDiscardRewardMatch[2], 10);
        if (isValidHeroKeyword(shuffleRewardCandidate)
            && SHUFFLE_DISCARD_EMPTY_REWARD_SEEDED_REWARDS.has(shuffleRewardCandidate)
            && shuffleRewardMagnitude >= 1) {
            keywords.push('shuffle-discard-empty-reward');
            magnitudes.set('shuffle-discard-empty-reward', shuffleRewardMagnitude);
            rewardTypes.set('shuffle-discard-empty-reward', shuffleRewardCandidate);
        }
        shuffleDiscardRewardMatch = shuffleDiscardRewardRegex.exec(abilityText);
    }
    // Step 2f: Extract [keyword:optional-put-bottom-hq:<n>] markup. Simple 2-segment token
    // for "You may put a card from the HQ on the bottom of the Hero Deck". The magnitude
    // is always 1 for this MVP form (how many cards to move). Only one occurrence per line.
    const optionalPutBottomHqRegex = new RegExp(OPTIONAL_PUT_BOTTOM_HQ_PATTERN.source, 'g');
    let optionalPutBottomHqMatch = optionalPutBottomHqRegex.exec(abilityText);
    if (optionalPutBottomHqMatch !== null) {
        const magnitude = parseInt(optionalPutBottomHqMatch[1], 10);
        if (magnitude >= 1) {
            keywords.push('optional-put-bottom-hq');
            magnitudes.set('optional-put-bottom-hq', magnitude);
        }
    }
    // Step 2f-bis: Extract [keyword:put-any-number-bottom-hq:<n>] markup (D-24132). Simple
    // 2-segment token for "Choose any number of cards/Heroes from the HQ. Put them on the bottom
    // of the Hero Deck" (the MULTI-select sibling of optional-put-bottom-hq). The magnitude is
    // always 1 for this MVP form. Only one occurrence per line. Any trailing Empowered classes
    // were captured by the pre-pass into putAnyNumberEmpoweredClasses and attach in the builder.
    const putAnyNumberBottomHqRegex = new RegExp(PUT_ANY_NUMBER_BOTTOM_HQ_PATTERN.source, 'g');
    const putAnyNumberBottomHqMatch = putAnyNumberBottomHqRegex.exec(abilityText);
    if (putAnyNumberBottomHqMatch !== null) {
        const magnitude = parseInt(putAnyNumberBottomHqMatch[1], 10);
        if (magnitude >= 1) {
            keywords.push('put-any-number-bottom-hq');
            magnitudes.set('put-any-number-bottom-hq', magnitude);
        }
    }
    // Step 2g: Extract [keyword:reveal:<predicate>:<actions>(:continue)?] parameterized
    // reveal tokens (forward-compat — no card uses this grammar this WP). Each token is
    // ONE RevealRule, accumulated in source order. When at least one rule parses, the
    // base 'reveal' keyword is recorded so the effect builder emits a single collapsed
    // reveal descriptor carrying these rules.
    // why: D-24024 — the parameterized grammar makes a new reveal variant a data marker
    // rather than a new keyword + handler + drift-test + WP. A malformed predicate or
    // action voids that one rule token (safe-skip, no throw).
    const parameterizedRevealRules = [];
    const revealRuleRegex = new RegExp(REVEAL_RULE_PATTERN.source, 'g');
    let revealRuleMatch = revealRuleRegex.exec(abilityText);
    while (revealRuleMatch !== null) {
        const parsedRule = parseRevealRuleToken(revealRuleMatch[1], revealRuleMatch[2], revealRuleMatch[3]);
        if (parsedRule !== null) {
            parameterizedRevealRules.push(parsedRule);
        }
        revealRuleMatch = revealRuleRegex.exec(abilityText);
    }
    if (parameterizedRevealRules.length > 0) {
        keywords.push('reveal');
    }
    // Step 2h: Extract the [keyword:reveal-count:<n>] modifier — how many deck-top
    // cards the reveal descriptor peeks. Absent ⇒ the WP-253 default of 1. Only the
    // first occurrence is read (one reveal descriptor per ability line).
    // why: D-24027 — the count is DESCRIPTOR-level (the reveal handler's peek-loop
    // bound), not rule-level (a per-card predicate); a dedicated 2-segment token
    // (mirrors COUNT_SCALED_PATTERN) keeps it distinct from the legacy
    // `[keyword:reveal:N]` draw threshold. `reveal-count` is a modifier marker, never
    // a HeroKeyword, so it contributes no keyword/effect of its own.
    let revealCount = 1;
    const revealCountRegex = new RegExp(REVEAL_COUNT_PATTERN.source, 'g');
    const revealCountMatch = revealCountRegex.exec(abilityText);
    if (revealCountMatch !== null) {
        const parsedRevealCount = parseInt(revealCountMatch[1], 10);
        // why: guard n ≥ 1 (mirrors the optional-ko-reward magnitude gate) — a 0 count
        // would build a reveal that peeks nothing, so fall back to the default.
        if (parsedRevealCount >= 1) {
            revealCount = parsedRevealCount;
        }
    }
    // why: WP-479 / D-24286 — the [keyword:reveal-reorder] modifier marker (bare, no
    // value) flags that the reveal's non-drawn remainder is player-reordered. Threaded
    // onto the collapsed reveal descriptor below, like revealCount.
    const reorderRemainder = REVEAL_REORDER_PATTERN.test(abilityText);
    // Step 3: Extract [icon:X] markup
    // why: WP-564 / D-24373 — on a RESOLVED investigate line the [icon:attack] / [icon:recruit]
    // token is the CRITERION ("Investigate for a card with an [icon:attack] icon"), not a
    // resource grant; it is already captured in investigateCriteria. Skip the icon→keyword
    // mapping so no phantom attack/recruit effect is emitted (mirrors the count-scaled and
    // KO-gated icon suppressions below, applied at extraction time here).
    if (!lineHasResolvedInvestigate) {
        const iconRegex = new RegExp(ICON_PATTERN.source, 'g');
        let iconMatch = iconRegex.exec(abilityText);
        while (iconMatch !== null) {
            const iconValue = iconMatch[1].toLowerCase();
            const mappedKeyword = ICON_TO_KEYWORD[iconValue];
            // why: WP-660 / D-24471 — skip an icon that is a CONDITION threshold (e.g. the
            // "[icon:recruit]" in "made at least 6[icon:recruit]"), so it does not push a phantom
            // 'recruit'/'attack' grant keyword. A grant icon elsewhere on the line is kept.
            if (mappedKeyword !== undefined
                && !overlapsSuppressedRange(iconMatch.index, iconMatch.index + iconMatch[0].length, suppressedIconRanges)) {
                keywords.push(mappedKeyword);
            }
            iconMatch = iconRegex.exec(abilityText);
        }
    }
    // Step 4: Normalize keywords — dedup, validate against union
    let uniqueKeywords = deduplicateKeywords(keywords);
    // Icon-suppression: a count-scaled attack effect subsumes the printed attack
    // icon on the same line. Without this, "+N[icon:attack] for each X" would emit
    // BOTH a flat 'attack' effect (from the icon, Steps 2b/3) AND the
    // 'attack-per-count' effect — a double-count (N flat + N×count). Drop the plain
    // 'attack' keyword and its magnitude so only the count-scaled effect remains.
    // why: the count-scaled keyword subsumes the printed attack icon (D-24016;
    // mirrors the D-21901 reveal-cost-attack precedent).
    let lineHasCountScaledAttack = false;
    for (const keyword of uniqueKeywords) {
        if (keyword === 'attack-per-count') {
            lineHasCountScaledAttack = true;
            break;
        }
    }
    if (lineHasCountScaledAttack) {
        const keywordsWithoutAttackIcon = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== 'attack') {
                keywordsWithoutAttackIcon.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutAttackIcon;
        magnitudes.delete('attack');
    }
    // Icon-suppression (WP-859 / D-24795): on a deck-koed-attack-icon line the printed attack icon
    // names the condition's criterion ("a card with an [icon:attack] icon was KO'd"), not a grant.
    // why: drop the `attack` keyword the descriptive icon produced (the D-24016 descriptive-token
    // shape), so the Transform hook carries no stray attack effect. The criterion icon prints no
    // number, so it never set an attack magnitude; a numbered "+N[icon:attack]" on the same line
    // is a real grant and is kept.
    if (lineReadsAttackIconDeckKo && !magnitudes.has('attack')) {
        const keywordsWithoutCriterionIcon = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== 'attack') {
                keywordsWithoutCriterionIcon.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutCriterionIcon;
    }
    // Icon-suppression (sibling): a count-scaled RECRUIT effect subsumes the
    // printed recruit icon on the same line — the recruit analog of the
    // attack-per-count suppression above. Without this, "+N[icon:recruit] for each
    // X" would emit BOTH a flat 'recruit' effect (from the icon, Steps 2b/3) AND
    // the 'recruit-per-count' effect — a double-count (N flat + N×count). Drop the
    // plain 'recruit' keyword and its magnitude so only the count-scaled effect
    // remains.
    // why: WP-674 / D-24489 — the count-scaled recruit keyword subsumes the printed
    // recruit icon (mirrors the D-24016 attack-per-count suppression above).
    let lineHasCountScaledRecruit = false;
    for (const keyword of uniqueKeywords) {
        if (keyword === 'recruit-per-count') {
            lineHasCountScaledRecruit = true;
            break;
        }
    }
    if (lineHasCountScaledRecruit) {
        const keywordsWithoutRecruitIcon = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== 'recruit') {
                keywordsWithoutRecruitIcon.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutRecruitIcon;
        magnitudes.delete('recruit');
    }
    // Icon-suppression (count-scaled choose-one): a count-scaled-choose line carries BOTH a
    // printed grant icon per option (`+1[icon:recruit]`, `+1[icon:attack]`) and the criterion
    // icons ("with a [icon:recruit] icon"). Steps 2b/3 would promote them to flat 'recruit' /
    // 'attack' keywords that fire UNCONDITIONALLY alongside the parked choice — a double-grant.
    // Drop both plain keywords so only the count-scaled-choose park remains (the grant is applied
    // at resolve time by the chosen option's per-count executor).
    // why: WP-675 / D-24490 — the count-scaled-choose keyword subsumes both printed icons
    // (mirrors the D-24016 / D-24489 per-count icon suppression above).
    if (processedAsCountScaledChoose) {
        const keywordsWithoutResourceIcons = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== 'attack' && keyword !== 'recruit') {
                keywordsWithoutResourceIcons.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutResourceIcons;
        magnitudes.delete('attack');
        magnitudes.delete('recruit');
    }
    // Icon-suppression (sibling): a reveal-herodeck-attack effect subsumes the
    // printed attack icon on the same line. Jade Giantess reads "…and you get that
    // card's printed[icon:attack]" — that bare icon is the effect's OWN grant (the
    // revealed Hero-Deck card's printed attack), NOT a separate flat attack. Without
    // this the icon promotes to a magnitude-less 'attack' keyword (Step 3), which the
    // executeSingleEffect magnitude pre-gate drops to a no-op but which still
    // mis-classifies the hook. Drop the plain 'attack' keyword (and any magnitude) so
    // only the reveal-herodeck-attack effect remains.
    // why: WP-668 / D-24481 — the reveal-Hero-Deck-attack keyword subsumes the printed
    // attack icon (mirrors the D-24016 attack-per-count suppression above).
    // why: D-24670 — the same holds for every keyword that computes its own "+[icon:attack]
    // equal to …" amount (SELF_COMPUTED_ATTACK_KEYWORDS); High Stakes Jackpot traced a
    // phantom 'attack' no-handler on every play.
    let lineHasRevealHeroDeckAttack = false;
    for (const keyword of uniqueKeywords) {
        if (SELF_COMPUTED_ATTACK_KEYWORDS.has(keyword)) {
            lineHasRevealHeroDeckAttack = true;
            break;
        }
    }
    if (lineHasRevealHeroDeckAttack) {
        const keywordsWithoutAttackIcon = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== 'attack') {
                keywordsWithoutAttackIcon.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutAttackIcon;
        magnitudes.delete('attack');
    }
    // Icon-suppression (sibling): a reveal whose matched rule grants a FIXED attack
    // subsumes the printed attack icon on the same line. "Reveal the top card of your
    // deck. If it costs 0, KO it and you get +1[icon:attack]" carries its grant as the
    // rule's attack-fixed action, applied only when the reveal matches. Without this,
    // Steps 2b/3 also promote the icon to a plain 'attack' effect granted on EVERY play
    // — a free +N with no reveal and no cost check, and +2N when the reveal matches.
    // why: D-24582 — the reveal attack-fixed action subsumes the printed attack icon
    // (the reveal analog of the D-24570 / D-24016 suppressions above). Covers the legacy
    // reveal-ko-attack keyword and any parameterized rule carrying attack-fixed.
    if (lineHasRevealFixedAttack(uniqueKeywords, parameterizedRevealRules)) {
        const keywordsWithoutAttackIcon = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== 'attack') {
                keywordsWithoutAttackIcon.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutAttackIcon;
        magnitudes.delete('attack');
    }
    // Icon-suppression (sibling): a shuffle-discard-empty-reward effect subsumes
    // the printed reward icon on the same line. Without this, "If your discard
    // pile is empty, you get +2[icon:recruit]..." would emit BOTH a flat
    // 'recruit' effect (from the icon, Steps 2b/3 — granted UNCONDITIONALLY on
    // every play) AND the conditional effect — a double-grant on the empty-discard
    // branch and a phantom grant on the shuffle branch. Drop the plain keyword
    // matching the seeded rewardType and its magnitude so only the conditional
    // effect remains.
    // why: D-24148 — the conditional keyword subsumes the printed reward icon
    // (mirrors the D-24016 count-scaled suppression above).
    const shuffleDiscardRewardType = rewardTypes.get('shuffle-discard-empty-reward');
    if (shuffleDiscardRewardType !== undefined) {
        const keywordsWithoutRewardIcon = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== shuffleDiscardRewardType) {
                keywordsWithoutRewardIcon.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutRewardIcon;
        magnitudes.delete(shuffleDiscardRewardType);
    }
    // Icon-suppression (sibling): an optional-ko-reward effect whose reward is an
    // icon resource (attack/recruit) subsumes the printed reward icon on the same
    // line. Without this, "You may KO a card...; if you do, you get +N[icon:recruit]"
    // emits BOTH a plain 'recruit' effect (from the icon, Steps 2b/3 — granted
    // UNCONDITIONALLY once the gate passes) AND the KO-gated optional-ko-reward — a
    // free grant plus a double-grant. Drop the plain keyword matching the seeded
    // rewardType and its magnitude so only the KO-gated reward remains. A no-op for
    // draw/rescue rewards (their prose emits no plain icon keyword), so a marked
    // rescue/draw card (Dangerous Rescue) is byte-identical.
    // why: D-24398 — the KO-gated reward subsumes the printed reward icon (mirrors
    // the D-24148 shuffle-discard-empty-reward and D-24016 count-scaled suppression).
    const optionalKoRewardType = rewardTypes.get('optional-ko-reward');
    if (optionalKoRewardType !== undefined) {
        const keywordsWithoutRewardIcon = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== optionalKoRewardType) {
                keywordsWithoutRewardIcon.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutRewardIcon;
        magnitudes.delete(optionalKoRewardType);
    }
    // Icon-suppression (sibling): an attack-per-ko-this-way effect subsumes the printed attack
    // icon on the same line. Mobilize for War's "[keyword:Savior]: For each card KO'd this way, you
    // get +1[icon:attack]." would otherwise also emit a plain 'attack' effect (Steps 2b/3) granted
    // once the Savior gate passes, with or without a KO. Drop it so only the per-KO reward remains.
    // why: WP-826 / D-24759 — the per-KO reward subsumes the printed attack icon (mirrors the
    // D-24398 optional-ko-reward suppression above).
    let lineHasAttackPerKoThisWay = false;
    for (const keyword of uniqueKeywords) {
        if (keyword === 'attack-per-ko-this-way') {
            lineHasAttackPerKoThisWay = true;
            break;
        }
    }
    if (lineHasAttackPerKoThisWay) {
        const keywordsWithoutAttackIcon = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== 'attack') {
                keywordsWithoutAttackIcon.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutAttackIcon;
        magnitudes.delete('attack');
    }
    // Icon-suppression (sibling): a ko-wound-reward effect whose reward is an icon
    // resource (attack/recruit) subsumes the printed reward icon on the same line.
    // Without this, Unstoppable Hulk's "You may KO a Wound ... If you do, you get
    // +2[icon:attack]" emits BOTH a plain 'attack' effect (Steps 2b/3 — granted
    // UNCONDITIONALLY, even with no Wound to KO) AND the Wound-gated reward — +2
    // with no Wound, +4 with one. Drop the plain keyword matching the seeded
    // rewardType and its magnitude so only the Wound-gated reward remains. A no-op
    // for the draw reward (its prose emits no plain icon keyword).
    // why: D-24570 — the Wound-gated reward subsumes the printed reward icon (the
    // ko-wound-reward analog of the D-24398 optional-ko-reward suppression above).
    const koWoundRewardIconType = rewardTypes.get('ko-wound-reward');
    if (koWoundRewardIconType !== undefined) {
        const keywordsWithoutRewardIcon = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== koWoundRewardIconType) {
                keywordsWithoutRewardIcon.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutRewardIcon;
        magnitudes.delete(koWoundRewardIconType);
    }
    // Icon-suppression (sibling): a put-bottom-hq-icon-reward effect subsumes BOTH
    // printed reward icons on the same line. Wonder Man's Absorb Ambient Power reads
    // "If that card had a [icon:recruit] icon, you get +3[icon:recruit]. If that card
    // had an [icon:attack] icon, you get +3[icon:attack]." — both grants are applied
    // at resolve time by resolveOptionalPutBottomHQ from the moved card's icons
    // (iconRewardMagnitude). Without this, Steps 2b/3 also emit plain 'recruit' and
    // 'attack' effects granted UNCONDITIONALLY on every play — a free +N/+N on top of
    // the real reward. Drop both plain keywords and their magnitudes.
    // why: D-24570 — the resolve-time icon reward subsumes both printed reward icons
    // (mirrors the D-24490 count-scaled-choose two-icon suppression above).
    let lineHasPutBottomHqIconReward = false;
    for (const keyword of uniqueKeywords) {
        if (keyword === 'put-bottom-hq-icon-reward') {
            lineHasPutBottomHqIconReward = true;
            break;
        }
    }
    if (lineHasPutBottomHqIconReward) {
        const keywordsWithoutResourceIcons = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== 'attack' && keyword !== 'recruit') {
                keywordsWithoutResourceIcons.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutResourceIcons;
        magnitudes.delete('attack');
        magnitudes.delete('recruit');
    }
    // Icon-suppression (sibling): an optional-play-villain-top effect subsumes the printed
    // attack icon on the same line. Without this, Shadowed Thoughts' "[hc:covert]: You may play
    // the top card of the Villain Deck. If you do, +2[icon:attack]." would emit BOTH a plain
    // 'attack' effect (from the icon, Steps 2b/3 — granted UNCONDITIONALLY once the covert gate
    // passes) AND the pending-choice optional-play-villain-top effect — a free +2 plus the
    // choice's reward. Drop the plain 'attack' keyword + its magnitude so only the pending
    // choice remains (the +2 is the choice's `attackReward`, granted on accept).
    // why: WP-663 / D-24474 — the pending-choice reward subsumes the printed attack icon
    // (mirrors the D-24016 count-scaled + D-24398 optional-ko-reward suppression above).
    let lineHasOptionalPlayVillainTop = false;
    for (const keyword of uniqueKeywords) {
        if (keyword === 'optional-play-villain-top') {
            lineHasOptionalPlayVillainTop = true;
            break;
        }
    }
    if (lineHasOptionalPlayVillainTop) {
        const keywordsWithoutAttackIcon = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== 'attack') {
                keywordsWithoutAttackIcon.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutAttackIcon;
        magnitudes.delete('attack');
    }
    // Icon-suppression (sibling): a Blood Frenzy line's printed icons are DESCRIPTIVE. Morbius
    // Mesmerize reads "Blood Frenzy, gaining [icon:recruit] instead of [icon:attack]" — neither
    // icon is a flat grant; the blood-frenzy(-recruit) handler grants the distinct-VP count.
    // Without this, Step 3 promotes both icons to magnitude-less plain keywords that sit beside the
    // real effect and mis-classify the hook.
    // why: WP-765 / D-24598 — the Blood Frenzy keywords subsume the printed resource icons
    // (mirrors the D-24481 reveal-herodeck-attack suppression above).
    let lineHasBloodFrenzy = false;
    for (const keyword of uniqueKeywords) {
        if (keyword === 'blood-frenzy' || keyword === 'blood-frenzy-recruit') {
            lineHasBloodFrenzy = true;
            break;
        }
    }
    if (lineHasBloodFrenzy) {
        const keywordsWithoutResourceIcons = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== 'attack' && keyword !== 'recruit') {
                keywordsWithoutResourceIcons.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutResourceIcons;
        magnitudes.delete('attack');
        magnitudes.delete('recruit');
    }
    // Unmodeled day/night line suppression: drop the parsed attack/recruit grant and every
    // non-day/night condition, keeping only the Sunlight/Moonlight gate and the unresolved marker
    // pushed in Step 2.
    // why: WP-765 / D-24598 — without this the gated lines in DAY_NIGHT_UNMODELED_LINES would
    // silently grant bonuses the engine does not model ("If you do, you get +2[icon:attack]",
    // "+1[icon:attack] for each …") whenever the state is in effect — the D-24570 parsed-grant
    // suppression sibling. Their other tokens ("for each other [team:x-men] card") describe the
    // unmodeled grant, not a play gate, so they are dropped too: the line then logs and records
    // its day/night hollow exactly when its state holds.
    if (dayNightUnmodeled) {
        const keywordsWithoutResourceIcons = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== 'attack' && keyword !== 'recruit') {
                keywordsWithoutResourceIcons.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutResourceIcons;
        magnitudes.delete('attack');
        magnitudes.delete('recruit');
        const dayNightConditions = [];
        for (const condition of conditions) {
            if (DAY_NIGHT_CONDITION_TYPES.has(condition.type)) {
                dayNightConditions.push(condition);
            }
        }
        conditions.splice(0, conditions.length, ...dayNightConditions);
    }
    // Unmodeled conditional-grant suppression: drop the parsed attack/recruit grant, keep the
    // play gate, and record an honest hollow.
    // why: D-24628 — the grant on these lines is earned only by a clause the engine does not model
    // ("KO a [team:shield] Hero or Wound … to get +2[icon:attack]", "If a Bindings is gained this
    // way, you get +1[icon:recruit]"), so the icon read would fire it for free. A spurious
    // requiresTeam gate from the body [team:X] used to hide that only some of the time.
    if (conditionalGrantUnmodeled) {
        const keywordsWithoutResourceIcons = [];
        for (const keyword of uniqueKeywords) {
            if (keyword !== 'attack' && keyword !== 'recruit') {
                keywordsWithoutResourceIcons.push(keyword);
            }
        }
        uniqueKeywords = keywordsWithoutResourceIcons;
        magnitudes.delete('attack');
        magnitudes.delete('recruit');
        unresolvedMarkers.push(CONDITIONAL_GRANT_UNMODELED_MARKER);
    }
    // If conditions were found, add 'conditional' keyword
    if (conditions.length > 0) {
        let hasConditional = false;
        for (const keyword of uniqueKeywords) {
            if (keyword === 'conditional') {
                hasConditional = true;
                break;
            }
        }
        if (!hasConditional) {
            uniqueKeywords.push('conditional');
        }
    }
    // Build effect descriptors from extracted keywords.
    // Apply magnitude from the magnitudes map when available.
    for (const keyword of uniqueKeywords) {
        if (keyword !== 'conditional') {
            const magnitude = magnitudes.get(keyword);
            if (keyword === 'attack-per-count') {
                // why: the count-scaled attack effect carries its count source so the
                // executor can resolve the count to scale the per-unit magnitude by.
                // Step 2d records both the per-unit magnitude and the source together,
                // so the guard both narrows the optional Map reads and is defensive.
                const countSource = countSources.get('attack-per-count');
                if (magnitude !== undefined && countSource !== undefined) {
                    // why: WP-740 / D-24562 — perEach is attached ONLY when the marker printed it
                    // (omit-when-absent), so every 3-segment card's effect stays byte-identical.
                    if (attackPerEach !== undefined) {
                        effects.push({ type: keyword, magnitude, countSource, perEach: attackPerEach });
                    }
                    else {
                        effects.push({ type: keyword, magnitude, countSource });
                    }
                }
            }
            else if (keyword === 'recruit-per-count') {
                // why: WP-674 / D-24489 — the count-scaled recruit effect carries its
                // count source (the attack-per-count precedent) so the executor can
                // resolve the count to scale the per-unit magnitude by. Step 2d' records
                // both together, so the guard both narrows the optional Map reads and is
                // defensive.
                const recruitCountSource = countSources.get('recruit-per-count');
                if (magnitude !== undefined && recruitCountSource !== undefined) {
                    effects.push({ type: keyword, magnitude, countSource: recruitCountSource });
                }
            }
            else if (keyword === 'kidnap-per-count') {
                // why: WP-714 / D-24537 — the count-scaled bystander-capture effect carries
                // its count source (the attack-per-count precedent) so the executor can
                // resolve the count to scale the per-unit magnitude by. Step 2d'' records
                // both together, so the guard both narrows the optional Map reads and is
                // defensive.
                const kidnapCountSource = countSources.get('kidnap-per-count');
                if (magnitude !== undefined && kidnapCountSource !== undefined) {
                    effects.push({ type: keyword, magnitude, countSource: kidnapCountSource });
                }
            }
            else if (keyword === 'count-scaled-choose') {
                // why: WP-675 / D-24490 — the count-scaled-choose effect carries the two options
                // (each a {resource, countSource, magnitude}) the pre-pass folded from the printed
                // "Choose one:" form; the park handler records them on the PendingCountScaledChoice.
                if (countScaledChoiceOptions !== undefined && countScaledChoiceOptions.length >= 2) {
                    effects.push({ type: keyword, countScaledChoiceOptions });
                }
            }
            else if (keyword === 'optional-ko-reward') {
                // why: D-24019 — the optional-KO-reward effect carries its rewardType so
                // the resolve move can dispatch the reward to the existing executor on
                // KO; magnitude is the reward magnitude. Step 2e records both together,
                // so the guard both narrows the optional Map reads and is defensive.
                const rewardType = rewardTypes.get('optional-ko-reward');
                if (magnitude !== undefined && rewardType !== undefined) {
                    effects.push({ type: keyword, magnitude, rewardType });
                }
            }
            else if (keyword === 'ko-wound-reward') {
                // why: WP-382 / D-24183 — the ko-wound-reward effect carries its rewardType
                // so the executor can dispatch the reward via executeSingleEffect after
                // KO'ing the Wound; magnitude is the reward magnitude. Step 2f records both,
                // so the guard both narrows the optional Map reads and is defensive.
                const koWoundRewardType = rewardTypes.get('ko-wound-reward');
                if (magnitude !== undefined && koWoundRewardType !== undefined) {
                    effects.push({ type: keyword, magnitude, rewardType: koWoundRewardType });
                }
            }
            else if (keyword === 'shuffle-discard-empty-reward') {
                // why: D-24148 — the shuffle-discard-empty-reward effect carries its
                // rewardType so the executor's empty-discard branch can grant the right
                // resource; magnitude is the grant amount. Step 2e-bis records both
                // together, so the guard both narrows the optional Map reads and is
                // defensive.
                const shuffleRewardType = rewardTypes.get('shuffle-discard-empty-reward');
                if (magnitude !== undefined && shuffleRewardType !== undefined) {
                    effects.push({ type: keyword, magnitude, rewardType: shuffleRewardType });
                }
            }
            else if (keyword === 'draw-or-empowered') {
                // why: D-24069 — the draw-or-empowered effect carries the empowered hero class parsed by
                // the pre-pass so the park site records it on the PendingDrawOrEmpowered entry; the resolve
                // move's 'empowered' branch reuses buildEmpoweredComposition(empoweredClass). The pre-pass
                // guarantees drawOrEmpoweredClass is set whenever this keyword reaches the effect builder.
                if (drawOrEmpoweredClass !== undefined) {
                    effects.push({ type: keyword, empoweredClass: drawOrEmpoweredClass });
                }
            }
            else if (keyword === 'put-any-number-bottom-hq') {
                // why: D-24132 — the put-any-number-bottom-hq effect carries any trailing Empowered
                // classes captured by the pre-pass so the park site records them on the pending entry;
                // the resolve move applies each via buildEmpoweredComposition AFTER the moves resolve.
                // Omit-when-empty: no empoweredClasses field when the line has no Empowered tail.
                effects.push({
                    type: keyword,
                    ...(magnitude !== undefined ? { magnitude } : {}),
                    ...(putAnyNumberEmpoweredClasses.length > 0
                        ? { empoweredClasses: putAnyNumberEmpoweredClasses }
                        : {}),
                });
            }
            else if (REVEAL_KEYWORD_SET.has(keyword)) {
                // why: D-24024 — the dual-grammar seam. A legacy reveal-* keyword translates
                // through revealRulesForLegacyKeyword into the collapsed `reveal` descriptor;
                // the parameterized `[keyword:reveal:...]` grammar (Step 2f) supplies its rules
                // directly for the base `reveal` keyword. Either way the LEGACY keyword stays
                // on hook.keywords (narrative identity — no reverse-map), only the effect is
                // translated. An invalid magnitude yields empty revealRules (a no-op reveal),
                // reproducing the legacy pre-gate/self-guard skip while still emitting one effect.
                let revealRules;
                if (keyword === 'reveal' && parameterizedRevealRules.length > 0) {
                    revealRules = parameterizedRevealRules;
                }
                else {
                    revealRules = revealRulesForLegacyKeyword(keyword, magnitude);
                }
                // why: D-24027 — the descriptor-level reveal-count (Step 2g; default 1) sets how
                // many deck-top cards the reveal handler peeks. Threaded onto the one collapsed
                // reveal descriptor this line emits, whether legacy-translated or parameterized.
                // why: WP-479 / D-24286 — thread reorderRemainder onto the reveal descriptor
                // only when the marker is present, so descriptors without it stay byte-identical.
                effects.push(reorderRemainder ? { type: 'reveal', revealCount, revealRules, reorderRemainder: true } : { type: 'reveal', revealCount, revealRules });
            }
            else if (keyword === 'investigate') {
                // why: WP-564 / D-24373 — the investigate descriptor carries the static criterion
                // (resolved up front) and the look count (default 2, the printed count — a descriptor
                // field so the deferred look-at-three modifier sets it without reshaping the effect).
                // The keyword is only present when investigateCriteria resolved, so the guard is defensive.
                if (investigateCriteria !== undefined) {
                    effects.push({
                        type: 'investigate',
                        investigateLookCount: INVESTIGATE_DEFAULT_LOOK_COUNT,
                        investigateCriteria,
                    });
                }
            }
            else if (keyword === 'reveal-from-hand') {
                // why: WP-659 / D-24470 — the reveal-from-hand descriptor carries the SINGLE reveal
                // criterion captured up front. The keyword is only present when the criterion
                // resolved (Step 2 Honest-Partial), so the guard is defensive.
                if (revealFromHandCriterion !== undefined) {
                    effects.push({ type: 'reveal-from-hand', revealCriterion: revealFromHandCriterion });
                }
            }
            else if (keyword === 'reveal-or-gain-wound-each') {
                // why: WP-843 / D-24777 — the reveal-or-gain-wound descriptor carries the same single
                // reveal criterion (the reveal AVOIDS a Wound instead of earning a draw). The keyword is
                // only present when the criterion resolved (Step 2 Honest-Partial), so the guard is defensive.
                if (revealFromHandCriterion !== undefined) {
                    effects.push({ type: 'reveal-or-gain-wound-each', revealCriterion: revealFromHandCriterion });
                }
            }
            else if (keyword === 'feast') {
                // why: WP-859 / D-24795 — "Feast up to three times" builds one `feast` effect per Feast
                // (three sequential KO-or-keep prompts); keywords stay de-duplicated, so the count comes
                // from FEAST_REPEAT_COUNTS via feastRepeatCount (1 for every other Feast card).
                for (let feastIndex = 0; feastIndex < feastRepeatCount; feastIndex++) {
                    effects.push({ type: 'feast' });
                }
            }
            else if (magnitude !== undefined) {
                effects.push({ type: keyword, magnitude });
            }
            else {
                effects.push({ type: keyword });
            }
        }
    }
    // Step 4d: WP-859 / D-24795 — the per-Feast reward stamp.
    // why: World-Spanning Hunger's "+2 for each non-grey Hero that was KO'd from your deck this turn"
    // counts its own Feasts' KOs, which land in later moves (RS-2, counted at resolution). The
    // play-time attack-per-count covers KOs already made; each Feast carries the same per-unit
    // magnitude to pay on its own non-grey Hero KO. Read from the card's one marker, never a second
    // literal.
    let nonGreyHeroKoCountMagnitude;
    for (const effect of effects) {
        if (effect.type === 'attack-per-count' && effect.countSource === 'non-grey-heroes-koed-from-deck-this-turn') {
            nonGreyHeroKoCountMagnitude = effect.magnitude;
        }
    }
    if (nonGreyHeroKoCountMagnitude !== undefined) {
        for (const effect of effects) {
            if (effect.type === 'feast') {
                effect.nonGreyHeroKoAttack = nonGreyHeroKoCountMagnitude;
            }
        }
    }
    // Step 4a: WP-790 / D-24652 — the "usable only against …" restriction clause.
    // why: set ONLY on an `attack` effect whose magnitude came from the Step 2b icon-magnitude
    // (a suppressed Focus / Lightshow cost, a magnitude-less "+[icon:attack]" and a keyword
    // magnitude never reach here). Fails closed: a clause with no recognized target, or with the
    // word "other" (nmut Karma), leaves the line parsing exactly as before.
    if (attackIconMagnitudeEnd !== undefined) {
        const restrictionTargets = parseAttackRestrictionTargets(abilityText.slice(attackIconMagnitudeEnd));
        if (restrictionTargets !== undefined) {
            for (const effect of effects) {
                if (effect.type === 'attack' && effect.magnitude !== undefined) {
                    effect.attackRestriction = { targets: restrictionTargets };
                }
            }
        }
    }
    // Step 4c: WP-794 / D-24663 — the fight-cost reduction clause.
    // why: a separate clause recognizer, NOT the icon extractors. D-24486's negative-icon
    // suppression stays in force (the "-N[icon:attack]" never becomes a phantom +N grant); this
    // step recognizes the whole printed clause instead. It pushes straight to `effects` /
    // `uniqueKeywords` (never through `keywords` / `magnitudes`, which would rebuild a bare effect
    // without its target). The gate prefix's conditions parsed above are unchanged, and a line
    // that matches neither locked regex parses exactly as before.
    const fightCostReduction = parseFightCostReductionClause(abilityText.slice(leadingGatePrefixLength).trim());
    if (fightCostReduction !== undefined) {
        effects.push({
            type: 'fight-cost-reduction',
            magnitude: fightCostReduction.amount,
            fightCostReductionTarget: fightCostReduction.target,
        });
        uniqueKeywords.push('fight-cost-reduction');
    }
    // Step 4b: surface an unmodeled [rule:X] line as an honest hollow.
    // why: D-24618 — a line that resolved NOTHING (no keyword, effect, composition, or other
    // unresolved marker) but carries a `[rule:X]` token is an ability the engine does not
    // model, not flavor text. Record `rule:<concept>` so detectHollowHeroHook flags it
    // `parse-unrecognized`. Scoped to fully-empty lines so a line that already resolved an
    // effect keeps its hook byte-identical, and reminder parentheticals are exempt.
    // why: D-24623 — the same holds for a line whose only resolved piece is its GATE
    // (`[hc:X]: <text>`, `[team:X]: <text>`, or a condition keyword such as Outwit / Savior).
    // The gate parses to `conditions` + the 'conditional' keyword and the body to nothing, so
    // a passed gate fired nothing and reported nothing (cvwr Tsunami of Justice). Such a line
    // records its `rule:<concept>` tokens, else its unmatched multi-word `[keyword:X N]` names
    // (normalized like the coverage probe, so runtime and ledger rows share one key), else
    // `gate-only`.
    // why: D-24624 — an UNGATED fully-empty line records its unmatched keyword names too
    // ("[keyword:Soaring Flight]", "[keyword:Danger Sense 2]"): same order (rule tokens first),
    // but no `gate-only` fallback, so plain-English flavor text still builds an empty hook.
    const lineResolvedOnlyItsGate = uniqueKeywords.length === 1 && uniqueKeywords[0] === 'conditional';
    let ungatedKeywordTiming;
    if ((uniqueKeywords.length === 0 || lineResolvedOnlyItsGate) &&
        effects.length === 0 &&
        primitiveEffects.length === 0 &&
        unresolvedMarkers.length === 0 &&
        !REMINDER_TEXT_PATTERN.test(abilityText)) {
        unresolvedMarkers.push(...collectRuleTokenMarkers(abilityText));
        for (const keywordSlug of collectUnmatchedKeywordSlugs(abilityText)) {
            if (!unresolvedMarkers.includes(keywordSlug)) {
                unresolvedMarkers.push(keywordSlug);
            }
        }
        if (lineResolvedOnlyItsGate && unresolvedMarkers.length === 0) {
            unresolvedMarkers.push(GATE_ONLY_MARKER);
        }
        if (!lineResolvedOnlyItsGate) {
            ungatedKeywordTiming = findLeadingUnmatchedKeywordTiming(abilityText);
        }
    }
    // Step 5: Assign timing — a keyword default (e.g. wall-crawl → onRecruit), then an
    // explicit [timing:X] markup override; otherwise 'onPlay'. No NL inference.
    // why: D-24049 — a keyword's default timing (KEYWORD_TIMING_DEFAULTS) is applied
    // first so [keyword:Wall-Crawl] resolves to an onRecruit hook; an explicit [timing:X]
    // marker still wins (applied after); a line with no mapped keyword keeps onPlay.
    let timing = 'onPlay';
    for (const keyword of uniqueKeywords) {
        const keywordDefaultTiming = KEYWORD_TIMING_DEFAULTS[keyword];
        if (keywordDefaultTiming !== undefined) {
            timing = keywordDefaultTiming;
            break;
        }
    }
    // why: D-24624 — a marker-only line led by a recruit / fight / reveal keyword takes that
    // printed timing (the line resolved no keyword, so the loop above left it at onPlay).
    if (ungatedKeywordTiming !== undefined) {
        timing = ungatedKeywordTiming;
    }
    const timingMatch = TIMING_PATTERN.exec(abilityText);
    if (timingMatch !== null) {
        const mappedTiming = TIMING_MARKUP_MAP[timingMatch[1]];
        if (mappedTiming !== undefined) {
            timing = mappedTiming;
        }
    }
    return {
        keywords: uniqueKeywords,
        conditions,
        effects: effects.length > 0 ? effects : [],
        primitiveEffects,
        unresolvedMarkers,
        resolvedMarkers,
        sizeChangingClasses,
        microscopicSizeChangingClasses,
        timing,
    };
}
/**
 * Parses a printed fight-cost reduction clause (WP-794 / D-24663).
 *
 * @param postGateText - The ability text after any leading gate prefix, trimmed.
 * @returns The target (a lowercased City space name, or `'mastermind'`) and the
 *   amount, or undefined when the text matches neither locked form (fail closed).
 */
function parseFightCostReductionClause(postGateText) {
    const cityMatch = FIGHT_COST_REDUCTION_CITY_PATTERN.exec(postGateText);
    if (cityMatch !== null) {
        const spaceName = cityMatch[1].toLowerCase();
        for (const citySpaceName of CITY_SPACE_NAMES) {
            if (citySpaceName === spaceName) {
                return { target: citySpaceName, amount: Number(cityMatch[2]) };
            }
        }
        return undefined;
    }
    const mastermindMatch = FIGHT_COST_REDUCTION_MASTERMIND_PATTERN.exec(postGateText);
    if (mastermindMatch !== null) {
        return { target: 'mastermind', amount: Number(mastermindMatch[1]) };
    }
    return undefined;
}
/**
 * Parses the targets of a "usable only against …" clause (WP-790 / D-24652).
 *
 * @param textAfterAttackIcon - The ability text after the `+N[icon:attack]` grant.
 * @returns The targets in canonical order (`CITY_SPACE_NAMES` order, then
 *   `'mastermind'`), or undefined when there is no clause, no recognized target,
 *   or an "other" exclusion (fail closed).
 */
function parseAttackRestrictionTargets(textAfterAttackIcon) {
    const clauseMatch = ATTACK_RESTRICTION_CLAUSE_PATTERN.exec(textAfterAttackIcon);
    if (clauseMatch === null) {
        return undefined;
    }
    const clause = clauseMatch[1];
    if (ATTACK_RESTRICTION_OTHER_PATTERN.test(clause)) {
        return undefined;
    }
    const targets = [];
    for (const spaceName of CITY_SPACE_NAMES) {
        if (new RegExp(`\\b${spaceName}\\b`, 'i').test(clause)) {
            targets.push(spaceName);
        }
    }
    if (ATTACK_RESTRICTION_MASTERMIND_PATTERN.test(clause)) {
        targets.push('mastermind');
    }
    if (targets.length === 0) {
        return undefined;
    }
    return targets;
}
/**
 * Returns the restricted `attack` effect on a hook, if any (WP-790 / D-24652).
 *
 * @param hook - A per-line hero ability hook.
 * @returns The hook's `attack` effect that carries `attackRestriction`, else undefined.
 */
function findRestrictedAttackEffect(hook) {
    for (const effect of hook.effects ?? []) {
        if (effect.type === 'attack' && effect.attackRestriction !== undefined) {
            return effect;
        }
    }
    return undefined;
}
/**
 * Fuses a Ranged "… against the Mastermind instead" follow-up line into the
 * restricted grant on the immediately preceding line (WP-790 / D-24652).
 *
 * When `abilityText` is widen (a) (Storm) or widen (b) (Electro, with N equal to
 * the preceding grant's magnitude), the preceding grant gets
 * `widenToMastermindWhen` = a copy of the follow-up's conditions, and the
 * follow-up hook loses its `attack` effect and `attack` keyword (its conditions
 * stay). Otherwise nothing changes.
 *
 * @param previousLineHook - The hook built from the previous line of the same card, or null.
 * @param followUpHook - The hook just built from `abilityText`.
 * @param abilityText - The follow-up line's text.
 */
function fuseAttackRestrictionWiden(previousLineHook, followUpHook, abilityText) {
    if (previousLineHook === null) {
        return;
    }
    const restrictedEffect = findRestrictedAttackEffect(previousLineHook);
    if (restrictedEffect === undefined) {
        return;
    }
    const isBonusWiden = ATTACK_RESTRICTION_WIDEN_BONUS_PATTERN.test(abilityText);
    const insteadMatch = ATTACK_RESTRICTION_WIDEN_INSTEAD_PATTERN.exec(abilityText);
    const isInsteadWiden = insteadMatch !== null && parseInt(insteadMatch[1], 10) === restrictedEffect.magnitude;
    if (!isBonusWiden && !isInsteadWiden) {
        return;
    }
    // why: WP-790 / D-24652 — the follow-up line is the SAME grant re-aimed at the Mastermind,
    // not a second grant (Electro's "Instead" would otherwise give +6). The widen conditions are a
    // fresh array of copied objects (D-13502: never alias the follow-up hook's own conditions).
    // Electro's Bank/Mastermind amount stays one shared bucket — a known approximation of "one or
    // the other" recorded in D-24652.
    const widenConditions = [];
    for (const condition of followUpHook.conditions ?? []) {
        widenConditions.push({ ...condition });
    }
    restrictedEffect.attackRestriction = {
        targets: [...restrictedEffect.attackRestriction.targets],
        widenToMastermindWhen: widenConditions,
    };
    // why: strip the follow-up's attack effect and keyword (conditions kept) so it makes no
    // second grant and no magnitude-less attack is classified as an `attack-no-magnitude`
    // hollow (D-24649) for a behavior that is now modeled.
    const remainingEffects = [];
    for (const effect of followUpHook.effects ?? []) {
        if (effect.type !== 'attack') {
            remainingEffects.push(effect);
        }
    }
    if (remainingEffects.length > 0) {
        followUpHook.effects = remainingEffects;
    }
    else {
        delete followUpHook.effects;
    }
    const remainingKeywords = [];
    for (const keyword of followUpHook.keywords) {
        if (keyword !== 'attack') {
            remainingKeywords.push(keyword);
        }
    }
    followUpHook.keywords = remainingKeywords;
}
/**
 * Collects the `rule:<concept>` unresolved markers for every `[rule:X]` token on a line
 * (D-24618): lower-case, whitespace to hyphen, plurals folded to the singular concept,
 * deduplicated, in text order.
 *
 * @param abilityText - One hero ability line.
 * @returns The rule markers, empty when the line has no `[rule:X]` token.
 */
function collectRuleTokenMarkers(abilityText) {
    const ruleMarkers = [];
    const ruleTokenRegex = new RegExp(RULE_TOKEN_PATTERN.source, 'g');
    let ruleTokenMatch = ruleTokenRegex.exec(abilityText);
    while (ruleTokenMatch !== null) {
        const ruleSlug = ruleTokenMatch[1].trim().toLowerCase().replace(/\s+/g, '-');
        const conceptSlug = RULE_TOKEN_SINGULAR[ruleSlug] ?? ruleSlug;
        const ruleMarker = `rule:${conceptSlug}`;
        if (!ruleMarkers.includes(ruleMarker)) {
            ruleMarkers.push(ruleMarker);
        }
        ruleTokenMatch = ruleTokenRegex.exec(abilityText);
    }
    return ruleMarkers;
}
/**
 * Collects the mechanic names of the `[keyword:...]` tokens KEYWORD_PATTERN cannot match
 * (multi-word or space-magnitude forms), normalized the way `scripts/hero-effect-coverage.mjs`
 * normalizes mechanic tokens: trimmed, lower-case, trailing magnitude dropped, whitespace to
 * hyphen ("Danger Sense 2" → "danger-sense"). Deduplicated, in text order (D-24623).
 *
 * @param abilityText - One hero ability line.
 * @returns The unmatched keyword names, empty when every token matched KEYWORD_PATTERN.
 */
function collectUnmatchedKeywordSlugs(abilityText) {
    const keywordSlugs = [];
    const tokenRegex = new RegExp(ANY_KEYWORD_TOKEN_PATTERN.source, 'g');
    let tokenMatch = tokenRegex.exec(abilityText);
    while (tokenMatch !== null) {
        const tokenBody = tokenMatch[1].trim();
        if (!MATCHED_KEYWORD_BODY_PATTERN.test(tokenBody)) {
            const keywordSlug = tokenBody
                .toLowerCase()
                .replace(/:\d+$/, '')
                .replace(/\s+\d+$/, '')
                .replace(/\s+/g, '-');
            if (keywordSlug !== '' && !keywordSlugs.includes(keywordSlug)) {
                keywordSlugs.push(keywordSlug);
            }
        }
        tokenMatch = tokenRegex.exec(abilityText);
    }
    return keywordSlugs;
}
/**
 * Returns the printed non-play timing of the unmatched keyword that leads a line, if any
 * (D-24624). Only a line whose FIRST token is the keyword qualifies — a sentence that merely
 * mentions it ("All Heroes you recruit this turn have [keyword:Soaring Flight].") is a play
 * effect and keeps onPlay.
 *
 * @param abilityText - One hero ability line.
 * @returns The keyword's timing from UNMATCHED_KEYWORD_TIMINGS, or undefined.
 */
function findLeadingUnmatchedKeywordTiming(abilityText) {
    const leadingMatch = LEADING_KEYWORD_TOKEN_PATTERN.exec(abilityText);
    if (leadingMatch === null) {
        return undefined;
    }
    const leadingSlugs = collectUnmatchedKeywordSlugs(leadingMatch[0]);
    if (leadingSlugs.length === 0) {
        return undefined;
    }
    return UNMATCHED_KEYWORD_TIMINGS[leadingSlugs[0]];
}
/**
 * Checks if a string is a valid HeroKeyword.
 */
function isValidHeroKeyword(value) {
    for (const keyword of HERO_KEYWORDS) {
        if (keyword === value) {
            return true;
        }
    }
    return false;
}
/**
 * Checks if a string is a hero composition marker (a key in HERO_COMPOSITION_MARKERS).
 *
 * Mirrors isValidHeroKeyword. A composition marker is the OPEN mechanic space (D-24031),
 * distinct from the closed HeroKeyword union — `berserk` is a marker, never a keyword.
 */
function isHeroCompositionMarker(value) {
    for (const markerName of HERO_COMPOSITION_MARKER_NAMES) {
        if (markerName === value) {
            return true;
        }
    }
    return false;
}
/**
 * Checks if a string is a parameterized composition marker
 * (a name in PARAMETERIZED_COMPOSITION_MARKER_NAMES).
 *
 * Mirrors isHeroCompositionMarker, but for markers whose AST is BUILT per a parsed parameter
 * (empowered) rather than stored as a static HERO_COMPOSITION_MARKERS row (D-24044).
 */
function isParameterizedCompositionMarker(value) {
    for (const markerName of PARAMETERIZED_COMPOSITION_MARKER_NAMES) {
        if (markerName === value) {
            return true;
        }
    }
    return false;
}
/**
 * Resolves the unconditional core form of a parameterized Empowered marker, or undefined for
 * a deferred variant. Returns a built `gain-resource` composition ONLY when (a) the text
 * immediately after the marker is an anchored `by [hc:COLOR]` tail, AND (b) that color is the
 * line's SOLE condition (no prefix gate, no multi-class, no team gate). Any miss → undefined,
 * so the caller records an unresolved marker (the Honest-Partial Invariant). Does not mutate
 * `conditions`; the caller suppresses the consumed param on a match.
 *
 * @param textAfterMarker - The ability text immediately following the `[keyword:Empowered]` token.
 * @param conditions - The line's conditions (heroClassMatch + requiresTeam), in hook order.
 * @returns The built Empowered composition, or undefined for a deferred variant.
 */
function tryResolveEmpoweredCore(textAfterMarker, conditions) {
    // why: D-24044 — anchored marker-tail match only; the color must immediately follow as
    // `by [hc:COLOR]`. A non-anchored scan would bind a later unrelated [hc:...] to Empowered.
    const tailMatch = EMPOWERED_PARAM_TAIL_PATTERN.exec(textAfterMarker);
    if (tailMatch === null) {
        return undefined;
    }
    const heroClass = normalizeTraitSlug(tailMatch[1]);
    // why: D-24044 Honest-Partial — resolve ONLY when the consumed [hc:COLOR] param is the
    // line's sole condition. A residual condition (an [hc:X]:/[team:X]: prefix gate, a
    // multi-class `and [hc:Y]`, or a team gate) means a deferred variant → keep it hollow.
    const onlyCondition = conditions[0];
    if (conditions.length !== 1
        || onlyCondition === undefined
        || onlyCondition.type !== 'heroClassMatch'
        || onlyCondition.value !== heroClass) {
        return undefined;
    }
    return buildEmpoweredComposition(heroClass);
}
/**
 * Extracts the trailing "Empowered by [classes]" hero classes from a put-any-number-bottom-hq
 * line, or an empty array when the line has no Empowered tail (D-24132). Finds the
 * `[keyword:Empowered]` token, then reads the anchored `by [hc:X] (and [hc:Y]…)` tail
 * immediately after it — the same multi-class / single-class grammar the standalone Empowered
 * resolvers accept. Returns the normalized classes in printed order. Pure; mutates nothing.
 *
 * @param abilityText - The full ability text line (already carrying the put-any-number marker).
 * @returns The normalized Empowered classes in printed order (empty when there is no tail).
 */
function extractPutAnyNumberEmpoweredTailClasses(abilityText) {
    const markerMatch = EMPOWERED_MARKER_TOKEN_PATTERN.exec(abilityText);
    if (markerMatch === null) {
        return [];
    }
    const textAfterMarker = abilityText.slice(markerMatch.index + markerMatch[0].length);
    const classes = [];
    // why: try the multi-class tail first (`by [hc:X] and [hc:Y]…`); its `(?:and [hc:…])+`
    // requirement means it never matches the single-class form, so the single-class fallback
    // below owns `by [hc:X]`. Extract every [hc:…] token from whichever tail matched.
    const multiClassTail = EMPOWERED_MULTICLASS_FULL_TAIL_PATTERN.exec(textAfterMarker);
    if (multiClassTail !== null) {
        const classTokenRegex = /\[hc:([a-z0-9-]+)\]/gi;
        let classMatch = classTokenRegex.exec(multiClassTail[0]);
        while (classMatch !== null) {
            classes.push(normalizeTraitSlug(classMatch[1]));
            classMatch = classTokenRegex.exec(multiClassTail[0]);
        }
        return classes;
    }
    const singleClassTail = EMPOWERED_PARAM_TAIL_PATTERN.exec(textAfterMarker);
    if (singleClassTail !== null) {
        classes.push(normalizeTraitSlug(singleClassTail[1]));
    }
    return classes;
}
/**
 * Resolves the unconditional MULTI-class Empowered form
 * (`by [hc:X] and [hc:Y] (and [hc:Z]…)`), or undefined for a deferred variant. Returns ONE
 * built `gain-resource` composition PER parsed class, in printed order — the total is the sum
 * of each class's HQ count (e.g. `by [hc:ranged] and [hc:strength]` grants +1 Attack per Ranged
 * card plus +1 per Strength card in the HQ). Resolves ONLY when (a) the text immediately after
 * the marker is an anchored `by [hc:X] and [hc:Y]…` tail with two-or-more classes, AND (b) those
 * parsed classes are the line's SOLE conditions (each a consumed count param — no prefix gate,
 * no team gate, no residual). Any miss → undefined, so the caller records an unresolved marker
 * (the Honest-Partial Invariant — e.g. a prefix-gated multi-class stays hollow). Reuses the
 * WP-256 `buildEmpoweredComposition` substrate — no new keyword / value-expression / node type.
 * Does not mutate `conditions`; the caller clears the consumed params on a match.
 *
 * @param textAfterMarker - The ability text immediately following the `[keyword:Empowered]` token.
 * @param conditions - The line's conditions (heroClassMatch + requiresTeam), in hook order.
 * @returns One composition per parsed class in printed order, or undefined for a deferred variant.
 */
function tryResolveEmpoweredMultiClass(textAfterMarker, conditions) {
    // why: WP-310 — anchored multi-class tail only; the `(?:and [hc:…])+` requirement means the
    // single-class `by [hc:X]` form (owned by tryResolveEmpoweredCore) never matches here.
    const tailMatch = EMPOWERED_MULTICLASS_FULL_TAIL_PATTERN.exec(textAfterMarker);
    if (tailMatch === null) {
        return undefined;
    }
    // why: WP-310 — extract every `[hc:X]` class token from the matched tail, in printed order.
    // A fresh global RegExp avoids carrying lastIndex state on a shared module-level const.
    const classTokenRegex = /\[hc:([a-z0-9-]+)\]/gi;
    const parsedClasses = [];
    let classMatch = classTokenRegex.exec(tailMatch[0]);
    while (classMatch !== null) {
        parsedClasses.push(normalizeTraitSlug(classMatch[1]));
        classMatch = classTokenRegex.exec(tailMatch[0]);
    }
    // why: WP-310 Honest-Partial — resolve ONLY when the parsed classes are the line's SOLE
    // conditions (each a consumed count param). This generalizes tryResolveEmpoweredCore's
    // sole-condition gate to N classes: a residual condition (a leading [hc:X]:/[team:X]: prefix
    // gate, a team gate, or any non-heroClassMatch) makes the counts unequal or the type check
    // fail → a deferred variant, kept hollow.
    if (conditions.length !== parsedClasses.length) {
        return undefined;
    }
    for (const condition of conditions) {
        if (condition.type !== 'heroClassMatch') {
            return undefined;
        }
    }
    for (const parsedClass of parsedClasses) {
        if (findFirstHeroClassMatchIndex(conditions, parsedClass) === -1) {
            return undefined;
        }
    }
    // why: WP-310 / D-24098 — one buildEmpoweredComposition per parsed class, pushed in printed
    // order. The sum is commutative but the order is fixed for deterministic replay. No `.reduce()`.
    const compositions = [];
    for (const parsedClass of parsedClasses) {
        compositions.push(buildEmpoweredComposition(parsedClass));
    }
    return compositions;
}
/**
 * Detection-only resolver for the conditional-prefix class-gated Empowered form
 * (`[hc:X]: ... [keyword:Empowered] by [hc:Y]`). Returns the built composition plus the
 * NORMALIZED count color `Y` when `abilityText` matches the canonical structural shape, or
 * undefined for any non-canonical / still-deferred form. Reads `conditions` only to confirm
 * the consumed count param `heroClassMatch(Y)` is present; mutates NOTHING — the caller
 * performs the push + suppression after a canonical match. The caller guarantees the marker
 * is `empowered` (gate #1) and that the unconditional core path already returned undefined.
 *
 * @param abilityText - The full ability text line.
 * @param textAfterMarker - The text immediately following the `[keyword:Empowered]` token.
 * @param conditions - The line's conditions (heroClassMatch + requiresTeam), in hook order.
 * @returns The built composition + normalized count color, or undefined for any non-canonical shape.
 */
function tryResolveEmpoweredConditionalPrefix(abilityText, textAfterMarker, conditions) {
    // why: D-24047 gate #2 — resolve ONLY a single [keyword:Empowered] marker. The two-marker
    // choose-one (fight-or-flight) is rejected here; a condition-counting gate would mis-resolve
    // it (treating the second branch's count param as a gate) — the Honest-Partial Invariant.
    const markerMatches = abilityText.match(EMPOWERED_MARKER_COUNT_PATTERN);
    if (markerMatches === null || markerMatches.length !== 1) {
        return undefined;
    }
    // why: D-24047 gate #3 — require a leading `[hc:X]:` class-condition prefix (the retained gate).
    const prefixMatch = EMPOWERED_PREFIX_GATE_PATTERN.exec(abilityText);
    if (prefixMatch === null) {
        return undefined;
    }
    // why: D-24047 gate #4 — require the anchored fixed-color count tail `by [hc:Y]` immediately
    // after the marker (never a broad forward scan that could bind an unrelated later [hc:...]).
    const tailMatch = EMPOWERED_PARAM_TAIL_PATTERN.exec(textAfterMarker);
    if (tailMatch === null) {
        return undefined;
    }
    // why: D-24047 gate #5 — reject a `... by [hc:Y] and [hc:Z]` multi-class continuation,
    // inspecting ONLY the slice after the consumed tail.
    const textAfterTail = textAfterMarker.slice(tailMatch[0].length);
    if (EMPOWERED_MULTICLASS_TAIL_PATTERN.test(textAfterTail)) {
        return undefined;
    }
    // why: D-24047 gate #6 — reject team-gated forms. A fresh non-global test off the canonical
    // TEAM_PATTERN source (the global const carries lastIndex state; a fresh RegExp is stateless).
    if (new RegExp(TEAM_PATTERN.source).test(abilityText)) {
        return undefined;
    }
    // why: D-24047 — normalize the count color ONCE; Step 1a stored heroClassConditions
    // normalized, so the existence check below compares normalized-to-normalized. The prefix
    // class X stays the retained gate (the caller keeps it); only the count color Y is consumed.
    const countColor = normalizeTraitSlug(tailMatch[1]);
    // why: D-24047 — confirm the consumed count param is present before declaring a canonical
    // match. It always is on the canonical shape (the anchored tail guarantees Step 1a extracted
    // `[hc:Y]`); if it were somehow absent, return undefined so the caller leaves conditions
    // unchanged and records the marker unresolved — never throw, never mutate here.
    if (findFirstHeroClassMatchIndex(conditions, countColor) === -1) {
        return undefined;
    }
    return { composition: buildEmpoweredComposition(countColor), countColor };
}
/**
 * Counts how many times each [hc:X] / [team:X] token appears in a line's leading gate prefix
 * (D-24563), keyed by the condition it emits ("heroClassMatch|ranged", "requiresTeam|shield").
 *
 * @param gatePrefix - The leading gate prefix text, or '' when the line has none.
 * @returns The count of each prefix token, keyed by condition type and normalized value.
 */
function countLeadingPrefixIcons(gatePrefix) {
    const prefixCounts = new Map();
    // why: fresh RegExp instances, as Step 1a / 1b use — the shared /g pattern constants carry
    // lastIndex between calls, so reusing them could skip a token.
    for (const match of gatePrefix.matchAll(new RegExp(HERO_CLASS_PATTERN.source, 'g'))) {
        const key = `heroClassMatch|${normalizeTraitSlug(match[1])}`;
        prefixCounts.set(key, (prefixCounts.get(key) ?? 0) + 1);
    }
    for (const match of gatePrefix.matchAll(new RegExp(TEAM_PATTERN.source, 'g'))) {
        const key = `requiresTeam|${normalizeTraitSlug(match[1])}`;
        prefixCounts.set(key, (prefixCounts.get(key) ?? 0) + 1);
    }
    return prefixCounts;
}
/**
 * Collapses a line's repeated leading-prefix icon conditions into one counted condition (D-24563).
 *
 * For each (type, value) the leading gate prefix repeats m ≥ 2 times, the first emitted matching
 * condition gains `requiredMatches: m` and the next m − 1 emitted identical conditions are dropped.
 * Further identical conditions (mid-sentence tokens D-24628 did not suppress) stay as plain
 * conditions. When fewer than m were emitted (a suppression flag skipped them), they are left
 * untouched; a condition is never synthesized. Survivors keep their relative order, and a line
 * with no repeated prefix token comes back unchanged.
 *
 * // why: D-24563 — the prefix is the only source of multiplicity (the D-24628
 * LEADING_GATE_PREFIX_PATTERN match, reused, not re-implemented). Mid-sentence repeats are prose,
 * not gates: Arc Reactor's "[hc:tech]: … for each other[hc:tech] Hero" must keep needing one.
 *
 * @param gatePrefix - The line's leading gate prefix match, or '' when the line has none.
 * @param conditions - The line's emitted conditions, in hook order.
 * @returns The conditions with each repeated prefix icon collapsed to one counted condition.
 */
function collapseLeadingRepeatedIconConditions(gatePrefix, conditions) {
    const prefixCounts = countLeadingPrefixIcons(gatePrefix);
    const emittedCounts = new Map();
    for (const condition of conditions) {
        const key = `${condition.type}|${condition.value}`;
        emittedCounts.set(key, (emittedCounts.get(key) ?? 0) + 1);
    }
    const collapsed = [];
    const seenCounts = new Map();
    for (const condition of conditions) {
        const key = `${condition.type}|${condition.value}`;
        const multiplicity = prefixCounts.get(key) ?? 0;
        if (multiplicity < 2 || (emittedCounts.get(key) ?? 0) < multiplicity) {
            collapsed.push(condition);
            continue;
        }
        const seen = seenCounts.get(key) ?? 0;
        seenCounts.set(key, seen + 1);
        if (seen === 0) {
            collapsed.push({ ...condition, requiredMatches: multiplicity });
        }
        else if (seen >= multiplicity) {
            collapsed.push(condition);
        }
    }
    return collapsed;
}
/**
 * Finds the index of the first `heroClassMatch` condition whose value equals the given
 * NORMALIZED slug, or -1 when none matches. Used both to confirm the consumed Empowered
 * count param exists (detection) and to suppress exactly one occurrence of it (the caller),
 * so suppression removes a single param and retains the leading prefix gate.
 *
 * @param conditions - The line's conditions, in hook order.
 * @param normalizedValue - The normalized hero-class slug to find.
 * @returns The index of the first matching heroClassMatch condition, or -1.
 */
function findFirstHeroClassMatchIndex(conditions, normalizedValue) {
    for (let index = 0; index < conditions.length; index += 1) {
        const condition = conditions[index];
        if (condition.type === 'heroClassMatch' && condition.value === normalizedValue) {
            return index;
        }
    }
    return -1;
}
/**
 * Detection resolver for the choose-one Empowered form ("Choose one: ... [keyword:Empowered]
 * by [hc:X], or ... [keyword:Empowered] by [hc:Y]"). Returns the built composition plus the
 * extracted class list when all gates pass, or undefined for any non-canonical form. Reads
 * `abilityText` only; mutates nothing — the caller performs push and condition suppression
 * after a canonical match.
 *
 * @param abilityText - The full ability text line.
 * @returns The built composition + extracted class list, or undefined for any non-canonical shape.
 */
function tryResolveEmpoweredChooseOneLine(abilityText) {
    // why: D-24063 — choose-one resolved as oracle-max of the two enumerated classes; one
    // composition for the whole line. Prefix gate limits this path to "Choose one:" lines only,
    // leaving the general empowered dispatch entirely unaffected for all other card texts.
    if (!EMPOWERED_CHOOSE_ONE_PREFIX_PATTERN.test(abilityText)) {
        return undefined;
    }
    // The canonical choose-one form has exactly 2 [keyword:Empowered] markers.
    const markerMatches = abilityText.match(EMPOWERED_MARKER_COUNT_PATTERN);
    if (markerMatches === null || markerMatches.length !== 2) {
        return undefined;
    }
    // Extract each `[keyword:Empowered] by [hc:X]` class tail in source order.
    // why: fresh RegExp per call (same lastIndex-safe pattern as heroClassRegex / teamRegex).
    const extractedClasses = [];
    const classTailRegex = new RegExp(EMPOWERED_CHOOSE_ONE_CLASS_TAIL_PATTERN.source, 'gi');
    let classTailMatch = classTailRegex.exec(abilityText);
    while (classTailMatch !== null) {
        extractedClasses.push(normalizeTraitSlug(classTailMatch[1]));
        classTailMatch = classTailRegex.exec(abilityText);
    }
    // Must have found exactly one class tail per marker; any other count is non-canonical.
    if (extractedClasses.length !== 2) {
        return undefined;
    }
    return {
        composition: buildEmpoweredChooseOneComposition(extractedClasses),
        classes: extractedClasses,
    };
}
/**
 * Detection resolver for the draw-or-empowered choose-one form ("Choose one: Draw a card, or you
 * get [keyword:Empowered] by [hc:X]" — One-Hit Wonder). Returns the parsed empowered hero class
 * when all gates pass, or undefined for any non-canonical form. Reads `abilityText` only; mutates
 * nothing — the caller records the keyword + class and suppresses the per-token empowered dispatch
 * after a canonical match.
 *
 * Gated strictly so it never claims the WP-283 two-empowered choose-one (fight-or-flight, two
 * markers) nor the core empowered path (no "Choose one:" prefix): (1) a "Choose one:" prefix,
 * (2) a "Draw a card" draw option, (3) exactly ONE [keyword:Empowered] marker, (4) exactly one
 * `[keyword:Empowered] by [hc:X]` class tail. Any miss → undefined. (D-24069)
 *
 * @param abilityText - The full ability text line.
 * @returns The normalized empowered hero class, or undefined for any non-canonical shape.
 */
// why: WP-675 / D-24490 + WP-679 / D-24495 — non-global marker extractors for choose-one
// options. Each per-count option carries `[keyword:(attack|recruit)-per-count:<source>:<magnitude>[:<perEach>]]`;
// the optional 4th segment is the "for each N" divisor (WP-679; absent ≡ 1). The resource is
// the marker family; the source + per-unit rate + optional divisor are the segments.
const RECRUIT_PER_COUNT_MARKER_PATTERN = /\[keyword:recruit-per-count:([a-z][a-z-]*):(\d+)(?::(\d+))?\]/;
const ATTACK_PER_COUNT_MARKER_PATTERN = /\[keyword:attack-per-count:([a-z][a-z-]*):(\d+)(?::(\d+))?\]/;
// why: WP-679 / D-24495 — the two Undercover source-shape option markers (WP-678 keywords).
const UNDERCOVER_HAND_MARKER_PATTERN = /\[keyword:undercover-hand-shield-hero\]/;
const UNDERCOVER_OFFICER_MARKER_PATTERN = /\[keyword:undercover-officer-stack\]/;
// why: D-24696 — the two single-line choose-one option markers of ca75 Spy Network ("Choose
// one: Gain a S.H.I.E.L.D. Officer, or you get +2[icon:attack]."): a gain-an-Officer option
// marker and the flat attack marker [keyword:attack:N] (already an allowlisted grant token,
// WP-681). Both are read ONLY inside tryResolveCountScaledChooseOneLine, which requires the
// "Choose one:" prefix + ≥2 recognized markers, so a flat [keyword:attack:N] on any other line
// is untouched. `attack-per-count` never matches (its segment after "attack" is "-per-count").
const GAIN_OFFICER_OPTION_MARKER_PATTERN = /\[keyword:gain-officer\]/;
const FLAT_ATTACK_OPTION_MARKER_PATTERN = /\[keyword:attack:([1-9]\d*)\]/;
// why: WP-856 / D-24792 — the flat-recruit sibling of the flat attack option (Possessed by
// Brother's Spirit's "+4[icon:recruit] or +4[icon:attack]"), read only inside
// tryResolveCountScaledChooseOneLine. `recruit-per-count` never matches (its segment after
// "recruit" is "-per-count"). No line carried [keyword:recruit:N] before this WP.
const FLAT_RECRUIT_OPTION_MARKER_PATTERN = /\[keyword:recruit:([1-9]\d*)\]/;
// why: WP-856 / D-24792 — the second entry form of the choose-one: a printed "+N[icon:X] or
// +N[icon:Y]" reward (no "Choose one:" prefix). Only two hero lines print it (this card and
// an unmarked cosm line), and the ≥2-option-marker rule still applies, so an unmarked line
// parses exactly as before.
const FLAT_RESOURCE_OR_PATTERN = /\+[1-9]\d*\[icon:(?:recruit|attack)\] or \+[1-9]\d*\[icon:(?:recruit|attack)\]/;
// why: WP-675 / D-24490 — a standalone "Choose one:" header entry (the multi-line choose-one form).
const STANDALONE_CHOOSE_ONE_HEADER_PATTERN = /^\s*Choose one\s*:\s*$/i;
// why: WP-675 / D-24490 — an option bullet line under a standalone "Choose one:" header.
const CHOOSE_ONE_OPTION_BULLET_PATTERN = /^\s*-\s/;
// why: WP-679 / D-24495 — the RECOGNIZED option-marker families that GATE coalescing: a
// per-count marker OR an Undercover source-shape marker. An option bullet with a recognized
// marker is one this pipeline can fold into a heterogeneous choice.
const RECOGNIZED_CHOOSE_ONE_OPTION_MARKER_PATTERN = /\[keyword:(?:attack|recruit)-per-count:|\[keyword:undercover-(?:hand-shield-hero|officer-stack)\]/;
/**
 * Coalesces a multi-line "Choose one:" group into ONE synthetic ability line (WP-675 /
 * D-24490; generalized to heterogeneous options WP-679 / D-24495).
 *
 * A standalone "Choose one:" entry followed by "- " option bullets is separate `abilities[]`
 * entries; parsed independently they would emit the options as independent hooks (N grants,
 * not a choice). This joins the header + its option bullets into one string so the single-line
 * choose-one pre-pass folds them into one choice.
 *
 * GATED (strict superset — zero regression): coalesces ONLY when there are ≥2 option bullets
 * and EVERY bullet carries a RECOGNIZED option marker (a per-count marker OR an Undercover
 * source-shape marker). A multi-line choose-one with any unrecognized bullet is left untouched
 * — it parses exactly as before. Other abilities on the card are preserved in place.
 *
 * @param abilities - The card's raw ability lines (with markers already applied).
 * @returns The ability lines with any recognized choose-one group joined into one.
 */
function coalesceCountScaledChooseOne(abilities) {
    const headerIndex = abilities.findIndex((line) => STANDALONE_CHOOSE_ONE_HEADER_PATTERN.test(line));
    if (headerIndex === -1) {
        return abilities;
    }
    const optionLines = [];
    let nextIndex = headerIndex + 1;
    while (nextIndex < abilities.length && CHOOSE_ONE_OPTION_BULLET_PATTERN.test(abilities[nextIndex])) {
        optionLines.push(abilities[nextIndex]);
        nextIndex++;
    }
    // why: a choose-one needs ≥2 options; the gate requires EVERY bullet to carry a recognized
    // option marker (per-count or Undercover). A choose-one with an unrecognized bullet passes
    // through untouched — the strict-superset guarantee (WP-679): only vnom + the two shld cards
    // newly coalesce; every other multi-line choose-one parses exactly as before.
    const allRecognized = optionLines.every((line) => RECOGNIZED_CHOOSE_ONE_OPTION_MARKER_PATTERN.test(line));
    if (optionLines.length < 2 || !allRecognized) {
        return abilities;
    }
    const joined = [abilities[headerIndex], ...optionLines].join(' ');
    return [...abilities.slice(0, headerIndex), joined, ...abilities.slice(nextIndex)];
}
// why: WP-859 / D-24795 — the separator FEAST_THEN_TRANSFORM_CARDS lines print between their Feast
// and their conditional Transform ("You may [keyword:Feast]. Then, if …").
const FEAST_THEN_TRANSFORM_SEPARATOR = ' Then, ';
/**
 * Splits a "Feast. Then, if …, Transform" line into a Feast line and a Transform line (WP-859 /
 * D-24795).
 *
 * A condition gates its whole line's hook, so on one line the Transform's condition would also
 * gate the Feast whose KO satisfies it. Each line holding both `[keyword:Feast]` and
 * `[keyword:Transform]` is replaced by `"<text before>"` and `"Then, <text after>"`, split at
 * the first " Then, "; the condition marker appended to the printed line lands on the Transform
 * half. A line without the separator is kept whole. Parse-only (the coalesceCountScaledChooseOne
 * precedent): the card's display text is untouched. Called only for FEAST_THEN_TRANSFORM_CARDS.
 *
 * @param abilityLines - The card's ability lines (with markers already applied).
 * @returns The ability lines with each Feast-then-Transform line split in two.
 */
function splitFeastThenTransformLine(abilityLines) {
    const splitLines = [];
    for (const abilityLine of abilityLines) {
        const isFeastThenTransform = abilityLine.includes('[keyword:Feast]') && abilityLine.includes('[keyword:Transform]');
        const separatorIndex = abilityLine.indexOf(FEAST_THEN_TRANSFORM_SEPARATOR);
        if (!isFeastThenTransform || separatorIndex === -1) {
            splitLines.push(abilityLine);
            continue;
        }
        splitLines.push(abilityLine.slice(0, separatorIndex));
        splitLines.push(`Then, ${abilityLine.slice(separatorIndex + FEAST_THEN_TRANSFORM_SEPARATOR.length)}`);
    }
    return splitLines;
}
/**
 * Resolves the multi-line "Choose one:" form to a heterogeneous option list (WP-675 /
 * D-24490; generalized WP-679 / D-24495).
 *
 * Handles both the homogeneous count-scaled shape (vnom Symbiotic Adaptation — two per-count
 * options) and the MIXED shape (shld approve-orbital-strike / spymaster — an Undercover send
 * + a count-scaled attack). Coalesced (buildHeroAbilityHooks) into one line first. Also the
 * SINGLE-line form (D-24696 — ca75 Spy Network "Choose one: Gain a S.H.I.E.L.D. Officer, or you
 * get +2[icon:attack]." carrying [keyword:gain-officer] + [keyword:attack:2]), which needs no
 * coalescing.
 *
 * Gated strictly so it never claims the Empowered / draw-or-empowered choose-ones: (1) a
 * "Choose one:" prefix, (2) ≥2 RECOGNIZED option markers (per-count with a valid source, or
 * an Undercover source-shape). Options are returned in PRINTED order (by marker position).
 * Any miss → undefined. Reads `abilityText` only.
 *
 * @param abilityText - The (coalesced) full ability text line.
 * @returns The options (≥2, in printed order), or undefined for any non-choose-one shape.
 */
function tryResolveCountScaledChooseOneLine(abilityText) {
    // why: gate #1 — the "Choose one:" prefix (reuses the choose-one prefix const), or the
    // printed "+N[icon:X] or +N[icon:Y]" form (WP-856 / D-24792).
    if (!EMPOWERED_CHOOSE_ONE_PREFIX_PATTERN.test(abilityText) &&
        !FLAT_RESOURCE_OR_PATTERN.test(abilityText)) {
        return undefined;
    }
    const candidates = [];
    const recruitMatch = RECRUIT_PER_COUNT_MARKER_PATTERN.exec(abilityText);
    if (recruitMatch !== null && isValidHeroCountSource(recruitMatch[1])) {
        candidates.push({
            index: recruitMatch.index,
            option: buildCountScaledOption('recruit', recruitMatch),
        });
    }
    const attackMatch = ATTACK_PER_COUNT_MARKER_PATTERN.exec(abilityText);
    if (attackMatch !== null && isValidHeroCountSource(attackMatch[1])) {
        candidates.push({
            index: attackMatch.index,
            option: buildCountScaledOption('attack', attackMatch),
        });
    }
    const undercoverHandMatch = UNDERCOVER_HAND_MARKER_PATTERN.exec(abilityText);
    if (undercoverHandMatch !== null) {
        candidates.push({
            index: undercoverHandMatch.index,
            option: { kind: 'undercover', source: 'hand-shield-hero' },
        });
    }
    const undercoverOfficerMatch = UNDERCOVER_OFFICER_MARKER_PATTERN.exec(abilityText);
    if (undercoverOfficerMatch !== null) {
        candidates.push({
            index: undercoverOfficerMatch.index,
            option: { kind: 'undercover', source: 'officer-stack' },
        });
    }
    const gainOfficerMatch = GAIN_OFFICER_OPTION_MARKER_PATTERN.exec(abilityText);
    if (gainOfficerMatch !== null) {
        candidates.push({
            index: gainOfficerMatch.index,
            option: { kind: 'gain-officer' },
        });
    }
    const flatAttackMatch = FLAT_ATTACK_OPTION_MARKER_PATTERN.exec(abilityText);
    if (flatAttackMatch !== null) {
        candidates.push({
            index: flatAttackMatch.index,
            option: { kind: 'flat-attack', magnitude: parseInt(flatAttackMatch[1], 10) },
        });
    }
    // why: WP-856 / D-24792 — the flat recruit option ("+4[icon:recruit] or …").
    const flatRecruitMatch = FLAT_RECRUIT_OPTION_MARKER_PATTERN.exec(abilityText);
    if (flatRecruitMatch !== null) {
        candidates.push({
            index: flatRecruitMatch.index,
            option: { kind: 'flat-recruit', magnitude: parseInt(flatRecruitMatch[1], 10) },
        });
    }
    // why: require ≥2 options — a single recognized marker is a plain effect line, not a choose-one.
    if (candidates.length < 2) {
        return undefined;
    }
    // why: printed order — sort by the marker's position so the UI shows the options as printed.
    candidates.sort((first, second) => first.index - second.index);
    return { options: candidates.map((candidate) => candidate.option) };
}
/**
 * Builds a count-scaled choose-one option from a per-count marker match (WP-679 / D-24495).
 *
 * The 3rd segment is the per-unit magnitude; the optional 4th is the `perEach` divisor
 * ("for each N"; absent ≡ 1).
 *
 * @param resource - The resource family ('attack' / 'recruit').
 * @param match - The per-count marker regex match (groups: source, magnitude, perEach?).
 * @returns The count-scaled option descriptor.
 */
function buildCountScaledOption(resource, match) {
    const perEachRaw = match[3];
    const option = {
        kind: 'count-scaled',
        resource,
        countSource: match[1],
        magnitude: parseInt(match[2], 10),
    };
    // why: only attach perEach when the marker carries the 4th segment — keeps vnom's 3-segment
    // markers producing an option with no perEach (≡ divisor 1), byte-identical to WP-675.
    if (perEachRaw !== undefined) {
        option.perEach = parseInt(perEachRaw, 10);
    }
    return option;
}
function tryResolveDrawOrEmpoweredLine(abilityText) {
    // why: D-24069 gate #1 — the "Choose one:" prefix (reuses the WP-283 prefix const); limits this
    // path to choose-one lines, leaving every other card text's empowered dispatch unaffected.
    if (!EMPOWERED_CHOOSE_ONE_PREFIX_PATTERN.test(abilityText)) {
        return undefined;
    }
    // why: D-24069 gate #2 — a printed "Draw a card" option separates this form from the WP-283
    // two-empowered choose-one (two Empowered halves, no draw).
    if (!DRAW_A_CARD_PATTERN.test(abilityText)) {
        return undefined;
    }
    // why: D-24069 gate #3 — EXACTLY ONE [keyword:Empowered] marker. Two markers is the WP-283
    // fight-or-flight shape (claimed by tryResolveEmpoweredChooseOneLine); zero is not this form.
    // String.match with the global const ignores lastIndex (stateless), mirroring the choose-one helper.
    const markerMatches = abilityText.match(EMPOWERED_MARKER_COUNT_PATTERN);
    if (markerMatches === null || markerMatches.length !== 1) {
        return undefined;
    }
    // why: D-24069 gate #4 — extract the single `[keyword:Empowered] by [hc:X]` class tail (fresh
    // RegExp per call, the lastIndex-safe pattern used by heroClassRegex / the choose-one helper).
    const classTailRegex = new RegExp(EMPOWERED_CHOOSE_ONE_CLASS_TAIL_PATTERN.source, 'gi');
    const extractedClasses = [];
    let classTailMatch = classTailRegex.exec(abilityText);
    while (classTailMatch !== null) {
        extractedClasses.push(normalizeTraitSlug(classTailMatch[1]));
        classTailMatch = classTailRegex.exec(abilityText);
    }
    if (extractedClasses.length !== 1) {
        return undefined;
    }
    return { empoweredClass: extractedClasses[0] };
}
/**
 * Resolver for the free-choice Empowered form ("by the color of your choice" — no
 * `[hc:CLASS]` literal in the tail after the marker). Returns a built free-choice
 * composition when `EMPOWERED_PARAM_TAIL_PATTERN` does NOT match `textAfterMarker`, and
 * undefined when it DOES match (that case is the core path's domain — a literal class IS
 * present). Called after both `tryResolveEmpoweredCore` and
 * `tryResolveEmpoweredConditionalPrefix` already returned undefined.
 *
 * @param textAfterMarker - The ability text immediately following the `[keyword:Empowered]` token.
 * @returns The built free-choice composition, or undefined when a class literal tail is present.
 */
function tryResolveEmpoweredFreeChoice(textAfterMarker, conditions) {
    // why: D-24063 — guard: return undefined when EMPOWERED_PARAM_TAIL_PATTERN matches —
    // a literal `by [hc:CLASS]` tail means the core path's domain; free-choice applies ONLY
    // when no class literal follows the marker ("by the color of your choice").
    if (EMPOWERED_PARAM_TAIL_PATTERN.test(textAfterMarker)) {
        return undefined;
    }
    // why: D-24065 — guard: return undefined when the revealed-classes dynamic pattern matches
    // the text after the marker. The dynamic form ("by the Hero Classes of the card you revealed
    // this way") has no [hc:X] literal and no conditions, so without this guard the free-choice
    // path would capture it before tryResolveEmpoweredDynamic can. The dynamic resolver is the
    // last fallback; this guard lets it reach that position.
    if (EMPOWERED_REVEALED_CLASSES_PATTERN.test(textAfterMarker)) {
        return undefined;
    }
    // why: D-24063 — free-choice applies ONLY when no [hc:X] token appears ANYWHERE on the line.
    // A non-empty conditions array means at least one heroClassMatch was extracted (prefix gate,
    // detached literal, or non-anchored class reference) — that form is not the simple free-choice,
    // so defer to keep Honest-Partial invariant intact.
    if (conditions.length > 0) {
        return undefined;
    }
    return buildEmpoweredFreeChoiceComposition();
}
/**
 * Resolver for the dynamic Empowered form ("by the Hero Classes of the card you revealed
 * this way" — no `[hc:CLASS]` literal; class is determined at runtime from the top deck card).
 * Returns a built dynamic composition when `EMPOWERED_REVEALED_CLASSES_PATTERN` matches
 * `textAfterMarker`, and undefined otherwise. Called as the LAST fallback before
 * `unresolvedMarkers.push`, after core, conditional-prefix, free-choice, and choose-one
 * all returned undefined.
 *
 * @param textAfterMarker - The ability text immediately following the `[keyword:Empowered]` token.
 * @returns The built dynamic composition, or undefined when the pattern does not match.
 */
// why: D-24065 — recognizes "classes of the card you revealed" phrasing; last fallback
// before unresolved; cards whose empowered form refers to a runtime-dynamic class (cross-the-multiverse).
function tryResolveEmpoweredDynamic(textAfterMarker) {
    if (!EMPOWERED_REVEALED_CLASSES_PATTERN.test(textAfterMarker)) {
        return undefined;
    }
    return buildDynamicEmpoweredComposition();
}
// ---------------------------------------------------------------------------
// Investigate criterion parsing (static-criterion subset; WP-564 / D-24373)
// ---------------------------------------------------------------------------
/**
 * Resolves the reveal-from-hand criterion from a full ability line, or undefined when the
 * line has no `[keyword:reveal-from-hand]` or `[keyword:reveal-or-gain-wound-each]` marker or
 * carries no `[hc:X]` / `[team:X]` token to read as the criterion (WP-659 / D-24470;
 * WP-843 / D-24777).
 *
 * The printed form is "Each player may reveal another [team:X]/[hc:X] Hero. …" — a SINGLE
 * criterion (not the OR-combined investigate list), so the resolver reads the first such
 * token into one InvestigateCriterion. `[team:X]` is tried before `[hc:X]` only to pick a
 * deterministic winner if a line ever carried both; Psychic Link carries exactly one team
 * token. Returns undefined (leaving the line an honest hollow) when the marker is present
 * but no class/team token is found.
 *
 * @param abilityText - The full ability text line.
 * @returns The single reveal criterion, or undefined for a non-reveal-from-hand line.
 */
function tryResolveRevealFromHandCriterion(abilityText) {
    // why: WP-843 / D-24777 — Crazed Rampage's "reveals another [hc:strength] Hero or gains a
    // Wound" reads its criterion exactly as Psychic Link does, so accepting the marker here makes
    // lineHasRevealFromHand true and the Step 1a / 1b suppression drop the spurious gate.
    const hasRevealMarker = REVEAL_FROM_HAND_MARKER_PATTERN.test(abilityText) ||
        REVEAL_OR_GAIN_WOUND_EACH_MARKER_PATTERN.test(abilityText);
    if (!hasRevealMarker) {
        return undefined;
    }
    const teamMatch = REVEAL_FROM_HAND_TEAM_PATTERN.exec(abilityText);
    if (teamMatch !== null) {
        return { kind: 'team', team: normalizeTraitSlug(teamMatch[1]) };
    }
    const heroClassMatch = REVEAL_FROM_HAND_HC_PATTERN.exec(abilityText);
    if (heroClassMatch !== null) {
        return { kind: 'hero-class', heroClass: normalizeTraitSlug(heroClassMatch[1]) };
    }
    // why: the marker is present but no [hc:X]/[team:X] token — leave the line an honest hollow
    // (Step 2 records `reveal-from-hand` as an unresolved marker), never a criterion-less effect.
    return undefined;
}
/**
 * Resolves the static Investigate criterion from a full ability line, or undefined
 * when the line has no `[keyword:Investigate]` marker or its criterion is a deferred
 * variant (choose-a-criterion, other-zone, name-match, disposition, modifier,
 * compose-with-Teleport). Reads only the text AFTER the marker.
 *
 * Whitelist by design (the Honest-Partial Invariant): a resolved line gets the
 * `investigate` keyword + descriptor; an unresolved line records `investigate` as an
 * unresolved marker so it stays hollow (parse-unrecognized), exactly as before this WP.
 *
 * @param abilityText - The full ability text line.
 * @returns The parsed criteria (INCLUSIVE OR), or undefined for a deferred / non-Investigate line.
 */
function tryResolveInvestigateFromLine(abilityText) {
    const markerMatch = INVESTIGATE_MARKER_PATTERN.exec(abilityText);
    if (markerMatch === null) {
        return undefined;
    }
    const textAfterMarker = abilityText.slice(markerMatch.index + markerMatch[0].length).trim();
    return tryResolveInvestigateCriteria(textAfterMarker);
}
/**
 * Parses the trimmed criterion text (the part after `[keyword:Investigate]`) into a
 * static criterion list, or undefined for any non-static / deferred form. Each pattern
 * is anchored `^…$`, so a trailing disposition / modifier clause fails to match.
 *
 * @param criterionText - The trimmed text following the Investigate marker.
 * @returns The parsed criteria (INCLUSIVE OR), or undefined when the form is not a static criterion.
 */
function tryResolveInvestigateCriteria(criterionText) {
    const iconMatch = INVESTIGATE_ICON_PATTERN.exec(criterionText);
    if (iconMatch !== null) {
        const icon = iconMatch[1].toLowerCase();
        // why: the capture group is anchored to `attack|recruit`, so this narrows safely.
        return [{ kind: 'icon', icon: icon === 'attack' ? 'attack' : 'recruit' }];
    }
    const costLteMatch = INVESTIGATE_COST_LTE_PATTERN.exec(criterionText);
    if (costLteMatch !== null) {
        return [{ kind: 'cost', comparison: 'lte', value: parseInt(costLteMatch[1], 10) }];
    }
    const costGteMatch = INVESTIGATE_COST_GTE_PATTERN.exec(criterionText);
    if (costGteMatch !== null) {
        return [{ kind: 'cost', comparison: 'gte', value: parseInt(costGteMatch[1], 10) }];
    }
    const costExactMatch = INVESTIGATE_COST_EXACT_PATTERN.exec(criterionText);
    if (costExactMatch !== null) {
        return [{ kind: 'cost', comparison: 'eq', value: parseInt(costExactMatch[1], 10) }];
    }
    const hcLeadingMatch = INVESTIGATE_HC_LEADING_PATTERN.exec(criterionText);
    if (hcLeadingMatch !== null) {
        return [{ kind: 'hero-class', heroClass: normalizeTraitSlug(hcLeadingMatch[1]) }];
    }
    const thatsMatch = INVESTIGATE_THATS_PATTERN.exec(criterionText);
    if (thatsMatch !== null) {
        return parseInvestigateThatsClause(thatsMatch[1]);
    }
    return undefined;
}
/**
 * Parses the inner clause of a "for a card that's <clause>." form into a criterion
 * list, or undefined when any part is not a lone `[hc:X]` / `[team:X]` token. Parts
 * are split on the printed " and/or " connector and combined with INCLUSIVE OR — a
 * card matching EITHER clause qualifies (D-24373).
 *
 * @param clause - The text between "that's " and the terminal period.
 * @returns The parsed criteria (INCLUSIVE OR), or undefined for a non-token clause.
 */
function parseInvestigateThatsClause(clause) {
    const criteria = [];
    // why: D-24373 — the printed connector is exactly " and/or "; a card satisfying
    // either clause qualifies (inclusive OR). A single-token clause (no connector) splits
    // to one part and is accepted too. Any non-token part (e.g. " or ", free text) voids
    // the whole clause → the line stays deferred (unresolved marker).
    for (const part of clause.split(' and/or ')) {
        const trimmedPart = part.trim();
        const hcMatch = INVESTIGATE_CLAUSE_HC_PATTERN.exec(trimmedPart);
        if (hcMatch !== null) {
            criteria.push({ kind: 'hero-class', heroClass: normalizeTraitSlug(hcMatch[1]) });
            continue;
        }
        const teamMatch = INVESTIGATE_CLAUSE_TEAM_PATTERN.exec(trimmedPart);
        if (teamMatch !== null) {
            criteria.push({ kind: 'team', team: normalizeTraitSlug(teamMatch[1]) });
            continue;
        }
        return undefined;
    }
    return criteria.length > 0 ? criteria : undefined;
}
// ---------------------------------------------------------------------------
// Parameterized reveal token parsing (forward-compat; D-24024)
// ---------------------------------------------------------------------------
/**
 * Parses a non-negative integer from a parameterized reveal token segment.
 *
 * @param value - The digit substring (e.g. the `2` of `cost-lte-2`).
 * @returns The parsed integer, or null when the segment is not all digits.
 */
function parseRevealTokenInteger(value) {
    if (!/^\d+$/.test(value)) {
        return null;
    }
    return parseInt(value, 10);
}
/**
 * Parses a parameterized reveal predicate segment into a RevealPredicate.
 *
 * Grammar: `always` | `cost-zero` | `cost-odd` | `cost-lte-<n>` | `cost-gte-<n>`.
 *
 * @param token - The predicate segment of a `[keyword:reveal:...]` token.
 * @returns The RevealPredicate, or null when the segment is unrecognized.
 */
function parseRevealPredicateToken(token) {
    if (token === 'always') {
        return { kind: 'always' };
    }
    if (token === 'cost-zero') {
        return { kind: 'cost-zero' };
    }
    if (token === 'cost-odd') {
        return { kind: 'cost-odd' };
    }
    if (token.startsWith('cost-lte-')) {
        const threshold = parseRevealTokenInteger(token.slice('cost-lte-'.length));
        return threshold === null ? null : { kind: 'cost-lte', threshold };
    }
    if (token.startsWith('cost-gte-')) {
        const threshold = parseRevealTokenInteger(token.slice('cost-gte-'.length));
        return threshold === null ? null : { kind: 'cost-gte', threshold };
    }
    // why: WP-729 / D-24550 / D-24281 — trait predicates `team-<slug>` / `hc-<slug>`
    // (mirroring the villain reveal-or-wound:<team|hc>:<value> convention). `hc` maps to
    // the 'hero-class' kind at parse; the slug is normalizeTraitSlug-normalized and matched
    // against G.cardTraits by revealPredicateMatches — never a hardcoded literal.
    if (token.startsWith('team-')) {
        const traitValue = normalizeTraitSlug(token.slice('team-'.length));
        return traitValue.length === 0 ? null : { kind: 'team', traitValue };
    }
    if (token.startsWith('hc-')) {
        const traitValue = normalizeTraitSlug(token.slice('hc-'.length));
        return traitValue.length === 0 ? null : { kind: 'hero-class', traitValue };
    }
    return null;
}
/**
 * Parses a parameterized reveal action segment into a RevealAction.
 *
 * Grammar: `draw` | `ko` | `discard` | `attack-by-cost` | `attack-fixed-<n>` |
 * `choose-discard-or-return`.
 *
 * @param token - One action segment (the `+`-joined parts are split by the caller).
 * @returns The RevealAction, or null when the segment is unrecognized.
 */
function parseRevealActionToken(token) {
    if (token === 'draw') {
        return { kind: 'draw' };
    }
    if (token === 'ko') {
        return { kind: 'ko' };
    }
    if (token === 'discard') {
        return { kind: 'discard' };
    }
    if (token === 'attack-by-cost') {
        return { kind: 'attack-by-cost' };
    }
    if (token === 'choose-discard-or-return') {
        return { kind: 'choose-discard-or-return' };
    }
    if (token.startsWith('attack-fixed-')) {
        const amount = parseRevealTokenInteger(token.slice('attack-fixed-'.length));
        return amount === null ? null : { kind: 'attack-fixed', amount };
    }
    return null;
}
/**
 * Returns whether an ability line's reveal grants a fixed attack from its matched
 * rule — the legacy `reveal-ko-attack` keyword, or a parameterized reveal rule that
 * carries an `attack-fixed` action. Such a line's printed attack icon is the rule's
 * own conditional grant, not a separate flat attack (D-24582).
 *
 * @param keywords - The line's normalized keywords.
 * @param parameterizedRevealRules - The line's parameterized reveal rules (Step 2g).
 * @returns Whether the printed attack icon must be suppressed.
 */
function lineHasRevealFixedAttack(keywords, parameterizedRevealRules) {
    for (const keyword of keywords) {
        if (keyword === 'reveal-ko-attack') {
            return true;
        }
    }
    for (const rule of parameterizedRevealRules) {
        for (const action of rule.actions) {
            if (action.kind === 'attack-fixed') {
                return true;
            }
        }
    }
    return false;
}
/**
 * Parses one parameterized reveal token (one RevealRule) from its captured
 * segments. Returns null when the predicate or any action segment is malformed —
 * a malformed token voids that one rule (safe-skip, no throw).
 *
 * @param predicateToken - The predicate segment.
 * @param actionsToken - The `+`-joined actions segment.
 * @param continueToken - The optional `continue` flag (undefined when absent).
 * @returns The RevealRule, or null when any segment is malformed.
 */
function parseRevealRuleToken(predicateToken, actionsToken, continueToken) {
    const predicate = parseRevealPredicateToken(predicateToken);
    if (predicate === null) {
        return null;
    }
    const actions = [];
    for (const actionToken of actionsToken.split('+')) {
        const action = parseRevealActionToken(actionToken);
        if (action === null) {
            return null;
        }
        actions.push(action);
    }
    if (actions.length === 0) {
        return null;
    }
    const rule = { predicate, actions };
    if (continueToken === 'continue') {
        rule.continue = true;
    }
    return rule;
}
/**
 * Checks if a string is a valid HeroCountSource.
 *
 * Used to gate the count-scaled attack token: only a source in
 * HERO_COUNT_SOURCES produces an 'attack-per-count' effect; an unrecognized
 * source is ignored (no effect emitted).
 */
function isValidHeroCountSource(value) {
    for (const source of HERO_COUNT_SOURCES) {
        if (source === value) {
            return true;
        }
    }
    return false;
}
/**
 * Removes duplicate keywords while preserving order.
 */
function deduplicateKeywords(keywords) {
    const seen = new Set();
    const result = [];
    for (const keyword of keywords) {
        if (!seen.has(keyword)) {
            seen.add(keyword);
            result.push(keyword);
        }
    }
    return result;
}
/**
 * Sets the printed cost floor on a line's "another [team:X] Hero that costs N or more" gate
 * (WP-825 / D-24758).
 *
 * Finds the first requiresTeam condition naming the clause's team and sets its minimumCost to N
 * when N is 1 or more. A line without the clause, or with N = 0, is left untouched. Mutates the
 * freshly built conditions array in place (setup-time, before the hook is stored).
 *
 * @param abilityText - The ability line.
 * @param conditions - The line's conditions, as built by Step 1 / 1b.
 */
function applyAnotherTeamHeroCostFloor(abilityText, conditions) {
    const costFloorMatch = ANOTHER_TEAM_HERO_COST_FLOOR_PATTERN.exec(abilityText);
    if (costFloorMatch === null) {
        return;
    }
    const teamSlug = normalizeTraitSlug(costFloorMatch[1]);
    const minimumCost = parseInt(costFloorMatch[2], 10);
    if (!Number.isInteger(minimumCost) || minimumCost < 1) {
        return;
    }
    for (const condition of conditions) {
        if (condition.type === 'requiresTeam' && condition.value === teamSlug) {
            condition.minimumCost = minimumCost;
            return;
        }
    }
}
// ---------------------------------------------------------------------------
// Hero slug extraction (same pattern as economy.logic.ts)
// ---------------------------------------------------------------------------
/**
 * Extracts the hero slug from a hero FlatCard key.
 *
 * Key format: {setAbbr}-hero-{heroSlug}-{slot}
 * The heroSlug is between "hero-" and the last "-{slot}" segment.
 *
 * Promoted to a named export for WP-113 — the validator's
 * `buildKnownHeroQualifiedIds` consumes this as the single source of
 * truth for hero-slug grammar (Class A: flat-card-key decoder). Inventing
 * a parallel decoder is contract drift per D-10014 Authority Lock.
 */
// why: D-10014 — single source of truth — flat-card-key decoder.
export function extractHeroSlug(card) {
    const prefix = `${card.setAbbr}-hero-`;
    if (!card.key.startsWith(prefix)) {
        return '';
    }
    // why: slot is always the last segment after the final "-"
    const afterPrefix = card.key.slice(prefix.length);
    const lastDashIndex = afterPrefix.lastIndexOf('-');
    if (lastDashIndex === -1) {
        return '';
    }
    return afterPrefix.slice(0, lastDashIndex);
}
/**
 * Parses a set-qualified ID `<setAbbr>/<slug>` into its components.
 *
 * Returns null on malformed input. Locally duplicated per WP-113 §6 step 1.
 */
// why: D-10014 — duplicated locally to avoid a circular import between
// builders and matchSetup.validate.ts.
function parseQualifiedId(input) {
    if (typeof input !== 'string' || input.length === 0)
        return null;
    if (input !== input.trim())
        return null;
    const slashIndex = input.indexOf('/');
    if (slashIndex === -1)
        return null;
    if (input.indexOf('/', slashIndex + 1) !== -1)
        return null;
    const setAbbr = input.slice(0, slashIndex);
    const slug = input.slice(slashIndex + 1);
    if (setAbbr.length === 0 || slug.length === 0)
        return null;
    return { setAbbr, slug };
}
// ---------------------------------------------------------------------------
// Runtime type guard
// ---------------------------------------------------------------------------
/**
 * Runtime type guard for HeroAbilityRegistryReader.
 *
 * Returns true if the registry object has the required listCards method
 * and returns cards with abilities arrays.
 */
// why: D-10014 — orchestration-side diagnostic detection seam. The
// orchestration layer (buildInitialGameState) imports this guard to detect
// registry-reader interface mismatches and emit G.messages diagnostics.
export function isHeroAbilityRegistryReader(registry) {
    if (!registry || typeof registry !== 'object')
        return false;
    const candidate = registry;
    return typeof candidate.listCards === 'function';
}
// ---------------------------------------------------------------------------
// buildHeroAbilityHooks — setup-time builder
// ---------------------------------------------------------------------------
// why: setup-time-only pattern — same as buildVillainDeck,
// buildCardStats. Registry data is consumed once and never accessed
// at runtime.
/**
 * Fuses an allowlisted Venomverse card's "Digest N / Indigestion" lines into one
 * digest-indigestion hook (WP-735 / D-24555).
 *
 * Reads the printed Digest threshold, parses each branch line's inline effects via the
 * shared parseAbilityText, reads the optional "Instead … both" upgrade condition, and
 * returns the single fused hook plus the set of ability-line indices it consumed (so the
 * caller skips them). Returns undefined when no Digest line is present (defensive — every
 * allowlisted card has one).
 *
 * @param cardId - The played hero card's CardExtId (the hook's cardId).
 * @param abilityLines - The card's ability lines (post-coalesce).
 * @returns The fused hook + consumed line indices, or undefined when no Digest line is found.
 */
function buildDigestIndigestionFusion(cardId, abilityLines) {
    const digestIndex = abilityLines.findIndex((line) => typeof line === 'string' && DIGEST_MARKER_PATTERN.test(line));
    if (digestIndex === -1) {
        return undefined;
    }
    const digestMatch = DIGEST_MARKER_PATTERN.exec(abilityLines[digestIndex]);
    // why: findIndex already matched DIGEST_MARKER_PATTERN, so exec cannot be null; the guard keeps
    // TS narrowing explicit instead of a non-null assertion on the capture group.
    if (digestMatch === null) {
        return undefined;
    }
    const digestThreshold = Number.parseInt(digestMatch[1], 10);
    const digestEffects = parseAbilityText(abilityLines[digestIndex]).effects;
    const consumedIndices = new Set([digestIndex]);
    // why: WP-735 / D-24555 — the Indigestion fallback branch (absent for a single-branch card
    // such as cauldron-of-the-cosmos). Its inline [keyword:rescue:N] / [icon:recruit] parse to the
    // branch effects; the bare [keyword:Indigestion] display token is discarded (never emitted as a
    // hook), so no unresolved-marker hollow survives.
    const indigestionIndex = abilityLines.findIndex((line) => typeof line === 'string' && INDIGESTION_MARKER_PATTERN.test(line));
    let indigestionEffects;
    if (indigestionIndex !== -1) {
        consumedIndices.add(indigestionIndex);
        indigestionEffects = parseAbilityText(abilityLines[indigestionIndex]).effects;
    }
    // why: WP-735 / D-24555 — the "Instead … both" upgrade line, if present. Its single [hc:X]
    // condition (Core-4 uses [hc:strength] / [hc:instinct]) is the bothCondition.
    // why: D-24563 — a DOUBLED "[team:X][team:X]" / "[hc:X][hc:X]" line (Play to the Crowd) now
    // arrives from the parser as ONE condition carrying requiredMatches (the rulebook's "Critical
    // Hit" rule: that many OTHER matching cards), so bothCondition = conditions[0] carries the count
    // and the old WP-740 descriptor count field is retired.
    const bothIndex = abilityLines.findIndex((line) => typeof line === 'string' && DIGEST_BOTH_LINE_PATTERN.test(line));
    let bothCondition;
    if (bothIndex !== -1) {
        consumedIndices.add(bothIndex);
        bothCondition = parseAbilityText(abilityLines[bothIndex]).conditions[0];
    }
    const effect = { type: 'digest-indigestion', digestThreshold, digestEffects };
    if (indigestionEffects !== undefined) {
        effect.indigestionEffects = indigestionEffects;
    }
    if (bothCondition !== undefined) {
        effect.bothCondition = bothCondition;
    }
    const hook = {
        cardId,
        timing: 'onPlay',
        keywords: ['digest-indigestion'],
        effects: [effect],
    };
    return { hook, consumedIndices };
}
/**
 * Fuses an allowlisted Venomverse card's "[keyword:Excessive Violence]" line into one
 * excessive-violence hook (WP-736 / D-24556).
 *
 * Finds the single ability line carrying the EV display token, parses that line's inline
 * effects via the shared parseAbilityText (the [keyword:draw:1] / [keyword:rescue:1] /
 * [keyword:optional-ko-hand-discard] / [icon:recruit] marker → the EV ability's inner
 * effects), and returns ONE fused hook (timing onFight) whose wrapper descriptor carries
 * those effects in `excessiveViolenceEffects`, plus the index of the consumed line so the
 * caller skips it. The printed [keyword:Excessive Violence] token is NOT a resolvable
 * keyword, so it is simply discarded with the rest of the consumed line (no leftover
 * standalone draw/recruit/rescue/ko hook that would fire at play, no residual unresolved
 * marker). Non-mutating (returns a new array; the display `abilities[]` is untouched).
 * Returns undefined when no EV line is present (defensive — every allowlisted card has one).
 *
 * @param cardId - The played hero card's CardExtId (the hook's cardId).
 * @param abilityLines - The card's ability lines (post-coalesce).
 * @returns The fused hook + consumed line index, or undefined when no EV line is found.
 */
function buildExcessiveViolenceFusion(cardId, abilityLines) {
    const excessiveViolenceIndex = abilityLines.findIndex((line) => typeof line === 'string' && EXCESSIVE_VIOLENCE_PATTERN.test(line));
    if (excessiveViolenceIndex === -1) {
        return undefined;
    }
    // why: WP-736 / D-24556 — the EV line's inline marker parses to the EV ability's inner
    // effect(s). The bare [keyword:Excessive Violence] display token is not a resolvable
    // keyword (KEYWORD_PATTERN cannot match the space form), so parseAbilityText returns only
    // the inner effect; consuming the whole line below discards the display token so no
    // unresolved-marker hollow survives.
    const excessiveViolenceEffects = parseAbilityText(abilityLines[excessiveViolenceIndex]).effects;
    const effect = { type: 'excessive-violence', excessiveViolenceEffects };
    const hook = {
        cardId,
        // why: WP-736 / D-24556 — onFight is the EV keyword's KEYWORD_TIMING_DEFAULTS timing.
        // The hook still fires at PLAY (executeHeroEffects does not filter by timing) — where its
        // handler ENROLLS the card; the inner effects fire from the fight move's driver.
        timing: 'onFight',
        keywords: ['excessive-violence'],
        effects: [effect],
    };
    return { hook, consumedIndices: new Set([excessiveViolenceIndex]) };
}
/**
 * Fuses an allowlisted card's "[keyword:Sunlight]: … / [keyword:Moonlight]: … / [X]: Instead,
 * you get both." lines into one day-night-both hook (WP-765 / D-24598).
 *
 * Parses each branch line's inline effects via the shared parseAbilityText (the branch's own
 * day/night condition is discarded — the handler selects the branch), reads the upgrade line's
 * condition into bothCondition (a repeated token arrives as one condition carrying requiredMatches,
 * e.g. Nanite's four [team:x-men] → 4, D-24563), and returns the single fused hook plus the consumed line indices so the caller
 * skips them. Returns undefined when the card lacks a Sunlight or Moonlight line (defensive —
 * every allowlisted card has both).
 *
 * @param cardId - The played hero card's CardExtId (the hook's cardId).
 * @param abilityLines - The card's ability lines (post-coalesce).
 * @returns The fused hook + consumed line indices, or undefined when a branch line is missing.
 */
function buildDayNightBothFusion(cardId, abilityLines) {
    const sunlightIndex = abilityLines.findIndex((line) => typeof line === 'string' && SUNLIGHT_MARKER_PATTERN.test(line));
    const moonlightIndex = abilityLines.findIndex((line) => typeof line === 'string' && MOONLIGHT_MARKER_PATTERN.test(line));
    if (sunlightIndex === -1 || moonlightIndex === -1) {
        return undefined;
    }
    const sunlightEffects = parseAbilityText(abilityLines[sunlightIndex]).effects;
    const moonlightEffects = parseAbilityText(abilityLines[moonlightIndex]).effects;
    const consumedIndices = new Set([sunlightIndex, moonlightIndex]);
    // why: WP-765 / D-24598 — the "Instead, you get both." upgrade line (the same detector the
    // digest fusion uses). Its class/team tokens are the bothCondition.
    // why: D-24563 — a repeated identical token (Nanite's four [team:x-men]) arrives from the parser
    // as ONE condition carrying requiredMatches, so bothCondition = conditions[0] carries the count;
    // the old WP-740 descriptor count field is retired.
    const bothIndex = abilityLines.findIndex((line) => typeof line === 'string' && DIGEST_BOTH_LINE_PATTERN.test(line));
    let bothCondition;
    if (bothIndex !== -1) {
        consumedIndices.add(bothIndex);
        bothCondition = parseAbilityText(abilityLines[bothIndex]).conditions[0];
    }
    const effect = { type: 'day-night-both', sunlightEffects, moonlightEffects };
    if (bothCondition !== undefined) {
        effect.bothCondition = bothCondition;
    }
    const hook = {
        cardId,
        timing: 'onPlay',
        keywords: ['day-night-both'],
        effects: [effect],
    };
    return { hook, consumedIndices };
}
/**
 * Whether one ability line of a card is a DAY_NIGHT_UNMODELED_LINES entry (WP-765 / D-24598).
 *
 * @param cardKey - The canonical `{setAbbr}/{heroSlug}/{cardSlug}` key.
 * @param abilityText - The ability line.
 * @returns Whether the line's Sunlight / Moonlight effect is allowlisted as unmodeled.
 */
function isDayNightUnmodeledLine(cardKey, abilityText) {
    if (SUNLIGHT_MARKER_PATTERN.test(abilityText) && DAY_NIGHT_UNMODELED_LINES.has(`${cardKey}:sunlight`)) {
        return true;
    }
    if (MOONLIGHT_MARKER_PATTERN.test(abilityText) && DAY_NIGHT_UNMODELED_LINES.has(`${cardKey}:moonlight`)) {
        return true;
    }
    return false;
}
/**
 * Builds hero ability hooks from registry card data at setup time.
 *
 * Called during Game.setup() via buildInitialGameState. Resolves hero
 * cards from the selected hero decks, extracts structured ability
 * metadata, and produces a list of HeroAbilityHook entries.
 *
 * After setup, G.heroAbilityHooks is immutable — moves must never
 * modify it.
 *
 * @param registry - Card registry for resolving hero card data.
 *   Used at setup time only. Accepts unknown to support narrow test
 *   mocks. If the registry does not satisfy HeroAbilityRegistryReader
 *   structurally, returns an empty array gracefully.
 * @param matchConfig - Validated match setup config with heroDeckIds.
 * @returns Array of HeroAbilityHook entries for all selected hero cards.
 */
export function buildHeroAbilityHooks(registry, matchConfig) {
    if (!isHeroAbilityRegistryReader(registry)) {
        return [];
    }
    // why: hook keys are the canonical-face slash instance ids emitted by
    // heroCardInstanceExtIds (D-18705), which reads the hero entry from set
    // data — so getSet is required here even though isHeroAbilityRegistryReader
    // guards only listCards (the guard's listCards-only contract is pinned by
    // buildInitialGameState.loadout.test.ts and the validator's
    // buildKnownHeroQualifiedIds). When getSet is absent (narrow listCards-only
    // test mocks), no hero entries are reachable, so no hooks are built —
    // identical to the empty-result path, no throw.
    const candidate = registry;
    if (typeof candidate.getSet !== 'function') {
        return [];
    }
    const getSet = candidate.getSet;
    const hooks = [];
    // Iterate selected hero decks deterministically.
    // why: D-10014 — Builder Filtering Order — iterate named set only. Each
    // heroDeckIds entry is `<setAbbr>/<heroSlug>`; resolve the hero entry from
    // that set's data only. Hero slugs collide across sets (51 / 307 instances
    // per the D-10014 PS-8 probe), so the named-set filter is non-negotiable.
    for (const heroDeckId of matchConfig.heroDeckIds) {
        const parsed = parseQualifiedId(heroDeckId);
        if (parsed === null) {
            // Malformed input: skip silently. The validator is the authoritative
            // format-error reporter; this builder is defense-in-depth.
            continue;
        }
        const heroEntry = findHeroAbilityHeroEntry(getSet(parsed.setAbbr), parsed.slug);
        if (heroEntry === null)
            continue;
        // why: D-18705 — emit one hook per (canonical-face slash instance id ×
        // ability line). The instance ids come from the shared emitter (matching
        // the played-card zone id getHooksForCard reads at the play site); the
        // ability text is resolved from the cards[] entry whose slug === the
        // canonical face (sides[0]). A copy with no resolvable canonical-face
        // card entry emits no hook (safe-skip, no throw) — non-canonical-face
        // ability text is out of scope.
        const instances = heroCardInstanceExtIds(parsed.setAbbr, parsed.slug, heroEntry);
        for (const instance of instances) {
            const cardEntry = findHeroAbilityCardBySlug(heroEntry.cards, instance.cardSlug);
            if (cardEntry === null)
                continue;
            if (!Array.isArray(cardEntry.abilities) || cardEntry.abilities.length === 0) {
                continue;
            }
            // why: WP-675 / D-24490 — join any multi-line count-scaled "Choose one:" group into one
            // synthetic line BEFORE the per-line parse, so the count-scaled-choose pre-pass folds the
            // two option markers into a single choice. Gated on count-scaled markers → non-count-scaled
            // multi-line choose-ones (S.H.I.E.L.D. undercover/levels) pass through unchanged.
            const coalescedLines = coalesceCountScaledChooseOne(cardEntry.abilities);
            // why: WP-859 / D-24795 — split Metamorphosis' / Bursting with Life's one "Feast. Then, if …,
            // Transform" line into a Feast line and a Transform line, so the Transform's condition does
            // not gate the Feast (FEAST_THEN_TRANSFORM_CARDS). Every other card's lines pass through.
            const abilityLines = FEAST_THEN_TRANSFORM_CARDS.has(`${parsed.setAbbr}/${parsed.slug}/${instance.cardSlug}`)
                ? splitFeastThenTransformLine(coalescedLines)
                : coalescedLines;
            // why: WP-735 / D-24555 — for an allowlisted Venomverse "Digest N / Indigestion" card, fuse
            // its Digest / Indigestion / "Instead … both" lines into ONE digest-indigestion hook up
            // front, and record which line indices that consumed so the per-line loop below SKIPS them
            // (they are subsumed by the fused hook, never emitted as their own attack/rescue hooks or an
            // unresolved [keyword:Indigestion] marker). The canonical key is the transform / x-gene
            // per-card-allowlist gate (RS-1: the fusion is gated here, where the card key exists).
            const digestFusion = DIGEST_INDIGESTION_CARDS.has(`${parsed.setAbbr}/${parsed.slug}/${instance.cardSlug}`)
                ? buildDigestIndigestionFusion(instance.extId, abilityLines)
                : undefined;
            if (digestFusion !== undefined) {
                hooks.push(digestFusion.hook);
            }
            // why: WP-736 / D-24556 — for an allowlisted Venomverse "Excessive Violence" card, fuse its
            // [keyword:Excessive Violence] line into ONE excessive-violence hook up front (mirroring the
            // digest fusion above), and record the consumed line index so the per-line loop below SKIPS it
            // (never emitting a standalone draw/recruit/rescue/ko hook that would fire at play, nor an
            // unresolved Excessive Violence marker). Same canonical-key allowlist gate.
            const excessiveViolenceFusion = EXCESSIVE_VIOLENCE_CARDS.has(`${parsed.setAbbr}/${parsed.slug}/${instance.cardSlug}`)
                ? buildExcessiveViolenceFusion(instance.extId, abilityLines)
                : undefined;
            if (excessiveViolenceFusion !== undefined) {
                hooks.push(excessiveViolenceFusion.hook);
            }
            // why: WP-765 / D-24598 — for an allowlisted "Sunlight / Moonlight / Instead, you get both"
            // card, fuse its three lines into ONE day-night-both hook up front (the digest fusion
            // precedent) and record the consumed indices so the per-line loop below SKIPS them — emitting
            // them as separate condition-gated hooks as well would double-fire a branch under the upgrade.
            const dayNightBothFusion = DAY_NIGHT_BOTH_CARDS.has(`${parsed.setAbbr}/${parsed.slug}/${instance.cardSlug}`)
                ? buildDayNightBothFusion(instance.extId, abilityLines)
                : undefined;
            if (dayNightBothFusion !== undefined) {
                hooks.push(dayNightBothFusion.hook);
            }
            // why: WP-790 / D-24652 — the hook built from abilityLines[lineIndex - 1] of THIS instance,
            // the only valid widen-fusion target. Tracked explicitly (null after every skipped line),
            // never read as hooks[hooks.length - 1]: the digest / EV / day-night fused hooks above are
            // pushed before this loop and must never become a fusion target.
            let previousLineHook = null;
            for (let lineIndex = 0; lineIndex < abilityLines.length; lineIndex++) {
                // why: WP-735 / D-24555 — skip the Digest/Indigestion/upgrade lines the fusion already
                // consumed into the single digest-indigestion hook above.
                if (digestFusion !== undefined && digestFusion.consumedIndices.has(lineIndex)) {
                    previousLineHook = null;
                    continue;
                }
                // why: WP-736 / D-24556 — skip the [keyword:Excessive Violence] line the EV fusion consumed
                // into the single excessive-violence hook above.
                if (excessiveViolenceFusion !== undefined && excessiveViolenceFusion.consumedIndices.has(lineIndex)) {
                    previousLineHook = null;
                    continue;
                }
                // why: WP-765 / D-24598 — skip the Sunlight / Moonlight / upgrade lines the day-night-both
                // fusion consumed into the single fused hook above.
                if (dayNightBothFusion !== undefined && dayNightBothFusion.consumedIndices.has(lineIndex)) {
                    previousLineHook = null;
                    continue;
                }
                const abilityText = abilityLines[lineIndex];
                if (typeof abilityText !== 'string' || abilityText.trim() === '') {
                    previousLineHook = null;
                    continue;
                }
                // why: WP-658 / D-24469 — resolve whether this base card's transform is
                // fully modeled (SUPPORTED_TRANSFORM_BASES) from its canonical key
                // `{setAbbr}/{heroSlug}/{cardSlug}`, and pass it so parseAbilityText resolves
                // a [keyword:Transform] to an executable transform only for allowlisted cards
                // (all others keep an honest unresolved marker).
                const transformSupported = SUPPORTED_TRANSFORM_BASES.has(`${parsed.setAbbr}/${parsed.slug}/${instance.cardSlug}`);
                // why: WP-705 / D-24526 — resolve whether this card's [keyword:Teleport] is the
                // modeled reactive teleport-on-discard form (TELEPORT_ON_DISCARD_CARDS), from the
                // same canonical key, and thread it in so parseAbilityText resolves the token only
                // for allowlisted cards (all others keep an honest unresolved marker).
                const teleportOnDiscardSupported = TELEPORT_ON_DISCARD_CARDS.has(`${parsed.setAbbr}/${parsed.slug}/${instance.cardSlug}`);
                // why: WP-723 / D-24544 — resolve whether this card's [keyword:X-Gene] is recognized
                // (X_GENE_CARDS), from the same canonical key, and thread it in so parseAbilityText
                // reroutes the co-located [hc:X] into the heroClassInDiscardPile condition and consumes
                // the marker only for allowlisted cards (all others keep an honest unresolved marker).
                const xGeneSupported = X_GENE_CARDS.has(`${parsed.setAbbr}/${parsed.slug}/${instance.cardSlug}`);
                // why: WP-765 / D-24598 — resolve whether this line is an allowlisted unmodeled day/night
                // line (DAY_NIGHT_UNMODELED_LINES), from the same canonical key, so parseAbilityText keeps
                // its gate but drops its grant and records it as an honest hollow.
                const dayNightUnmodeled = isDayNightUnmodeledLine(`${parsed.setAbbr}/${parsed.slug}/${instance.cardSlug}`, abilityText);
                const parsedAbility = parseAbilityText(abilityText, {
                    transformSupported,
                    teleportOnDiscardSupported,
                    xGeneSupported,
                    dayNightUnmodeled,
                    // why: D-24628 — drop the free grant on an unmodeled conditional-grant line. The key is
                    // per card, so only the line that prints the resource icon qualifies (not its Dodge line).
                    conditionalGrantUnmodeled: CONDITIONAL_GRANT_UNMODELED_LINES.has(`${parsed.setAbbr}/${parsed.slug}/${instance.cardSlug}`) &&
                        RESOURCE_ICON_PATTERN.test(abilityText),
                    // why: WP-857 / D-24793 — resolve whether this card's [keyword:Feast] is recognized
                    // (FEAST_CARDS), from the same canonical key, so parseAbilityText resolves the token only
                    // for allowlisted cards (all others keep an honest unresolved marker).
                    feastSupported: FEAST_CARDS.has(`${parsed.setAbbr}/${parsed.slug}/${instance.cardSlug}`),
                    // why: WP-859 / D-24795 — how many Feasts this card's line prints ("Feast up to three
                    // times"), from FEAST_REPEAT_COUNTS by the same canonical key; 1 for every other card.
                    feastRepeatCount: FEAST_REPEAT_COUNTS.get(`${parsed.setAbbr}/${parsed.slug}/${instance.cardSlug}`) ?? 1,
                });
                // why: freshly-constructed hook per instance — copies never alias a
                // shared object or arrays (D-13502).
                const hook = {
                    cardId: instance.extId,
                    timing: parsedAbility.timing,
                    keywords: parsedAbility.keywords,
                };
                if (parsedAbility.conditions.length > 0) {
                    hook.conditions = parsedAbility.conditions;
                }
                if (parsedAbility.effects.length > 0) {
                    hook.effects = parsedAbility.effects;
                }
                // why: D-24031 — assign primitiveEffects only when non-empty (mirror the
                // effects/conditions conditional construction — exactOptionalPropertyTypes
                // forbids `: x ?? undefined`). Each element is already a deep copy of its
                // registry AST, so the hook never aliases the shared HERO_COMPOSITION_MARKERS const.
                if (parsedAbility.primitiveEffects.length > 0) {
                    hook.primitiveEffects = parsedAbility.primitiveEffects;
                }
                // why: WP-257 / D-24034 — assign unresolvedMarkers only when non-empty (same
                // exactOptionalPropertyTypes conditional-construction pattern). Absent means
                // "no unresolved marker" — flavor-text lines carry no markers and so omit it,
                // which is exactly what keeps flavor text from flagging hollow at runtime.
                if (parsedAbility.unresolvedMarkers.length > 0) {
                    hook.unresolvedMarkers = parsedAbility.unresolvedMarkers;
                }
                // why: D-24045 — assign resolvedMarkers only when non-empty (mirror the
                // unresolvedMarkers conditional-assign verbatim — exactOptionalPropertyTypes forbids
                // assigning `undefined`). Absent means "no composition resolved on this line"; the
                // mechanic ledger reads this per card to classify composition markers by-hook.
                if (parsedAbility.resolvedMarkers.length > 0) {
                    hook.resolvedMarkers = parsedAbility.resolvedMarkers;
                }
                // why: D-24801 — assign the Size-Changing class list only when non-empty (same
                // exactOptionalPropertyTypes conditional-construction pattern as the fields above).
                // Absent means "no Size-Changing discount" — both non-Size-Changing lines and a
                // Size-Changing line with no [hc:X] omit it; getSizeChangingClasses treats absent as [].
                if (parsedAbility.sizeChangingClasses.length > 0) {
                    hook.sizeChangingClasses = parsedAbility.sizeChangingClasses;
                }
                // why: D-24801 — the Microscopic icons, same omit-when-empty construction.
                if (parsedAbility.microscopicSizeChangingClasses.length > 0) {
                    hook.microscopicSizeChangingClasses = parsedAbility.microscopicSizeChangingClasses;
                }
                // why: WP-790 / D-24652 — a Ranged "… against the Mastermind instead" line widens the
                // restricted grant on the immediately preceding line of the same card (Storm, Electro),
                // and loses its own attack effect + keyword so it never grants twice or reads as hollow.
                fuseAttackRestrictionWiden(previousLineHook, hook, abilityText);
                hooks.push(hook);
                previousLineHook = hook;
            }
        }
    }
    return hooks;
}
/**
 * Finds a hero entry within set data's heroes[] by slug.
 *
 * Returns null when the set data is absent/malformed or the named hero is
 * not present (no cross-set fallback) — mirrors the soft-skip pattern in
 * buildHeroDeckCards and buildCardStats.
 *
 * @param setData - Raw set data from getSet().
 * @param heroSlug - Hero slug to match.
 * @returns The matching hero entry, or null.
 */
function findHeroAbilityHeroEntry(setData, heroSlug) {
    if (!setData || typeof setData !== 'object')
        return null;
    const candidate = setData;
    if (!Array.isArray(candidate.heroes))
        return null;
    for (const entry of candidate.heroes) {
        if (!entry || typeof entry !== 'object')
            continue;
        const heroEntry = entry;
        if (typeof heroEntry.slug !== 'string')
            continue;
        if (heroEntry.slug !== heroSlug)
            continue;
        if (!Array.isArray(heroEntry.cards))
            continue;
        return heroEntry;
    }
    return null;
}
/**
 * Finds the hero card entry whose slug matches the canonical-face slug.
 *
 * @param cards - The hero entry's cards array.
 * @param slug - The canonical-face slug (physicalCards[].sides[0]).
 * @returns The matching card entry, or null when none matches.
 */
function findHeroAbilityCardBySlug(cards, slug) {
    for (const cardEntry of cards) {
        if (!cardEntry || typeof cardEntry !== 'object')
            continue;
        if (cardEntry.slug === slug)
            return cardEntry;
    }
    return null;
}
//# sourceMappingURL=heroAbility.setup.js.map